import { useState, useCallback, useEffect, useRef } from 'react';
import kwLogo from './kw_logo.png';
import './index.css';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const APP_VERSION = '2.3.7';
const APP_RELEASE_DATE = '2026-05-12';
const STORAGE_SCHEMA_VERSION = 1;
const STORAGE_KEY = 'kaffeewerk.vertrieb.state';
const FORCED_ADMIN_EMAILS = ['dw@kaffeewerk.de'];

const DEFAULT_WORK_STATUS = {
  offers: { offers: [], count: 0, lastSyncAt: null, status: 'Nicht synchronisiert', importedIds: [], rejectedIds: [], statusArchive: {} },
  calculation: { draft: null, calculations: [], itemCount: 0, netTotal: 0, updatedAt: null },
  checklist: { draft: null, pct: 0, status: 'Offen', updatedAt: null },
};

const DW_ADMIN_USER = {
  pass: '', role: 'admin', name: 'Dirk Werdelmann', email: 'dw@kaffeewerk.de',
  mailRecipient: 'dw@kaffeewerk.de',
  insideSalesRecipient: 'buero@kaffeewerk.de',
  initials: 'DW', slnSachbearbeiter: 'DW', salesmanNumber: 'DW', erpSource: 'selectline',
  notify: { checklistDone: true, newOffer: true, weeklyReport: true },
};

const INITIAL_USERS = {
  admin: {
    pass: 'admin123', role: 'admin', name: 'Admin', email: 'admin@kaffeewerk.de',
    mailRecipient: 'admin@kaffeewerk.de',
    insideSalesRecipient: 'buero@kaffeewerk.de',
    initials: 'AD', slnSachbearbeiter: 'AD', salesmanNumber: 'AD', erpSource: 'selectline',
    notify: { checklistDone: true, newOffer: true, weeklyReport: true },
  },
  'dw@kaffeewerk.de': DW_ADMIN_USER,
  vertrieb: {
    pass: 'user123', role: 'user', name: 'Mitarbeiter', email: 'vertrieb@kaffeewerk.de',
    mailRecipient: 'vertrieb@kaffeewerk.de',
    insideSalesRecipient: 'buero@kaffeewerk.de',
    initials: 'MA', slnSachbearbeiter: 'MA', salesmanNumber: 'MA', erpSource: 'outlook',
    notify: { checklistDone: true, newOffer: false, weeklyReport: false },
  },
};

// Globale Integrations-Settings (Admin-konfigurierbar)
const DEFAULT_INTEGRATIONS = {
  selectline: {
    enabled: true,
    apiUrl: 'https://demo.slmobile.de/demoApi',
    appKeySet: false,
    mandantenNr: '100',
    belegArt: 'F',
    pollInterval: 300,
    lastSync: null,
    status: 'nicht_verbunden',
  },
  outlook: {
    enabled: true,
    subjectFilter: 'Angebot',
    lookbackMonths: 1,
    lastSync: null,
    status: 'nicht_verbunden',
  },
  smtp: {
    enabled: true,
    host: 'smtp.office365.com',
    port: 587,
    security: 'starttls',
    user: 'vertrieb@kaffeewerk.de',
    passwordSet: false,
    fromName: 'Kaffeewerk Vertrieb',
    fromEmail: 'vertrieb@kaffeewerk.de',
    replyTo: '',
    testStatus: null,
  },
  catalog: {
    priceListUrl: '',
  },
};

const DEFAULT_MAIL_TEMPLATES = {
  checklist_done: {
    label: 'Checkliste abgeschlossen',
    subject: 'Inbetriebnahme-Checkliste: {kunde} · Angebot {angebot_nr}',
    body: 'Moin zusammen,\n\nanbei die abgeschlossene Inbetriebnahme-Checkliste für {kunde}.\n\nProjektdaten\n· Datum: {datum}\n· Sachbearbeiter: {sachbearbeiter}\n· Angebot: {angebot_nr}\n· Fortschritt: {fortschritt}\n\nBleibt sportlich,\n{absender}',
  },
  offer_imported: {
    label: 'Neues Angebot importiert',
    subject: 'Neues Angebot: {angebot_nr} — {kunde}',
    body: 'Moin {empfaenger},\n\nein neues Angebot wurde aus dem ERP-System geladen:\n\n· Nummer: {angebot_nr}\n· Kunde: {kunde}\n· Gesamt: {betrag}\n· Positionen: {positionen}\n\nBleibt sportlich',
  },
  weekly_report: {
    label: 'Wochenbericht (montags)',
    subject: 'Wochenbericht KW {kw} — Kaffeewerk Vertrieb',
    body: 'Moin {empfaenger},\n\nhier dein Wochenbericht:\n\n· Neue Angebote: {neue_angebote}\n· Abgeschlossene Checklisten: {abgeschlossen}\n· Offene Checklisten: {offen}\n\nBleibt sportlich',
  },
};

// Checkpunkt-Typen — jeder Typ rendert eigene Felder:
//   'standort'     → Thekenposition + Foto (Frontheke/Rückbuffet/Freitext)
//   'stellflaeche' → B/T/Abstände zu Wasser/Strom auf der Arbeitsplatte
//   'anschluesse'  → Auto-berechneter Bedarf aus Angebot + Vorhanden-Checkboxen + Foto
//   'dosierung'    → Standards übernehmen ODER individuelle Werte
//   'schulung'     → Multi-Checkbox-Liste fester Schulungsthemen
//   'notiz_foto'   → Freitext mit Foto
//   'text'         → einfacher Freitext (Legacy)
//   'photo'        → Foto allein (Legacy)
const DEFAULT_ITEMS = [
  { id: 1,  label: 'Thekenposition / Standort',  type: 'standort',     active: true,  hint: 'Wo soll die Maschine stehen?' },
  { id: 2,  label: 'Verfügbare Stellfläche',      type: 'stellflaeche', active: true,  hint: 'Maße und Anschlussabstände' },
  { id: 3,  label: 'Anschlüsse vor Ort',          type: 'anschluesse',  active: true,  hint: 'Bedarf wird automatisch aus Angebot berechnet' },
  { id: 4,  label: 'Dosiermenge / Kalibrierung',  type: 'dosierung',    active: true,  hint: 'Standards oder individuelle Werte' },
  { id: 5,  label: 'Schulungsbedarf Personal',    type: 'schulung',     active: true,  hint: 'Welche Themen sind zu schulen?' },
  { id: 6,  label: 'Besonderheiten / Sonstiges',  type: 'notiz_foto',   active: true,  hint: 'Auffälligkeiten, Hindernisse, Sonderwünsche' },
];

// Schulungsthemen für den 'schulung'-Typ (im Settings konfigurierbar)
const DEFAULT_TRAINING_TOPICS = [
  { id: 't1', label: 'Automatisches Gruppen-Reinigungsprogramm schulen', active: true },
  { id: 't2', label: 'TurboSteam Reinigung schulen',                      active: true },
  { id: 't3', label: 'Standby programmieren und erläutern',               active: true },
  { id: 't4', label: 'Tägliche Reinigungsroutinen',                       active: true },
  { id: 't5', label: 'Wasserfilter-Wechsel erläutern',                    active: true },
];

// Artikelschlüssel → Anschlussbedarf
// Wenn im Angebot ein Artikel mit passender Art-Nr. enthalten ist, wird der Bedarf
// automatisch errechnet (Steckdosen, Eckventile, Siphons).
// Editierbar in den Settings unter "Anschluss-Regeln".
const DEFAULT_ANSCHLUSS_RULES = [
  { matchArt: 'M200',     name: 'La Cimbali M200',         steckdose: 1, is400v: true,  eckventil: 1, siphon: 1 },
  { matchArt: 'M100',     name: 'La Cimbali M100',         steckdose: 1, is400v: true,  eckventil: 1, siphon: 1 },
  { matchArt: 'SLAYER',   name: 'Slayer Espresso',         steckdose: 1, is400v: true,  eckventil: 1, siphon: 1 },
  { matchArt: 'E71',      name: 'Faema E71',               steckdose: 1, is400v: true,  eckventil: 1, siphon: 1 },
  { matchArt: 'PUQM',     name: 'PuqPress Tamper',         steckdose: 1, is400v: false, eckventil: 0, siphon: 0 },
  { matchArt: 'E80S',     name: 'Mahlkönig E80S',          steckdose: 1, is400v: false, eckventil: 0, siphon: 0 },
  { matchArt: 'EK43',     name: 'Mahlkönig EK43',          steckdose: 1, is400v: false, eckventil: 0, siphon: 0 },
  { matchArt: 'PEAK',     name: 'Mahlkönig Peak',          steckdose: 1, is400v: false, eckventil: 0, siphon: 0 },
  { matchArt: 'GRIND',    name: 'Mahlkönig On-Demand',     steckdose: 1, is400v: false, eckventil: 0, siphon: 0 },
  { matchArt: 'KKT',      name: 'Kaffee Klar Wasserfilter', steckdose: 0, is400v: false, eckventil: 1, siphon: 0 },
];

// Kontextuelle Leitlinien-Hinweise pro Checkpunkt-Label (Stichwort-Match).
// Werden in der Checkliste als aufklappbarer Info-Block angezeigt.
const GUIDELINE_HINTS = {
  'Standort':                    ['bruehtemperatur', 'pre_infusion', 'reinigung_modus'],
  'Wasseranschluss':              ['bruehtemperatur'],
  'Wasserfilter / Wasserzähler':  ['bruehtemperatur'],
  'Dosiermenge / Kalibrierung':   ['grammatur_doppelt', 'grammatur_einzeln', 'zeit_espresso', 'zeit_kaffee'],
  'Schulungsbedarf Personal':     ['milch_temp_soll', 'milch_temp_max', 'schaum_taste_1', 'schaum_taste_2', 'reinigung_modus'],
  'Besonderheiten / Sonstiges':   ['flushing_dauer', 'pre_infusion'],
};

// Zusätzliche Freitext-Empfehlungen pro Checkpunkt (aus den Leitlinien)
const GUIDELINE_TEXTS = {
  'Standort':                    'Brühgruppen- & Kesseltemperatur sind häufig zu hoch eingestellt. Temperaturmessung immer durch Technik prüfen lassen.',
  'Wasseranschluss':              'Brühtemperatur 88–92 °C. Wasserqualität beeinflusst Extraktion direkt.',
  'Wasserfilter / Wasserzähler':  'Kein Filtermanagement ohne Wasserzähler — Filterwechsel sonst auf Verdacht.',
  'Dosiermenge / Kalibrierung':   'Grammatur, Zeit und Mahlgrad bilden einen Dreiklang. Änderungen an Gramm → Nachjustierung des Mahlgrads.',
  'Schulungsbedarf Personal':     'Flushing nach längerer Standzeit (2 Sek.) und tägliche Gruppenreinigung am Schichtende sind Pflichtthemen der Einweisung.',
  'Besonderheiten / Sonstiges':   'Pre-Infusion grundsätzlich AUS — nur aktivieren, wenn der Röster es ausdrücklich verlangt.',
};

const DEMO_OFFERS = {
  dirk: [
    {
      id: 'ANG-26-0750', nr: '26-0750', kunde: 'Bäckerei Horsthemke', date: '2026-04-15',
      status: 'offen', total: 18420.00, subject: 'Angebot Neuinstallation La Cimbali M200',
      items: [
        { pos: 1, art: 'M200-DT2-3G',  desc: 'La Cimbali M200 Dosatron 3-Gr. Siebträger',  qty: 1, unit: 'St.',  price: 12800 },
        { pos: 2, art: 'PUQM5-SE',     desc: 'PuqPress M5 SE Tamper automatisch',           qty: 1, unit: 'St.',  price: 1490  },
        { pos: 3, art: 'E80S-GBW',     desc: 'Mahlkönig E80S GbW Scheibenmahlwerk',         qty: 2, unit: 'St.',  price: 980   },
        { pos: 4, art: 'KKT1-PRO',     desc: 'Kaffee Klar T1 Wasserfilteranlage',           qty: 1, unit: 'St.',  price: 390   },
        { pos: 5, art: 'INST-KOMPL',   desc: 'Installations-Pauschale inkl. Einweisung',    qty: 1, unit: 'Psch.', price: 780  },
      ],
    },
    {
      id: 'ANG-26-0680', nr: '26-0680', kunde: 'Hotel Schloss Bensberg', date: '2026-03-28',
      status: 'angenommen', total: 34900.00, subject: 'Angebot Slayer Espresso V3 + Ausstattung',
      items: [
        { pos: 1, art: 'SLAYER-V3-2G', desc: 'Slayer Espresso V3 2-Gruppe Siebträger',   qty: 1, unit: 'St.',  price: 22500 },
        { pos: 2, art: 'EK43S-SLAYER', desc: 'Mahlkönig EK43S Espresso',                 qty: 1, unit: 'St.',  price: 2200  },
        { pos: 3, art: 'INST-LUXURY',  desc: 'Premiuminstallation + Barista-Training',    qty: 1, unit: 'Psch.', price: 1800 },
      ],
    },
    {
      id: 'ANG-26-0601', nr: '26-0601', kunde: 'Café Freigeist Düsseldorf', date: '2026-03-02',
      status: 'abgelehnt', total: 9860.00, subject: 'Angebot Faema E71E Kompaktanlage',
      items: [
        { pos: 1, art: 'E71E-2G-B',   desc: 'Faema E71E 2-Gruppe Black Edition',  qty: 1, unit: 'St.', price: 7200 },
        { pos: 2, art: 'PEAK-DUO',    desc: 'Mahlkönig Peak Duo Set',              qty: 1, unit: 'St.', price: 1860 },
      ],
    },
  ],
  admin: [
    {
      id: 'ANG-26-0770', nr: '26-0770', kunde: 'Brabus GmbH Bottrop', date: '2026-04-28',
      status: 'offen', total: 55000.00, subject: 'Angebot Slayer Co-Branding Showroom',
      items: [
        { pos: 1, art: 'SLAYER-V3-3G',  desc: 'Slayer Espresso V3 3-Gruppe Custom Finish', qty: 2, unit: 'St.',  price: 24000 },
        { pos: 2, art: 'INST-SHOWROOM', desc: 'Showroom-Installation + Design-Konzept',     qty: 1, unit: 'Psch.', price: 3500 },
      ],
    },
  ],
  vertrieb: [
    {
      id: 'ANG-26-0744', nr: '26-0744', kunde: 'Handelshof EXPO Köln', date: '2026-04-10',
      status: 'offen', total: 6200.00, subject: 'Angebot Messeausstattung La Cimbali',
      items: [
        { pos: 1, art: 'M100-1G-EXPO', desc: 'La Cimbali M100 1-Gruppe Messeversion', qty: 2, unit: 'St.', price: 2800 },
        { pos: 2, art: 'GRIND-ON-45',  desc: 'Mahlkönig On-Demand 45 Grinder',        qty: 2, unit: 'St.', price: 300  },
      ],
    },
  ],
};

// ─── TECHNISCHE LEITLINIEN ────────────────────────────────────────────────────
// Statische Empfehlungen (Referenz). Werden in jeder Inbetriebnahme als Standard angewendet.

const TECH_GUIDELINES = {
  allgemein: [
    { topic: 'Grundsatz bei Geschmack',         value: 'Kunde probieren lassen',                            hint: 'Zufriedenheit des Kunden entscheidet — nicht das persönliche Empfinden.' },
    { topic: 'Temperaturmessung',                value: 'Immer durch Technik prüfen lassen',                  hint: 'Sensorabweichungen sind normal; Anzeigen sind nicht immer realitätsgetreu.' },
    { topic: 'Brühgruppen- & Kesseltemperatur',  value: 'Häufig zu hoch eingestellt',                         hint: 'Zu hohe Hitze verfälscht den Geschmack und macht Mahlgrad & Grammatur weitgehend wirkungslos.' },
    { topic: 'Brühtemperatur',                   value: '88 – 92 °C',                                          hint: 'Standardbereich für saubere Extraktion. Höher → Bitterstoffe, niedriger → saure Noten. Immer in Relation zu Bohne & Röstung bewerten.' },
    { topic: 'Flushing',                         value: 'Nach längerer Standzeit, immer 2 Sek. programmieren', hint: 'Muss bei Montage erklärt werden — verhindert Überhitzung und Reklamationen.' },
    { topic: 'Einheitliches Vorgehen',           value: 'Temperatur als zentraler Faktor',                     hint: 'Größter Qualitätshebel, aber sensibel je nach Bohne.' },
    { topic: 'Pre-Infusion',                     value: 'Grundsätzlich AUS',                                   hint: 'Nur aktivieren, wenn der Röster es ausdrücklich vor Ort verlangt.' },
    { topic: 'Teilautomatische Gruppenreinigung', value: 'Tägliche Nutzung am Schichtende',                   hint: 'Vereinfachtes Handling, ist ein USP beim Verkauf.' },
  ],
  kaffee: [
    { topic: 'Grammatur Doppelbezug',  value: '16 – 18 g',     hint: 'Stabiler Standard für reproduzierbare Extraktionen.' },
    { topic: 'Grammatur Einzelbezug',  value: '8 – 10 g',      hint: 'Bewährter Wert für klare, cremige Ergebnisse.' },
    { topic: 'Durchlaufzeit Espresso', value: '20 – 30 Sek.',  hint: 'Harmonische Extraktion und verlässliche Crema.' },
    { topic: 'Durchlaufzeit Kaffee',   value: '30 – 40 Sek.',  hint: 'Konstante Stärke, saubere Struktur.' },
    { topic: 'Mahlgrad-Abstimmung',    value: 'Ergibt sich aus Gramm + Sekunden', hint: 'Grammatur, Zeit und Mahlgrad bilden einen Dreiklang. Änderungen an Gramm → Nachjustierung des Mahlgrads.' },
  ],
  milch: [
    { topic: 'Milchtemperatur (TS)',                  value: '59 – 60 °C',  hint: 'Real durch Nachheizen ca. 61 – 62 °C.' },
    { topic: 'Max. Milchtemperatur',                  value: '≤ 63 °C',     hint: 'Darüber verliert Milch Süße, Textur & Stabilität.' },
    { topic: 'Schaumvolumen Taste 1 (Latte Art)',     value: '25 – 30 %',   hint: 'Besonders geeignet für Latte Art — aktuell Standardanforderung.' },
    { topic: 'Schaumvolumen Taste 2 (Latte Macchiato)', value: '40 – 45 %', hint: 'Für klare Schichtung & voluminöse Optik im Macchiato.' },
  ],
};

// Änderbare Vorgabewerte — aus den Leitlinien abgeleitet, pro Inbetriebnahme überschreibbar
const DEFAULT_MACHINE_VALUES = {
  bruehtemperatur:        { label: 'Brühtemperatur',                  value: 90,    unit: '°C',     min: 86,  max: 94,  group: 'Allgemein' },
  pre_infusion:           { label: 'Pre-Infusion',                    value: 'aus', unit: '',       options: ['aus', 'an'], group: 'Allgemein' },
  flushing_dauer:         { label: 'Flushing-Dauer',                  value: 2,     unit: 'Sek.',   min: 1,   max: 5,   group: 'Allgemein' },
  reinigung_modus:        { label: 'Gruppenreinigung',                value: 'täglich am Schichtende', unit: '', options: ['täglich am Schichtende', 'manuell'], group: 'Allgemein' },
  grammatur_doppelt:      { label: 'Grammatur Doppelbezug',           value: 17,    unit: 'g',      min: 14,  max: 22,  group: 'Kaffee' },
  grammatur_einzeln:      { label: 'Grammatur Einzelbezug',           value: 9,     unit: 'g',      min: 7,   max: 12,  group: 'Kaffee' },
  zeit_espresso:          { label: 'Durchlaufzeit Espresso',          value: 25,    unit: 'Sek.',   min: 18,  max: 35,  group: 'Kaffee' },
  zeit_kaffee:            { label: 'Durchlaufzeit Kaffee',            value: 35,    unit: 'Sek.',   min: 25,  max: 45,  group: 'Kaffee' },
  milch_temp_soll:        { label: 'Milchtemperatur (TS)',            value: 60,    unit: '°C',     min: 55,  max: 65,  group: 'Milch' },
  milch_temp_max:         { label: 'Max. Milchtemperatur',            value: 63,    unit: '°C',     min: 60,  max: 68,  group: 'Milch' },
  schaum_taste_1:         { label: 'Schaumvolumen Taste 1 (Latte Art)', value: 28, unit: '%',      min: 20,  max: 40,  group: 'Milch' },
  schaum_taste_2:         { label: 'Schaumvolumen Taste 2 (Macchiato)', value: 42, unit: '%',      min: 30,  max: 55,  group: 'Milch' },
};

const STANDARD_CATALOG = [
  { id: 'produkte-la-cimbali-m200', articleNumber: 'Homepage', group: 'Produkte / La Cimbali', name: 'M200', brand: 'La Cimbali', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/m200/' },
  { id: 'produkte-la-cimbali-m40', articleNumber: 'Homepage', group: 'Produkte / La Cimbali', name: 'M40', brand: 'La Cimbali', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/m40/' },
  { id: 'produkte-la-cimbali-m26', articleNumber: 'Homepage', group: 'Produkte / La Cimbali', name: 'M26', brand: 'La Cimbali', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/m26/' },
  { id: 'produkte-la-cimbali-s60', articleNumber: 'Homepage', group: 'Produkte / La Cimbali', name: 'S60', brand: 'La Cimbali', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/s60/' },
  { id: 'produkte-la-cimbali-s30', articleNumber: 'Homepage', group: 'Produkte / La Cimbali', name: 'S30', brand: 'La Cimbali', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/kaffeewerk-marken/la-cimbali/s30.html' },
  { id: 'produkte-la-cimbali-s20', articleNumber: 'Homepage', group: 'Produkte / La Cimbali', name: 'S20', brand: 'La Cimbali', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/kaffeewerk-marken/la-cimbali/S20.html' },
  { id: 'produkte-la-cimbali-s15', articleNumber: 'Homepage', group: 'Produkte / La Cimbali', name: 'S15', brand: 'La Cimbali', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/s15/' },
  { id: 'produkte-la-cimbali-s-lite', articleNumber: 'Homepage', group: 'Produkte / La Cimbali', name: 'S-lite', brand: 'La Cimbali', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/s-lite/' },
  { id: 'produkte-faema-e71-touch', articleNumber: 'Homepage', group: 'Produkte / Faema', name: 'E71 touch', brand: 'Faema', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/e71-touch/' },
  { id: 'produkte-faema-e71e', articleNumber: 'Homepage', group: 'Produkte / Faema', name: 'E71e', brand: 'Faema', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/e71e/' },
  { id: 'produkte-faema-president', articleNumber: 'Homepage', group: 'Produkte / Faema', name: 'President', brand: 'Faema', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/president/' },
  { id: 'produkte-faema-e61', articleNumber: 'Homepage', group: 'Produkte / Faema', name: 'E61', brand: 'Faema', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/e61/' },
  { id: 'produkte-faema-faemina', articleNumber: 'Homepage', group: 'Produkte / Faema', name: 'Faemina', brand: 'Faema', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/faemina/' },
  { id: 'produkte-slayer-espresso-v3', articleNumber: 'Homepage', group: 'Produkte / Slayer', name: 'Espresso V3', brand: 'Slayer', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/slayer-espresso-v3/' },
  { id: 'produkte-slayer-steam-ep', articleNumber: 'Homepage', group: 'Produkte / Slayer', name: 'Steam EP', brand: 'Slayer', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/slayer-steam-ep/' },
  { id: 'produkte-slayer-steam-lp', articleNumber: 'Homepage', group: 'Produkte / Slayer', name: 'Steam LP', brand: 'Slayer', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/steam-lp/' },
  { id: 'produkte-slayer-steam-single', articleNumber: 'Homepage', group: 'Produkte / Slayer', name: 'Slayer Steam Single', brand: 'Slayer', category: 'Siebtraeger', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/steam-single/' },
  { id: 'produkte-thermoplan-bw4', articleNumber: 'Homepage', group: 'Produkte / Thermoplan', name: 'Black&White4', brand: 'Thermoplan', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/blackwhite4/' },
  { id: 'produkte-thermoplan-bw4-compact', articleNumber: 'Homepage', group: 'Produkte / Thermoplan', name: 'Black&White4 compact', brand: 'Thermoplan', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/bw4-compact/' },
  { id: 'produkte-jura-giga-x8', articleNumber: 'Homepage', group: 'Produkte / Jura', name: 'GIGA X8/X8c', brand: 'Jura', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/giga-x8-x8c/' },
  { id: 'produkte-jura-giga-x3', articleNumber: 'Homepage', group: 'Produkte / Jura', name: 'GIGA X3/X3c', brand: 'Jura', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/giga-x3-x3c/' },
  { id: 'produkte-jura-giga-w10', articleNumber: 'Homepage', group: 'Produkte / Jura', name: 'GIGA W10', brand: 'Jura', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/giga-w10/' },
  { id: 'produkte-jura-x10', articleNumber: 'Homepage', group: 'Produkte / Jura', name: 'X10/X10c', brand: 'Jura', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/x10-x10c/' },
  { id: 'produkte-jura-x4', articleNumber: 'Homepage', group: 'Produkte / Jura', name: 'X4', brand: 'Jura', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/x4/' },
  { id: 'produkte-jura-w8', articleNumber: 'Homepage', group: 'Produkte / Jura', name: 'W8', brand: 'Jura', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/w8/' },
  { id: 'produkte-jura-w4', articleNumber: 'Homepage', group: 'Produkte / Jura', name: 'W4', brand: 'Jura', category: 'Vollautomat', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/w4/' },
  { id: 'produkte-fetco-cbs-12er', articleNumber: 'Homepage', group: 'Produkte / Fetco', name: 'CBS 12er Familie', brand: 'Fetco', category: 'Filterkaffee', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/cbs-12er-familie/' },
  { id: 'produkte-fetco-cbs-22er', articleNumber: 'Homepage', group: 'Produkte / Fetco', name: 'CBS 22er Familie', brand: 'Fetco', category: 'Filterkaffee', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/cbs-22er-familie/' },
  { id: 'produkte-marco-sp9', articleNumber: 'Homepage', group: 'Produkte / Marco', name: 'SP9', brand: 'Marco', category: 'Filterkaffee', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/sp9/' },
  { id: 'tools-mahlwerke-la-cimbali-elective', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / La Cimbali', name: 'Elective', brand: 'La Cimbali', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/kaffeewerk-marken/la-cimbali/mahlwerke/elective.html' },
  { id: 'tools-mahlwerke-la-cimbali-g50', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / La Cimbali', name: 'G50', brand: 'La Cimbali', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/g50/' },
  { id: 'tools-mahlwerke-la-cimbali-g20', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / La Cimbali', name: 'G20', brand: 'La Cimbali', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/g20/' },
  { id: 'tools-mahlwerke-mahlkoenig-e65s', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / Mahlkoenig', name: 'E65S', brand: 'Mahlkoenig', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/e65s/' },
  { id: 'tools-mahlwerke-mahlkoenig-e65s-gbw', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / Mahlkoenig', name: 'E65S GBW', brand: 'Mahlkoenig', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/e65s-gbw/' },
  { id: 'tools-mahlwerke-mahlkoenig-e80s', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / Mahlkoenig', name: 'E80S', brand: 'Mahlkoenig', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/e80s/' },
  { id: 'tools-mahlwerke-mahlkoenig-ek43', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / Mahlkoenig', name: 'EK43', brand: 'Mahlkoenig', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/ek43/' },
  { id: 'tools-mahlwerke-eureka-atom-excellence75', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / Eureka', name: 'ATOM Excellence75', brand: 'Eureka', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/atom-excellence75/' },
  { id: 'tools-mahlwerke-eureka-atom-excellence75-gbw', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / Eureka', name: 'ATOM Excellence 75 GBW', brand: 'Eureka', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/atom-excellence-75-gbw/' },
  { id: 'tools-mahlwerke-faema-groundbreaker', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / Faema', name: 'Groundbreaker', brand: 'Faema', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/groundbreaker/' },
  { id: 'tools-mahlwerke-faema-md5000', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / Faema', name: 'MD5000', brand: 'Faema', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/md5000/' },
  { id: 'tools-mahlwerke-faema-touch-match', articleNumber: 'Homepage', group: 'Tools / Mahlwerke / Faema', name: 'Touch&Match', brand: 'Faema', category: 'Muehle', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/touch-match/' },
  { id: 'tools-lattiz-advance', articleNumber: 'Homepage', group: 'Tools / Milchsysteme / Lattiz', name: 'Lattiz Advance', brand: 'Lattiz', category: 'Tool', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/lattiz-advance/' },
  { id: 'tools-lattiz', articleNumber: 'Homepage', group: 'Tools / Milchsysteme', name: 'Lattiz', brand: 'Lattiz', category: 'Tool', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/lattiz/' },
  { id: 'tools-cream-creator', articleNumber: 'Homepage', group: 'Tools / Milchsysteme', name: 'Cream Creator', brand: 'Kaffeewerk', category: 'Tool', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/cream-creator/' },
  { id: 'tools-uebermilk', articleNumber: 'Homepage', group: 'Tools / Milchsysteme', name: 'Uebermilk', brand: 'Uebermilk', category: 'Tool', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/uebermilk/' },
  { id: 'tools-puqpress-q', articleNumber: 'Homepage', group: 'Tools / Tamper / PuqPress', name: 'Q', brand: 'PuqPress', category: 'Tamper', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/q/' },
  { id: 'tools-puqpress-m3', articleNumber: 'Homepage', group: 'Tools / Tamper / PuqPress', name: 'M3', brand: 'PuqPress', category: 'Tamper', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/m3/' },
  { id: 'tools-eureka-under-the-grinder', articleNumber: 'Homepage', group: 'Tools / Tamper / Eureka', name: 'Under-The-Grinder', brand: 'Eureka', category: 'Tamper', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkt/under-the-grinder/' },
  { id: 'tools-wasserfilter', articleNumber: 'Homepage', group: 'Tools / Wasser', name: 'Wasserfilteranlage', brand: 'Kaffeewerk', category: 'Tool', price: 0, sourceUrl: 'https://www.kaffeewerk.de/' },
  { id: 'tools-heisswasser', articleNumber: 'Homepage', group: 'Tools / Heisswasser', name: 'Heisswasserspender', brand: 'Marco', category: 'Tool', price: 0, sourceUrl: 'https://www.kaffeewerk.de/produkte-kaffeewerk/kaffeevollautomaten/jura.html' },
  { id: 'tools-installation', articleNumber: 'Service', group: 'Tools / Service', name: 'Erstinstallations-Kit', brand: 'Kaffeewerk', category: 'Service', price: 0, sourceUrl: 'https://www.kaffeewerk.de/' },
];

const CATALOG_STORAGE_KEY = 'kaffeewerk.vertrieb.homepageCatalog';
const STANDARD_CATALOG_VERSION = 'homepage-catalog-v3';

const VERSION_HISTORY = [
  { version: '2.3.7', date: '2026-05-28', author: 'Codex', changes: [
    'Angenommene und abgelehnte Angebote werden mit Angebots-Snapshot gespeichert und in den Status-Tabs stabil angezeigt',
    'Status-Tabs bleiben auch bei alten Status-IDs ohne aktuellen Sync-Treffer nicht mehr leer',
  ]},
  { version: '2.3.6', date: '2026-05-28', author: 'Codex', changes: [
    'Logo-Klick fuehrt direkt zur Uebersicht',
    'Klick auf die Initialen oeffnet die eigene Benutzereinstellung fuer Admins',
  ]},
  { version: '2.3.5', date: '2026-05-28', author: 'Codex', changes: [
    'Angebotsfilter Angenommen und Abgelehnt zeigen jetzt die passenden Angebotskarten statt nur Zaehler',
  ]},
  { version: '2.3.4', date: '2026-05-28', author: 'Codex', changes: [
    'PDF-Parser uebernimmt Unterpositionen wie 1.1 und 1.2 als eigene Angebotspositionen',
    'PDF-Anhaenge werden auch bei erfolgreichem Standardparser per visueller Zeilenextraktion gegengeprueft',
  ]},
  { version: '2.3.3', date: '2026-05-28', author: 'Codex', changes: [
    'Globale Einstellungen sind benutzerunabhaengig und duerfen serverseitig von Admins aus der globalen Benutzerverwaltung gespeichert werden',
  ]},
  { version: '2.3.2', date: '2026-05-28', author: 'Codex', changes: [
    'Speicherung globaler Konfigurationen reagiert jetzt auch auf reine Anschluss-Regel-Aenderungen',
  ]},
  { version: '2.3.1', date: '2026-05-28', author: 'Codex', changes: [
    'Outlook-Import erkennt Angebotsnummern im Format JJ-NNNN und JJ-INNN, z.B. 26-1374 und 26-I452',
  ]},
  { version: '2.3.0', date: '2026-05-28', author: 'Codex', changes: [
    'Versionssprung nach Stabilisierung von Persistenz, Konfiguration, Kalkulationen und Mailversand',
  ]},
  { version: '2.2.22', date: '2026-05-28', author: 'Codex', changes: [
    'Globale Einstellungen werden serverseitig validiert und nur von Admins gespeichert',
    'Persoenlicher Cloud-Arbeitsstatus wird von globalen Konfigurationsfeldern getrennt',
  ]},
  { version: '2.2.21', date: '2026-05-28', author: 'Codex', changes: [
    'PLZ wird in Angebotsmail und vCard-Kontakt explizit mitgefuehrt',
  ]},
  { version: '2.2.20', date: '2026-05-28', author: 'Codex', changes: [
    'Gespeicherte Kalkulationen werden im UI wieder als Liste angezeigt',
    'Kalkulationen koennen geladen, archiviert und geloescht werden',
  ]},
  { version: '2.2.19', date: '2026-05-28', author: 'Codex', changes: [
    'Angebotsanforderung haengt automatisch eine vCard mit den Kundendaten an',
  ]},
  { version: '2.2.18', date: '2026-05-28', author: 'Codex', changes: [
    'Angebotsstatus-Zaehlung fuer Offen, Angenommen und Abgelehnt korrigiert',
    'Uebernommene und abgelehnte Angebote aktualisieren den gespeicherten Status',
  ]},
  { version: '2.2.17', date: '2026-05-28', author: 'Codex', changes: [
    'Cloud-Sync schuetzt lokale Kalkulations- und Checklisten-Drafts vor aelteren Remote-Staenden',
    'Arbeitsstatus wird beim Laden anhand von updatedAt zusammengefuehrt statt hart ueberschrieben',
  ]},
  { version: '2.2.16', date: '2026-05-28', author: 'Codex', changes: [
    'Admin-Konfigurationen werden als gemeinsame App-Konfiguration in Upstash Redis gespeichert',
    'Anschluss-Regeln, Checkpunkte, Integrationen, Mailvorlagen und Technikwerte werden geraete- und benutzeruebergreifend geladen',
  ]},
  { version: '2.2.15', date: '2026-05-28', author: 'Codex', changes: [
    'Konfigurierbarer Preislisten-Link in den Einstellungen ergaenzt',
    'Preislisten-Button im Katalogbereich der Kalkulation eingefuegt',
  ]},
  { version: '2.2.14', date: '2026-05-28', author: 'Codex', changes: [
    'Checklisten-Bearbeitungsstand wird beim Bearbeiten automatisch in der aktiven Checkliste gesichert',
    'Kundenname wird vor Mailbetreff und Checklisten-Mail von angehaengten E-Mail-Platzhaltern bereinigt',
    'Angebots-Mail stellt Positionsdaten kompakt in einer Zeile dar',
  ]},
  { version: '2.2.13', date: '2026-05-28', author: 'Codex', changes: [
    'Checklisten-Mail uebernimmt die Vorgabewerte aus Dosierung und Kalibrierung',
    'Technik-Standardwerte werden im Checklisten-Snapshot fuer den Mailversand mitgespeichert',
  ]},
  { version: '2.2.12', date: '2026-05-28', author: 'Codex', changes: [
    'Checklisten-Mail ersetzt Template-Platzhalter serverseitig durch echte Werte',
    'Interne Checklisten-Feldnamen werden in der Mail als fachliche Bezeichnungen ausgegeben',
    'Schulungsthemen werden in der Mail mit lesbaren Themen statt topicChecks/t1 dargestellt',
  ]},
  { version: '2.2.11', date: '2026-05-28', author: 'Codex', changes: [
    'Mailversand setzt die Absenderadresse serverseitig automatisch in CC',
  ]},
  { version: '2.2.10', date: '2026-05-28', author: 'Codex', changes: [
    'PDF-Parser nutzt bei fehlerhafter Standardextraktion einen visuellen Zeilenmodus ueber PDF-Koordinaten',
    'Angebote zeigen den verwendeten PDF-Extraktionsmodus zur Diagnose an',
  ]},
  { version: '2.2.9', date: '2026-05-28', author: 'Codex', changes: [
    'PDF-Anhaenge aus Outlook-Angeboten koennen direkt aus der App geoeffnet werden',
    'PDF-Stream nutzt denselben Microsoft-Graph-Anhang, der auch fuer den Parser verwendet wird',
  ]},
  { version: '2.2.8', date: '2026-05-28', author: 'Codex', changes: [
    'PDF-Parser normalisiert Positionskopf-Varianten mit Stueck, Stück, Artikel / Bezeichnung und variablen Leerzeichen',
    'Parserdiagnose zeigt Positionskandidaten, verworfene Kandidaten und Summenkandidaten',
    'Parser-Regressionstests fuer HOHOFFS-aehnlichen Backend-Fall und Summenvarianten ergaenzt',
  ]},
  { version: '2.2.7', date: '2026-05-28', author: 'Codex', changes: [
    'PDF-Parser erkennt Positionen jetzt auch ohne exakt extrahierten Tabellenkopf',
    'Gesamt Netto wird flexibler erkannt oder aus Positionssummen berechnet',
    'Backend-Unterschied zwischen pypdf-Test und pdf-parse-Text wird durch Loose-Body-Fallback abgefangen',
  ]},
  { version: '2.2.6', date: '2026-05-28', author: 'Codex', changes: [
    'Explizite PDF-zu-UI-Zuordnung fuer Angebot-Nr., Kunde, Datum, Netto und Positionen ergaenzt',
    'PDF-Import meldet konkret, welche UI-Felder nicht aus der Angebotsvorlage befuellt werden konnten',
  ]},
  { version: '2.2.5', date: '2026-05-28', author: 'Codex', changes: [
    'PDF-Parser liefert detaillierte Diagnosegruende, wenn keine Positionen oder Summen erkannt werden',
    'Angebotskarten zeigen PDF-Textlaenge, Seiten und Parser-Diagnose an',
  ]},
  { version: '2.2.4', date: '2026-05-28', author: 'Codex', changes: [
    'Outlook-PDF-Anhaenge werden Graph-konform ohne contentBytes im Attachment-Select geladen',
    'PDF-Inhalte werden separat ueber attachments/{id}/$value abgerufen',
  ]},
  { version: '2.2.3', date: '2026-05-28', author: 'Codex', changes: [
    'PDF-Parser nutzt wieder statischen pdf-parse Import wie in der funktionierenden Variante',
    'Mehrere PDF-Anhaenge werden einzeln ausgewertet, fehlerhafte Anhaenge blockieren nicht mehr das Angebots-PDF',
    'PDF-Dateiname und Parserfehler werden in Angebot und Detailansicht sichtbar gemacht',
  ]},
  { version: '2.2.2', date: '2026-05-28', author: 'Codex', changes: [
    'Outlook-Hinweis stellt klar, dass 26-1374 nur ein Formatbeispiel ist',
  ]},
  { version: '2.2.1', date: '2026-05-28', author: 'Codex', changes: [
    'Outlook-Import dedupliziert Mails nach Angebotsnummer',
    'PDF-Import prueft alle PDF-Anhaenge und verwendet das beste erkannte Angebot',
    'Parser-Test fuer Angebotsnummer, Kunde, Positionen und Netto aus Beispiel-PDF bestaetigt',
  ]},
  { version: '2.2.0', date: '2026-05-28', author: 'Codex', changes: [
    'Konsolidiertes Release fuer Outlook-Import, PDF-Auswertung, Checklisten-Uebergabe und Status-Sync',
    'Angebote, Kalkulationen und Checklisten werden uebergreifend fortgefuehrt',
    'Uebersicht und Navigation fuer die aktuellen Arbeitsbereiche vereinheitlicht',
  ]},
  { version: '2.10.39', date: '2026-05-28', author: 'Codex', changes: [
    'Checklisten-Kacheln in der Uebersicht sind wie Angebots-Kacheln klickbar',
  ]},
  { version: '2.10.38', date: '2026-05-28', author: 'Codex', changes: [
    'Angebotsseite stellt den zuletzt gespeicherten Sync beim Oeffnen wieder her',
    'Uebergebene oder abgelehnte Angebote werden nur ausgeblendet, nicht aus dem letzten Sync geloescht',
  ]},
  { version: '2.10.37', date: '2026-05-28', author: 'Codex', changes: [
    'Outlook-PDF-Anhaenge werden notfalls ueber Microsoft Graph $value geladen',
    'Kundenname wird beim Outlook-Import aus dem Betreff uebernommen, falls die PDF leer bleibt',
    'Aus Angeboten und Kalkulationen wird direkt eine neue gespeicherte Checkliste angelegt',
  ]},
  { version: '2.10.36', date: '2026-05-28', author: 'Codex', changes: [
    'Outlook-Import zeigt nur Mails mit Angebotsnummer im Betreff wie 26-1374',
    'PDF-Parser uebernimmt Positionen robuster aus Angebots-PDFs',
    'Betrefffilter, importierte und abgelehnte Mails bleiben im Arbeitsstatus gespeichert',
  ]},
  { version: '2.10.35', date: '2026-05-27', author: 'Codex', changes: [
    'Outlook-Angebotsimport um Zeitraumfilter 1, 2 oder 6 Monate zurueck erweitert',
    'Bereits in Checklisten uebergebene Mails werden gemerkt und aus dem Import ausgeblendet',
  ]},
  { version: '2.10.34', date: '2026-05-27', author: 'Codex', changes: [
    'Outlook-Import liest Angebots-PDF-Anhaenge aus und uebernimmt Kunde, Angebotsnummer und Positionen',
    'PDF-Parser fuer Kaffeewerk-Angebotslayout ergaenzt',
  ]},
  { version: '2.10.33', date: '2026-05-27', author: 'Codex', changes: [
    'Outlook-Konfiguration auf notwendige Felder reduziert',
    'Outlook-Sync nutzt standardmaessig das angemeldete O365-Benutzerpostfach',
    'Outlook-Parameter bleiben ueber den bestehenden App-State gespeichert',
  ]},
  { version: '2.10.32', date: '2026-05-27', author: 'Codex', changes: [
    'Outlook-Betrefffilter * wird als kein Filter behandelt',
    'Outlook-Verbindungstest liest echte Mails statt nur UI-Status zu setzen',
  ]},
  { version: '2.10.31', date: '2026-05-27', author: 'Codex', changes: [
    'Outlook-Sync filtert nicht mehr automatisch auf angemeldete Benutzer-Mail',
    'Outlook-Betrefffilter wird aus den Integrationseinstellungen uebergeben',
  ]},
  { version: '2.10.30', date: '2026-05-27', author: 'Codex', changes: [
    'PDF-Export oeffnet stabile druckbare HTML-Datei statt leerem Browserfenster',
  ]},
  { version: '2.10.29', date: '2026-05-27', author: 'Codex', changes: [
    'Checklisten-Mailbetreff enthaelt Angebotsnummer statt Datum',
  ]},
  { version: '2.10.28', date: '2026-05-27', author: 'Codex', changes: [
    'Checklisten-Mail uebernimmt alle Eingabedaten je Checkpunkt',
    'Fotos aus Checklisten werden beim Mailversand als Anhaenge mitgesendet',
  ]},
  { version: '2.10.27', date: '2026-05-27', author: 'Codex', changes: [
    'Microsoft-Graph-Mailversand nutzt konfiguriertes Sendepostfach und Benutzeradresse als Reply-To',
    'Option MICROSOFT_SEND_AS_USER dokumentiert fuer echte Benutzerpostfach-Absender',
  ]},
  { version: '2.10.26', date: '2026-05-27', author: 'Codex', changes: [
    'Cloud-Sync speichert einen schlanken Status ohne Foto-Binaerdaten fuer PC/iPhone',
    'Checklistenversand nutzt den gleichen serverseitigen Mailversand wie Kalkulation',
    'PDF-Export fuer Kalkulation und Checkliste als Druck/PDF-Ausgabe ergaenzt',
  ]},
  { version: '2.10.25', date: '2026-05-27', author: 'Codex', changes: [
    'Arbeitsstatus wird nach Microsoft Login serverseitig pro Benutzer synchronisiert',
    'PC- und iPhone-Wechsel behalten aktive Ansicht, Kalkulation, Angebote und Checklistenstatus bei',
  ]},
  { version: '2.10.24', date: '2026-05-27', author: 'Codex', changes: [
    'Uebersicht berechnet Angebotszahlen aus synchronisierten Angeboten statt Demo-Daten',
    'Checklistenstatus wird aus vorhandenen Checklisten-Dokumenten abgeleitet',
  ]},
  { version: '2.10.23', date: '2026-05-27', author: 'Codex', changes: [
    'Outlook-Sync nutzt gemeinsamen Microsoft-Graph-Client mit konkreten Graph-Fehlerhinweisen',
    'ERP-Sync zeigt Graph-Code und Handlungshinweis im Frontend an',
  ]},
  { version: '2.10.22', date: '2026-05-27', author: 'Codex', changes: [
    'Menue-Reihenfolge auf Uebersicht, Kalkulation, Angebote, Checkliste angepasst',
    'Einstellungen, Benutzerverwaltung und Abmelden in Burgermenue verschoben',
    'Versendete Kalkulation kann als Grundlage fuer eine Checkliste genutzt werden',
    'Benutzeranzeige im Header auf Initialen reduziert und Versionsnummer in den Hauptbereich verschoben',
  ]},
  { version: '2.10.21', date: '2026-05-27', author: 'Codex', changes: [
    'Uebersicht zeigt Angebote, Kalkulation und Checkliste mit aktuellem Bearbeitungsstatus',
    'Bearbeitungsstand fuer Kalkulation, Angebote und Checkliste wird lokal gespeichert und fortsetzbar gemacht',
  ]},
  { version: '2.10.20', date: '2026-05-27', author: 'Codex', changes: [
    'Netto-Preis-Hinweis berechnet Rabatte weiterhin und zeigt den Hinweis pro Position',
    'Innendienst-Mail uebernimmt Netto-Preis-Hinweis und Rabattdarstellung je Artikel',
  ]},
  { version: '2.10.19', date: '2026-05-27', author: 'Codex', changes: [
    'Freie Position nutzt Hersteller-Auswahl aus dem Homepage-Katalog',
  ]},
  { version: '2.10.18', date: '2026-05-27', author: 'Codex', changes: [
    'Serviceposition Installation und Einweisung in Erstinstallations-Kit umbenannt',
  ]},
  { version: '2.10.17', date: '2026-05-27', author: 'Codex', changes: [
    'Kalkulation um individuelle Artikelpositionen mit Hersteller, Kategorie und Preis erweitert',
  ]},
  { version: '2.10.16', date: '2026-05-27', author: 'Codex', changes: [
    'Flexmiete auf Laufzeitauswahl 9 oder 24 Monate umgestellt',
  ]},
  { version: '2.10.15', date: '2026-05-27', author: 'Codex', changes: [
    'Innendienst-Anforderungen um Roesterfinanzierung mit Roestername und Ansprechpartner-Mail erweitert',
  ]},
  { version: '2.10.14', date: '2026-05-27', author: 'Codex', changes: [
    'Innendienst-Mail zeigt Datenblaetter, Flexmiete und Leasing-Laufzeit als eigenen Anforderungsblock',
  ]},
  { version: '2.10.13', date: '2026-05-27', author: 'Codex', changes: [
    'Innendienst-Optionen um Leasing-Laufzeit mit 24, 36, 48 und 60 Monaten erweitert',
    'Flexmiete 24 Monate durch Leasing-Auswahl ersetzt',
  ]},
  { version: '2.10.12', date: '2026-05-27', author: 'Codex', changes: [
    'Hinweise fuer den Innendienst um Checkboxen fuer Datenblaetter und Flexmiete erweitert',
    'Ausgewaehlte Innendienst-Optionen werden in der Angebotsanforderung uebergeben',
  ]},
  { version: '2.10.11', date: '2026-05-27', author: 'Codex', changes: [
    'Innendienst-Mail ohne Artikeltabelle auf einfache Positionsliste reduziert',
    'Kalkulation um optionale Hersteller-Rabattlogik fuer gewaehlte Produkte erweitert',
  ]},
  { version: '2.10.10', date: '2026-05-27', author: 'Codex', changes: [
    'Lokalen Seed-Benutzer dirk entfernt',
    'Fester Microsoft-Admin dw@kaffeewerk.de bleibt als geschuetzter Benutzer bestehen',
  ]},
  { version: '2.10.9', date: '2026-05-27', author: 'Codex', changes: [
    'Angebotsanforderungen verwenden die Benutzer-Mailadresse als Absender',
    'Microsoft Graph sendet ueber das Benutzerpostfach statt ueber das globale Sendepostfach',
  ]},
  { version: '2.10.8', date: '2026-05-27', author: 'Codex', changes: [
    'Benutzerverwaltung zeigt breite Tabellen vollstaendig mit horizontalem Scrollbereich',
    'Separater Innendienst-Empfaenger fuer Angebotsanforderungen pro Benutzer ergaenzt',
  ]},
  { version: '2.10.7', date: '2026-05-27', author: 'Codex', changes: [
    'Microsoft-Graph-Mailversand mit getrenntem Sendepostfach MICROSOFT_SEND_MAILBOX erweitert',
    'Graph-403-Fehler liefern jetzt konkrete Hinweise zu Mail.Send und Application Access Policies',
  ]},
  { version: '2.10.6', date: '2026-05-26', author: 'Codex', changes: [
    'Benutzerverwaltung um Mail-Empfaenger fuer Angebotserstellung und Checklisten erweitert',
    'Checklisten- und Angebotsversand nutzen den benutzerspezifischen Mail-Empfaenger mit E-Mail-Fallback',
  ]},
  { version: '2.10.5', date: '2026-05-26', author: 'Codex', changes: [
    'Angebotsanforderungen koennen ueber Microsoft Graph Outlook/Azure versendet werden',
    'Angebote-Seite um expliziten Outlook-Sync-Button erweitert',
  ]},
  { version: '2.10.4', date: '2026-05-26', author: 'Codex', changes: [
    'Kalkulation um Adressfelder in den Kundendaten erweitert',
    'Kalkulationspositionen mit editierbarem Preis, Kommentar, Netto-ohne-Rabatt-Option und Homepage-Link ergaenzt',
    'Vorschau-Auswahl unterhalb der Produktauswahl entfernt',
  ]},
  { version: '2.10.3', date: '2026-05-26', author: 'Codex', changes: [
    'Homepage-Katalog anhand des aktuellen Menues Produkte und Tools vervollstaendigt',
    'Lokaler Startkatalog der Kalkulation mit dem Server-Katalog synchronisiert',
  ]},
  { version: '2.10.2', date: '2026-05-26', author: 'Codex', changes: [
    'Anschluss-Regeln um 400V-Checkbox erweitert',
    'Checkliste unterscheidet bauseitige 16A-CEE-Steckdose und Schuko-Steckdose',
  ]},
  { version: '2.10.1', date: '2026-05-26', author: 'Codex', changes: [
    'Kalkulationsseite einspaltig wie Checkliste aufgebaut',
    'Kalkulationsfelder und Summen responsiv gegen Ueberlappungen abgesichert',
  ]},
  { version: '2.10.0', date: '2026-05-26', author: 'Codex', changes: [
    'Kalkulation nutzt gruppierten Homepage-Produktkatalog als Pulldown',
    'Homepage-Katalog kann per Aktualisieren-Button neu geladen und lokal gecached werden',
    'Menüstruktur Produkte/Marke/Geraet und Tools/Gruppen in der Artikelauswahl abgebildet',
  ]},
  { version: '2.9.5', date: '2026-05-26', author: 'Codex', changes: [
    'Kaffeewerk-Schriftlogo auch aus dem Login-Screen entfernt',
    'Login zeigt nur noch das Vertrieb-App-Icon und den App-Namen',
  ]},
  { version: '2.9.4', date: '2026-05-26', author: 'Codex', changes: [
    'Kaffeewerk-Schriftlogo aus dem App-Header entfernt',
    'Header zeigt nur noch das Vertrieb-App-Icon',
  ]},
  { version: '2.9.3', date: '2026-05-26', author: 'Codex', changes: [
    'Produktauswahl der Kalkulation um Homepage-Produkte und Produktgruppen erweitert',
    'Siebtraeger, Vollautomaten, Muehlen, Milchsysteme, Filterkaffee, Wasser und Service als Auswahl verfuegbar',
  ]},
  { version: '2.9.2', date: '2026-05-26', author: 'Codex', changes: [
    'Benutzerverwaltung bewahrt gespeicherte Felder fuer feste Admin-Benutzer',
    'Aktuell eingeloggter Benutzer wird nach Admin-Aenderungen aus den gespeicherten Werten aktualisiert',
  ]},
  { version: '2.9.1', date: '2026-05-26', author: 'Codex', changes: [
    'Kalkulation auf Homepage-Katalog ohne SelectLine-Bezug umgestellt',
    'Kalkulationslayout an Checklisten-Karten angeglichen',
    'Navigation bleibt beim Scrollen klickbar und Benutzerverwaltung behaelt gespeicherte Werte',
  ]},
  { version: '2.9.0', date: '2026-05-26', author: 'Codex', changes: [
    'Neue Kalkulationsseite fuer Verkaufsgespraeche ergaenzt',
    'Standard-Produktkatalog, Positionskalkulation und Angebotsanforderung per Mail hinzugefuegt',
  ]},
  { version: '2.8.6', date: '2026-05-26', author: 'Codex', changes: [
    'Buildfehler durch fehlende integrations-Prop im Angebote-Tab behoben',
    'SelectDocumentKindFlag nutzt weiterhin die konfigurierte SelectLine-Belegart',
  ]},
  { version: '2.8.5', date: '2026-05-26', author: 'Codex', changes: [
    'ERP-API liefert Implementation-Version zur Deployment-Kontrolle',
    'Erwartete 404 bei documentKey-Kandidaten werden nicht mehr als Hauptfehler geloggt',
  ]},
  { version: '2.8.4', date: '2026-05-26', author: 'Codex', changes: [
    'Konfigurierte SelectLine-Belegart wird bei documentKey-Kandidaten bevorzugt',
    'Positionsdebug zeigt Kandidatenreihenfolge fuer /Documents/{documentKey}/Positions',
  ]},
  { version: '2.8.3', date: '2026-05-26', author: 'Codex', changes: [
    'Positionsabruf testet mehrere SelectLine-documentKey-Kandidaten statt nur Kind/Nummer',
    '404 bei einzelnen Positionsabrufen bricht den ERP-Sync nicht mehr ab',
  ]},
  { version: '2.8.2', date: '2026-05-26', author: 'Codex', changes: [
    'Belegart wird beim ERP-Sync an SelectLine uebergeben',
    'DocumentKey-Fallback fuer Positionsabrufe nutzt DocumentKind/DocumentNumber und Default F',
  ]},
  { version: '2.8.1', date: '2026-05-26', author: 'Codex', changes: [
    'SelectLine-Positionsmapping an echte Positionsfelder aus Swagger angepasst',
    'Positionsname, Zusatzbeschreibung, Menge, Einheit und Gesamtpreis werden korrekt uebernommen',
  ]},
  { version: '2.8.0', date: '2026-05-26', author: 'Codex', changes: [
    'SelectLine-Login an Swagger-Curl angepasst',
    'ERP-Anmeldung nutzt eingegebenen User und Passwort plus SELECTLINE_APP_KEY',
  ]},
  { version: '2.7.9', date: '2026-05-26', author: 'Codex', changes: [
    'Serverless-Function-Anzahl fuer Vercel Hobby reduziert',
    'Nicht genutzte Einzelrouten fuer Kunden, Artikel und Lieferanten entfernt',
  ]},
  { version: '2.7.8', date: '2026-05-26', author: 'Codex', changes: [
    'Debug-Logging fuer SelectLine-Belegabruf und Belegpositionen ergaenzt',
    'Debug-Endpunkt und lokale Logdatei fuer ERP-Analyse eingerichtet',
  ]},
  { version: '2.7.7', date: '2026-05-26', author: 'Codex', changes: [
    'Zeichensatz-Probleme nach Build korrigiert',
    'Betroffene App-, HTML- und Dokumentationsdateien wieder als UTF-8 gespeichert',
  ]},
  { version: '2.7.6', date: '2026-05-26', author: 'Codex', changes: [
    'SelectLine-Abruf um Vertreterfilter SalesmanNumber erweitert',
    'Belegpositionen werden je Beleg ueber /Documents/{documentKey}/Positions geladen',
    'Benutzerverwaltung um Vertreternummer fuer SelectLine-Abgleich erweitert',
    'Aktive Angebote werden standardmaessig gefiltert',
  ]},
  { version: '2.7.5', date: '2026-05-19', author: 'Codex', changes: [
    'ERP-Sync-Anmeldebereich in SelectLine-Konfiguration ergaenzt',
    'SelectLine-Token wird per ERP-Anmeldung serverseitig erzeugt und als HttpOnly-Cookie genutzt',
  ]},
  { version: '2.7.4', date: '2026-05-18', author: 'Codex', changes: [
    'Vertrieb-App-Icon im Login, Header und als Favicon eingebunden',
    'Login-Screen visuell an die reduzierte App-Kachel-Anmutung angeglichen',
  ]},
  { version: '2.7.3', date: '2026-05-18', author: 'Codex', changes: [
    'Admin-Rechte fuer dw@kaffeewerk.de unabhaengig vom lokalen Browser-Speicher erzwungen',
  ]},
  { version: '2.7.2', date: '2026-05-18', author: 'Codex', changes: [
    'Microsoft-Benutzer dw@kaffeewerk.de als Admin mit voller Berechtigung hinterlegt',
  ]},
  { version: '2.7.1', date: '2026-05-18', author: 'Codex', changes: [
    'Login-Screen auf Microsoft Login als einzigen sichtbaren Anmeldeweg reduziert',
    'Benutzerverwaltung als Microsoft-Benutzer-/Rollenmapping geschaerft',
  ]},
  { version: '2.7.0', date: '2026-05-12', author: 'Codex', changes: [
    'SelectLine-/SL.mobile-Login ueber POST /Login mit User, Passwort und AppKey',
    'Serverseitige Token-Erzeugung mit HttpOnly-Cookie statt statischem Bearer-Token im Frontend',
    'ERP-Routen auf Documents, Customers, Articles und Suppliers umgestellt',
    'Versionierter lokaler App-Speicher mit Schema-Version und Persistenz fuer Checklisten, Benutzer und Einstellungen',
  ]},
  { version: '2.6.0', date: '2026-05-11', author: 'Dirk W.', changes: [
    'Vollständige Versionierung pro Checkliste — jede Speicherung legt eine neue Version an',
    'Versions-Historie-Modal mit allen früheren Ständen (klickbar je Version)',
    'Globaler "Installation komplett abbrechen"-Button mit Gesamt-Begründung',
    'Punkt-Blocker und Komplett-Abbruch parallel nutzbar',
    'Neuer Status "abgebrochen" mit eigener Markierung in Liste und Banner',
    'Versions-Pill (v1, v2, ...) direkt in Übersichtsliste sichtbar',
  ]},
  { version: '2.5.0', date: '2026-05-11', author: 'Dirk W.', changes: [
    'Checklisten bearbeiten statt neu anlegen — Update überschreibt bestehenden Eintrag',
    'Alle Admin-Settings unter einem Menü "Einstellungen" konsolidiert',
    'Spezialisierte Checkpunkt-Typen: Standort+Foto, Stellfläche, Anschlüsse (auto-calc), Dosierung, Schulung, Notiz+Foto',
    'Pro Checkpunkt: "Verhindert Installation"-Markierung mit Begründung',
    'Anschluss-Bedarf wird automatisch aus Angebot berechnet (Steckdosen, Eckventile, Siphons)',
    'Dosierung-Modi: nach Technik-Standards oder individuell (mit Standards als Vorlage)',
    'Schulungs-Themen als konfigurierbare Checkbox-Liste (Settings)',
    'Standort-Auswahl mit Foto und optionalem Zusatztext',
    'Stellflächen-Erfassung: Breite/Tiefe + Anschlussabstände auf der Arbeitsplatte',
    'Checklisten in Verlaufsliste löschbar',
  ]},
  { version: '2.4.0', date: '2026-05-11', author: 'Dirk W.', changes: [
    'Fotofunktion: echte Kamera- und Bibliothek-Auswahl',
    'Automatische Bildkomprimierung auf max. 1600px (JPEG 85%)',
    'Vollbild-Vorschau, Ersetzen und Entfernen von Fotos',
    'Übersicht: Angebote-Metriken (Gesamt, Offen, Angenommen, Abgelehnt)',
    'Status "gewonnen" → "angenommen", "verloren" → "abgelehnt"',
    'Klickbare Metric-Cards springen direkt in den passenden Tab',
  ]},
  { version: '2.3.0', date: '2026-05-11', author: 'Dirk W.', changes: [
    'SelectLine NEO REST-API als ERP-Quelle (parallel zu Outlook)',
    'Microsoft 365 Outlook/Exchange-Anbindung via Graph API',
    'SMTP-Versand via Microsoft 365 (smtp.office365.com)',
    'Erweiterte Benutzerverwaltung — Edit-Modal mit allen Feldern',
    'Pro Benutzer konfigurierbar: SLN-Sachbearbeiter, ERP-Quelle, Mail-Benachrichtigungen',
    'Neuer Admin-Tab "Integrationen" mit Verbindungstest',
    'E-Mail-Vorlagen mit Platzhaltern (Checkliste, Angebot, Wochenbericht)',
    'Checkliste per Mail versenden — Empfänger automatisch aus Notify-Settings',
  ]},
  { version: '2.2.0', date: '2026-05-11', author: 'Dirk W.', changes: [
    'Technische Leitlinien zur Inbetriebnahme integriert',
    'Neuer Tab "Technik-Standards" mit kategorisierten Empfehlungen',
    'Änderbare Maschinen-Vorgabewerte (nur Admin-konfigurierbar)',
    'Kontextuelle Leitlinien-Hinweise pro Checkpunkt in der Checkliste',
    'Vorgabewerte als Banner in jeder neuen Inbetriebnahme sichtbar',
  ]},
  { version: '2.1.0', date: '2026-05-11', author: 'System', changes: [
    'ERP-Angebotsintegration via Gmail-Sync',
    'PDF-Artikelextraktion → direkt in Checkliste',
    'Benutzerspezifische Angebotsansicht (benutzerabhängig gefiltert)',
    'Versionsverlauf mit Changelog',
    'Checklisten-Detailansicht (Verlauf + Status)',
    'E-Mail-Feld in Benutzerverwaltung für Gmail-Sync',
  ]},
  { version: '2.0.0', date: '2026-05-08', author: 'Dirk W.', changes: [
    'Kaffeewerk CI komplett überarbeitet (helles Warm-Grau)',
    'Echtes Logo integriert (PNG mit transparentem Hintergrund)',
    'Barlow Condensed Typografie für Headlines + Navigation',
    'Olivgrün als Primärfarbe, dunkles Braun als Akzent',
  ]},
  { version: '1.1.0', date: '2026-05-05', author: 'System', changes: [
    'GitHub/Vercel-Deployment konfiguriert (vercel.json)',
    'React 18 Migration aus Widget-Prototype',
    'CSS-Variablen-System für einfaches Theming',
  ]},
  { version: '1.0.0', date: '2026-05-01', author: 'System', changes: [
    'Erste Version — Login, Checkliste, Admin',
    'Konfigurierbare Checkpunkte mit Status-Farbsystem',
    'Foto-Anforderung, Notizfelder, Standort-Auswahl',
  ]},
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function fmt(n) { return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n); }
function fmtDate(s) { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}.${m}.${y}`; }
function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function openPrintDocument(title, bodyHtml) {
  const documentHtml = `<!doctype html>
    <html lang="de">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: Arial, sans-serif; color: #2a1f15; margin: 28px; }
          .print-actions { display: flex; justify-content: flex-end; gap: 8px; margin-bottom: 18px; }
          .print-actions button { border: 1px solid #6f7d24; background: #6f7d24; color: #fff; padding: 8px 14px; border-radius: 4px; font-weight: 700; cursor: pointer; }
          h1 { font-size: 24px; margin: 0 0 8px; text-transform: uppercase; letter-spacing: 1px; }
          h2 { font-size: 15px; margin: 22px 0 8px; border-bottom: 1px solid #d7d0c6; padding-bottom: 4px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th, td { border-bottom: 1px solid #e2ddd5; padding: 7px 6px; text-align: left; vertical-align: top; }
          th { font-size: 11px; text-transform: uppercase; color: #6f6a60; }
          .meta { color: #6f6a60; font-size: 12px; margin-bottom: 14px; }
          .total { text-align: right; font-size: 16px; font-weight: 700; margin-top: 14px; }
          .box { border: 1px solid #d7d0c6; padding: 10px; margin-top: 10px; }
          @media print { body { margin: 16mm; } .print-actions { display: none; } }
        </style>
      </head>
      <body>
        <div class="print-actions">
          <button onclick="window.print()">Als PDF speichern / drucken</button>
        </div>
        ${bodyHtml}
        <script>
          window.addEventListener('load', function () {
            window.setTimeout(function () { window.print(); }, 500);
          });
        </script>
      </body>
    </html>`;

  const blob = new Blob([documentHtml], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank');
  if (!printWindow) {
    URL.revokeObjectURL(url);
    return false;
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
}

function loadVersionedState() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    if (parsed.schemaVersion === STORAGE_SCHEMA_VERSION) {
      return parsed.payload || {};
    }

    // Future migrations can be chained here. Unknown versions are ignored safely.
    return {};
  } catch {
    return {};
  }
}

function saveVersionedState(payload) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      schemaVersion: STORAGE_SCHEMA_VERSION,
      appVersion: APP_VERSION,
      savedAt: new Date().toISOString(),
      payload,
    }));
  } catch {
    // Storage can fail in private mode or when quota is exhausted; the app keeps working in memory.
  }
}

function clearVersionedState() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors; reset still clears the in-memory state.
  }
}

function normalizeUsersForApp(storedUsers = {}) {
  const merged = { ...INITIAL_USERS, ...storedUsers };
  Object.keys(merged).forEach((key) => {
    merged[key] = {
      ...merged[key],
      mailRecipient: merged[key].mailRecipient || merged[key].email || '',
      insideSalesRecipient: merged[key].insideSalesRecipient || merged[key].offerRecipient || 'buero@kaffeewerk.de',
    };
  });

  FORCED_ADMIN_EMAILS.forEach((email) => {
    const existing = merged[email] || {};
    merged[email] = {
      ...DW_ADMIN_USER,
      ...existing,
      email,
      mailRecipient: existing.mailRecipient || existing.email || email,
      insideSalesRecipient: existing.insideSalesRecipient || existing.offerRecipient || 'buero@kaffeewerk.de',
      role: 'admin',
      pass: existing.pass || '',
      notify: existing.notify || DW_ADMIN_USER.notify,
    };
  });

  return merged;
}

function normalizeAnschlussRules(rules = DEFAULT_ANSCHLUSS_RULES) {
  return rules.map((rule) => ({
    ...rule,
    is400v: Boolean(rule.is400v),
  }));
}

function normalizeIntegrationsForApp(storedIntegrations = {}) {
  const source = storedIntegrations || {};
  return {
    selectline: {
      ...DEFAULT_INTEGRATIONS.selectline,
      ...(source.selectline || {}),
    },
    outlook: {
      ...DEFAULT_INTEGRATIONS.outlook,
      ...(source.outlook || {}),
      subjectFilter: source.outlook?.subjectFilter ?? DEFAULT_INTEGRATIONS.outlook.subjectFilter,
      lookbackMonths: [1, 2, 6].includes(Number(source.outlook?.lookbackMonths))
        ? Number(source.outlook.lookbackMonths)
        : DEFAULT_INTEGRATIONS.outlook.lookbackMonths,
    },
    smtp: {
      ...DEFAULT_INTEGRATIONS.smtp,
      ...(source.smtp || {}),
    },
    catalog: {
      ...DEFAULT_INTEGRATIONS.catalog,
      ...(source.catalog || {}),
    },
  };
}

function normalizeWorkStatusForApp(storedWorkStatus = {}) {
  const source = storedWorkStatus || {};
  return {
    ...DEFAULT_WORK_STATUS,
    ...source,
    offers: {
      ...DEFAULT_WORK_STATUS.offers,
      ...(source.offers || {}),
      offers: Array.isArray(source.offers?.offers) ? source.offers.offers : [],
      importedIds: Array.isArray(source.offers?.importedIds) ? source.offers.importedIds : [],
      rejectedIds: Array.isArray(source.offers?.rejectedIds) ? source.offers.rejectedIds : [],
      statusArchive: source.offers?.statusArchive && typeof source.offers.statusArchive === 'object' ? source.offers.statusArchive : {},
    },
    calculation: {
      ...DEFAULT_WORK_STATUS.calculation,
      ...(source.calculation || {}),
      calculations: Array.isArray(source.calculation?.calculations) ? source.calculation.calculations : [],
    },
    checklist: {
      ...DEFAULT_WORK_STATUS.checklist,
      ...(source.checklist || {}),
    },
  };
}

function timestampMs(value) {
  const time = value ? new Date(value).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
}

function isMeaningfulCalculationStatus(status = {}) {
  return Boolean(status?.draft)
    || Number(status?.itemCount || 0) > 0
    || Number(status?.netTotal || 0) > 0
    || Boolean(status?.customer);
}

function isMeaningfulChecklistStatus(status = {}) {
  return Boolean(status?.draft)
    || Number(status?.pct || 0) > 0
    || Boolean(status?.customer);
}

function pickNewerArea(localArea = {}, remoteArea = {}, isMeaningful) {
  const localMeaningful = isMeaningful(localArea);
  const remoteMeaningful = isMeaningful(remoteArea);
  if (localMeaningful && !remoteMeaningful) return localArea;
  if (!localMeaningful && remoteMeaningful) return remoteArea;
  if (!localMeaningful && !remoteMeaningful) return { ...localArea, ...remoteArea };
  return timestampMs(remoteArea.updatedAt) >= timestampMs(localArea.updatedAt) ? remoteArea : localArea;
}

function mergeWorkStatus(localStatus = {}, remoteStatus = {}) {
  const local = normalizeWorkStatusForApp(localStatus);
  const remote = normalizeWorkStatusForApp(remoteStatus);
  return {
    ...local,
    ...remote,
    offers: timestampMs(remote.offers?.lastSyncAt) >= timestampMs(local.offers?.lastSyncAt) ? remote.offers : local.offers,
    calculation: pickNewerArea(local.calculation, remote.calculation, isMeaningfulCalculationStatus),
    checklist: pickNewerArea(local.checklist, remote.checklist, isMeaningfulChecklistStatus),
  };
}

function loadHomepageCatalog() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CATALOG_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed.products)) return null;
    if (parsed.catalogVersion !== STANDARD_CATALOG_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveHomepageCatalog(products, meta = {}) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CATALOG_STORAGE_KEY, JSON.stringify({
      ...meta,
      savedAt: new Date().toISOString(),
      products,
    }));
  } catch {
    // Catalog cache is optional; the default catalog remains available.
  }
}

function stripCloudOnlyData(value) {
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(stripCloudOnlyData);

  const result = {};
  Object.entries(value).forEach(([key, item]) => {
    if (key === 'microsoft') return;
    if (key === 'pass') {
      result[key] = '';
      return;
    }
    if (key === 'photo') {
      result[key] = item ? {
        name: item.name || 'Foto',
        type: item.type || '',
        size: item.size || 0,
        omittedFromCloud: true,
      } : item;
      return;
    }
    if (key === 'dataUrl') return;
    result[key] = stripCloudOnlyData(item);
  });
  return result;
}

function compactOfferForCloud(offer) {
  if (!offer || typeof offer !== 'object') return offer;
  return {
    id: offer.id,
    nr: offer.nr,
    kunde: offer.kunde,
    date: offer.date,
    status: offer.status,
    source: offer.source,
    subject: offer.subject,
    total: offer.total,
    pdfName: offer.pdfName,
    pdfAttachmentId: offer.pdfAttachmentId,
    pdfError: offer.pdfError,
    pdfAttachmentCount: offer.pdfAttachmentCount,
    pdfRawTextLength: offer.pdfRawTextLength,
    pdfDiagnostics: offer.pdfDiagnostics,
    pdfUiMapping: offer.pdfUiMapping,
    pdfPages: offer.pdfPages,
    pdfExtractionMode: offer.pdfExtractionMode,
    items: Array.isArray(offer.items) ? offer.items.slice(0, 80).map(item => ({
      pos: item.pos,
      art: item.art,
      desc: item.desc,
      qty: item.qty,
      unit: item.unit,
      price: item.price,
    })) : [],
  };
}

function compactStatusArchiveForCloud(statusArchive = {}) {
  if (!statusArchive || typeof statusArchive !== 'object') return {};
  return Object.fromEntries(
    Object.entries(statusArchive)
      .filter(([, offer]) => offer && typeof offer === 'object')
      .map(([id, offer]) => [id, compactOfferForCloud(offer)])
  );
}

function buildCloudStatePayload(payload) {
  const clean = stripCloudOnlyData(payload);
  delete clean.users;
  delete clean.items;
  delete clean.machineValues;
  delete clean.integrations;
  delete clean.mailTemplates;
  delete clean.trainingTopics;
  delete clean.anschlussRules;
  const offers = clean.workStatus?.offers?.offers;
  if (Array.isArray(offers)) {
    const statusArchive = clean.workStatus?.offers?.statusArchive;
    clean.workStatus = {
      ...clean.workStatus,
      offers: {
        ...clean.workStatus.offers,
        offers: offers.map(compactOfferForCloud),
        statusArchive: compactStatusArchiveForCloud(statusArchive),
      },
    };
  }
  return clean;
}

function buildSharedConfigPayload(payload) {
  return stripCloudOnlyData({
    users: payload.users,
    items: payload.items,
    machineValues: payload.machineValues,
    integrations: payload.integrations,
    mailTemplates: payload.mailTemplates,
    trainingTopics: payload.trainingTopics,
    anschlussRules: payload.anschlussRules,
  });
}

function formatBytes(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function cleanCustomerName(value = '') {
  return String(value || '')
    .replace(/\s+(?:E-?Mail|Mailadresse)\s*:?\s*[\w.+-]+@[\w.-]+\.[a-z]{2,}/ig, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Liest gewählte Bilddatei, komprimiert auf max. 1600px Längsseite, ruft Callback mit
// {name, dataUrl, size, originalSize} auf. Hält Speicherbedarf und Versand-Größe in Schach.
function handlePhotoSelected(e, callback) {
  const file = e.target.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = ev => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
        else { width = Math.round(width * MAX / height); height = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      const compressedSize = Math.round((dataUrl.length - 22) * 3 / 4); // base64 → bytes
      callback({
        name: file.name,
        dataUrl,
        size: compressedSize,
        originalSize: file.size,
        addedAt: new Date().toISOString(),
      });
    };
    img.src = ev.target.result;
  };
  reader.readAsDataURL(file);
  e.target.value = ''; // reset, damit gleicher File nochmal gewählt werden kann
}

const STATUS_LABEL = { offen: 'Offen', angenommen: 'Angenommen', abgelehnt: 'Abgelehnt' };
const STATUS_CLASS  = { offen: 'tag-hint', angenommen: 'tag-ok', abgelehnt: 'tag-problem' };

// ─── LOGO ─────────────────────────────────────────────────────────────────────

function KWLogo({ height = 30 }) {
  return <img src={kwLogo} alt="KAFFEEWERK" style={{ height, width: 'auto', display: 'block' }} />;
}

function AppIcon({ size = 40, className = '' }) {
  return (
    <img
      src="/icon500x500_app_vertrieb.png"
      alt="Vertrieb"
      className={className}
      style={{ width: size, height: size }}
    />
  );
}

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────

function Toast({ msg }) {
  if (!msg) return null;
  return <div className="toast">{msg}</div>;
}

function Modal({ open, onClose, title, children, width = 560 }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: width }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────

function LoginScreen() {
  const [err, setErr] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth_error')) {
      setErr(params.get('message') || 'Microsoft Login fehlgeschlagen.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  function loginWithMicrosoft() {
    window.location.href = '/api/auth/microsoft/start';
  }

  return (
    <div className="login-screen">
      <div className="login-hero">
        <AppIcon size={150} className="login-app-icon" />
        <div className="login-headline">Vertrieb</div>
      </div>
      <div className="login-card">
        <div className="login-title">Anmelden</div>
        <button className="btn btn-olive" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }} onClick={loginWithMicrosoft} autoFocus>
          Microsoft Login
        </button>
        {err && <div className="login-err">{err}</div>}
        <div className="login-hint">Anmeldung mit Microsoft 365</div>
      </div>
      <div className="login-version">v{APP_VERSION}</div>
    </div>
  );
}

// --- HEADER ───────────────────────────────────────────────────────────────────

function Header({ user, onLogout, setTab, isAdmin, onUserSettings }) {
  const [menuOpen, setMenuOpen] = useState(false);
  function go(tabId) {
    setTab(tabId);
    setMenuOpen(false);
  }

  return (
    <div className="app-header">
      <div className="header-brand">
        <button className="header-logo-btn" onClick={() => setTab('overview')} aria-label="Zur Uebersicht">
          <AppIcon size={32} className="header-app-icon" />
        </button>
      </div>
      <div className="header-right">
        <div className="user-info">
          <button className="user-avatar user-avatar-btn" onClick={onUserSettings} aria-label="Eigene Benutzereinstellungen">
            {user.initials || user.name.slice(0,2).toUpperCase()}
          </button>
        </div>
        <div className="burger-wrap">
          <button className="burger-btn" onClick={() => setMenuOpen(v => !v)} aria-label="Menue">☰</button>
          {menuOpen && (
            <div className="burger-menu">
              {isAdmin && <button onClick={() => go('settings')}>Einstellungen</button>}
              {isAdmin && <button onClick={() => go('admin')}>Benutzerverwaltung</button>}
              <button onClick={() => { setMenuOpen(false); onLogout(); }}>Abmelden</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── NAV ──────────────────────────────────────────────────────────────────────

function Nav({ tab, setTab, isAdmin }) {
  const items = [
    { id: 'overview',  label: 'Übersicht'  },
    { id: 'calculation', label: 'Kalkulation' },
    { id: 'offers',    label: 'Angebote'   },
    { id: 'checklist', label: 'Checkliste' },
  ];
  return (
    <div className="app-nav">
      {items.map(i => (
        <button key={i.id} className={`nav-item${tab === i.id ? ' active' : ''}`} onClick={() => setTab(i.id)}>
          {i.label}
        </button>
      ))}
    </div>
  );
}

function AppFooter({ onVersionClick, stateSync }) {
  const syncLabel = stateSync?.loading
    ? 'Status wird geladen'
    : stateSync?.error
      ? `Status-Sync Fehler: ${stateSync.error}`
      : stateSync?.configured === false
        ? 'Status lokal'
        : stateSync?.lastSavedAt
          ? `Status gespeichert ${new Date(stateSync.lastSavedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
          : 'Status-Sync aktiv';

  return (
    <div className="app-footer">
      <span className={`sync-state${stateSync?.error ? ' sync-state-error' : ''}`} title={stateSync?.error || ''}>{syncLabel}</span>
      <button className="btn-version" onClick={onVersionClick}>v{APP_VERSION}</button>
    </div>
  );
}

// ─── VERSION HISTORY MODAL ────────────────────────────────────────────────────

function VersionModal({ open, onClose }) {
  return (
    <Modal open={open} onClose={onClose} title="Versionsverlauf" width={560}>
      <div className="version-list">
        {VERSION_HISTORY.map((v, i) => (
          <div key={v.version} className="version-entry">
            <div className="version-entry-header">
              <span className="version-num">v{v.version}</span>
              {i === 0 && <span className="tag tag-ok" style={{ fontSize: 10 }}>Aktuell</span>}
              <span className="version-meta">{fmtDate(v.date)} · {v.author}</span>
            </div>
            <ul className="version-changes">
              {v.changes.map((c, j) => <li key={j}>{c}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ─── OFFER DETAIL MODAL ───────────────────────────────────────────────────────

function OfferModal({ offer, open, onClose, onUseInChecklist, onReject }) {
  if (!offer) return null;
  const pdfUrl = offer.pdfName
    ? `/api/erp/outlook?pdf_message_id=${encodeURIComponent(offer.id)}${offer.pdfAttachmentId ? `&pdf_attachment_id=${encodeURIComponent(offer.pdfAttachmentId)}` : ''}`
    : '';
  return (
    <Modal open={open} onClose={onClose} title={`Angebot ${offer.nr} — ${offer.kunde}`} width={700}>
      <div className="offer-detail-grid">
        <div><label>Kunde</label><strong>{offer.kunde}</strong></div>
        <div><label>Datum</label><span>{fmtDate(offer.date)}</span></div>
        <div><label>Status</label><span className={`tag ${STATUS_CLASS[offer.status]}`}>{STATUS_LABEL[offer.status]}</span></div>
        <div><label>Gesamtbetrag</label><strong className="offer-total-hl">{fmt(offer.total)}</strong></div>
      </div>
      <div className="offer-subject-line">{offer.subject}</div>
      {(offer.pdfName || offer.pdfError) && (
        <div style={{ fontSize: 11, color: offer.pdfError ? 'var(--status-prob)' : 'var(--kw-text-muted)', marginTop: 6 }}>
          {offer.pdfName && <>PDF: {offer.pdfName}</>}
          {pdfUrl && <> · <a href={pdfUrl} target="_blank" rel="noreferrer">PDF öffnen</a></>}
          {offer.pdfError && <> · PDF-Fehler: {offer.pdfError}</>}
          {offer.pdfRawTextLength !== undefined && <> · Textzeichen: {offer.pdfRawTextLength || 0}</>}
          {offer.pdfPages ? <> · Seiten: {offer.pdfPages}</> : null}
          {offer.pdfExtractionMode && <> · Modus: {offer.pdfExtractionMode}</>}
        </div>
      )}
      {offer.pdfDiagnostics && (
        <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 6, lineHeight: 1.45 }}>
          Diagnose: Positionskopf {offer.pdfDiagnostics.hasPositionHeader ? 'gefunden' : 'nicht gefunden'},
          Positionsblock {offer.pdfDiagnostics.positionBlockLineCount || 0} Zeilen,
          Kandidaten {offer.pdfDiagnostics.candidatePositionLineCount || 0},
          akzeptiert {offer.pdfDiagnostics.acceptedCandidatePositionLineCount || 0},
          Unterpositionen {offer.pdfDiagnostics.acceptedSubPositionLineCount || 0},
          Netto {offer.pdfDiagnostics.hasTotal ? 'erkannt' : 'nicht erkannt'}.
        </div>
      )}
      {offer.pdfUiMapping && (
        <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 6, lineHeight: 1.45 }}>
          UI-Zuordnung: Angebot-Nr. {offer.pdfUiMapping.fields?.offerNumber?.value || '-'} → Angebot-Nr.,
          Kunde {offer.pdfUiMapping.fields?.customer?.value || '-'} → Kunde,
          Netto {fmt(offer.pdfUiMapping.fields?.netTotal?.value || 0)} → Gesamtbetrag,
          Positionen {offer.pdfUiMapping.fields?.positions?.value || 0} → Tabelle.
        </div>
      )}

      <div className="section-label">Angebotspositionen — aus ERP-PDF extrahiert</div>
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 0 }}>
        <table className="kw-table">
          <thead>
            <tr><th style={{ width: 36 }}>Pos.</th><th>Artikel-Nr.</th><th>Beschreibung</th><th style={{ textAlign: 'right' }}>Mge.</th><th style={{ textAlign: 'right' }}>EP netto</th></tr>
          </thead>
          <tbody>
            {offer.items.length === 0 && (
              <tr>
                <td colSpan={5} style={{ color: 'var(--kw-text-muted)', padding: 14 }}>
                  Keine Positionen aus dem PDF erkannt.
                </td>
              </tr>
            )}
            {offer.items.map((item, index) => (
              <tr key={`${item.pos}-${index}`}>
                <td style={{ color: 'var(--kw-text-muted)' }}>{item.pos}</td>
                <td><code className="art-code">{item.art}</code></td>
                <td>{item.desc}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{item.qty} {item.unit}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 500 }}>{fmt(item.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="modal-actions">
        <span style={{ fontSize: 12, color: 'var(--kw-text-muted)' }}>
          {offer.items.length} Position{offer.items.length !== 1 ? 'en' : ''}
        </span>
        <button className="btn btn-outline btn-sm" onClick={onClose}>Schließen</button>
        {onReject && (
          <button className="btn btn-outline btn-sm" onClick={() => { onReject(offer); onClose(); }}>
            Ablehnen
          </button>
        )}
        <button className="btn btn-olive btn-sm" onClick={() => { onUseInChecklist(offer); onClose(); }}>
          ✓ In Checkliste verwenden
        </button>
      </div>
    </Modal>
  );
}

// ─── OFFERS TAB ───────────────────────────────────────────────────────────────

function OffersTab({ currentUser, onUseInChecklist, setTab, showToast, integrations, workStatus, onWorkStatusChange }) {
  const [loading, setLoading] = useState(false);
  const savedOffers = Array.isArray(workStatus?.offers?.offers) ? workStatus.offers.offers : [];
  const [synced, setSynced] = useState(savedOffers.length > 0);
  const [syncError, setSyncError] = useState('');
  const [remoteOffers, setRemoteOffers] = useState(savedOffers);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [sourceFilter, setSourceFilter] = useState('alle');

  const userKey = currentUser.username;
  const erpSource = currentUser.erpSource || 'selectline';
  const importedIds = workStatus?.offers?.importedIds || [];
  const rejectedIds = workStatus?.offers?.rejectedIds || [];
  const statusArchive = workStatus?.offers?.statusArchive && typeof workStatus.offers.statusArchive === 'object'
    ? workStatus.offers.statusArchive
    : {};

  useEffect(() => {
    if (loading) return;
    const storedOffers = Array.isArray(workStatus?.offers?.offers) ? workStatus.offers.offers : [];
    if (storedOffers.length > 0) {
      setRemoteOffers(storedOffers);
      setSynced(true);
    }
  }, [workStatus?.offers?.offers, loading]);

  // Demo: jedes Angebot bekommt eine Quelle zugewiesen
  const rawOffers = (DEMO_OFFERS[userKey] || []).map((o, i) => ({
    ...o,
    source: erpSource === 'both' ? (i % 2 === 0 ? 'selectline' : 'outlook') : erpSource,
  }));

  const importedIdSet = new Set(importedIds);
  const rejectedIdSet = new Set(rejectedIds);
  const applyStoredOfferStatus = (offer) => ({
    ...offer,
    status: rejectedIdSet.has(offer.id) ? 'abgelehnt'
      : importedIdSet.has(offer.id) ? 'angenommen'
      : offer.status,
  });

  const archivedOffers = Object.values(statusArchive)
    .filter(offer => offer && typeof offer === 'object')
    .map(applyStoredOfferStatus);
  const sourceOfferMap = new Map();
  archivedOffers.forEach(offer => sourceOfferMap.set(offer.id, offer));
  (synced ? remoteOffers : rawOffers).map(applyStoredOfferStatus).forEach(offer => sourceOfferMap.set(offer.id, offer));
  importedIds.forEach(id => {
    if (sourceOfferMap.has(id)) return;
    sourceOfferMap.set(id, applyStoredOfferStatus({
      id,
      nr: '-',
      kunde: 'Bereits in Checkliste uebernommen',
      date: null,
      status: 'angenommen',
      source: erpSource === 'both' ? 'outlook' : erpSource,
      subject: 'Dieses Angebot ist im letzten Sync nicht mehr enthalten.',
      total: 0,
      items: [],
    }));
  });
  rejectedIds.forEach(id => {
    if (sourceOfferMap.has(id)) return;
    sourceOfferMap.set(id, applyStoredOfferStatus({
      id,
      nr: '-',
      kunde: 'Abgelehntes Angebot',
      date: null,
      status: 'abgelehnt',
      source: erpSource === 'both' ? 'outlook' : erpSource,
      subject: 'Dieses Angebot ist im letzten Sync nicht mehr enthalten.',
      total: 0,
      items: [],
    }));
  });

  let sourceOffers = Array.from(sourceOfferMap.values());
  if (erpSource === 'none') sourceOffers = [];
  if (sourceFilter !== 'alle') sourceOffers = sourceOffers.filter(o => o.source === sourceFilter);

  const statusCounts = {
    offen: sourceOffers.filter(o => o.status === 'offen' && !importedIdSet.has(o.id) && !rejectedIdSet.has(o.id)).length,
    angenommen: sourceOffers.filter(o => o.status === 'angenommen' || importedIdSet.has(o.id)).length,
    abgelehnt: sourceOffers.filter(o => o.status === 'abgelehnt' || rejectedIdSet.has(o.id)).length,
  };

  const visibleOffersByStatus = {
    alle: sourceOffers.filter(o => !importedIdSet.has(o.id) && !rejectedIdSet.has(o.id)),
    offen: sourceOffers.filter(o => o.status === 'offen' && !importedIdSet.has(o.id) && !rejectedIdSet.has(o.id)),
    angenommen: sourceOffers.filter(o => o.status === 'angenommen' || importedIdSet.has(o.id)),
    abgelehnt: sourceOffers.filter(o => o.status === 'abgelehnt' || rejectedIdSet.has(o.id)),
  };
  if (erpSource === 'none') {
    Object.keys(visibleOffersByStatus).forEach(key => { visibleOffersByStatus[key] = []; });
  }

  const offers = visibleOffersByStatus[filter] || visibleOffersByStatus.alle;

  async function fetchJson(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
      const detail = [data.error, data.graphCode, data.hint].filter(Boolean).join(' ');
      throw new Error(detail || `ERP-Aufruf fehlgeschlagen (${response.status}).`);
    }
    return data;
  }

  async function syncERP(sourceOverride = erpSource) {
    setLoading(true);
    setSyncError('');

    try {
      const requests = [];
      const syncSource = sourceOverride || erpSource;
      if (syncSource === 'selectline' || syncSource === 'both') {
        const params = new URLSearchParams();
        if (currentUser.slnSachbearbeiter) params.set('sachbearbeiter', currentUser.slnSachbearbeiter);
        if (currentUser.salesmanNumber) params.set('SalesmanNumber', currentUser.salesmanNumber);
        params.set('SelectDocumentKindFlag', integrations.selectline?.belegArt || 'F');
        params.set('activeOnly', 'true');
        params.set('includePositions', 'true');
        requests.push(fetchJson(`/api/erp/selectline?${params.toString()}`).then(data => data.offers || []));
      }
      if (syncSource === 'outlook' || syncSource === 'both') {
        const params = new URLSearchParams();
        const outlookSubjectFilter = integrations.outlook?.subjectFilter ?? 'Angebot';
        const normalizedFilter = String(outlookSubjectFilter || '').trim();
        if (normalizedFilter && normalizedFilter !== '*') params.set('subject_filter', normalizedFilter);
        params.set('lookback_months', String(integrations.outlook?.lookbackMonths || 1));
        requests.push(fetchJson(`/api/erp/outlook?${params.toString()}`).then(data => data.offers || []));
      }

      const results = await Promise.all(requests);
      const loadedOffers = results.flat();
      setRemoteOffers(loadedOffers);
      setSynced(true);
      const sourceLabel = syncSource === 'selectline' ? 'SelectLine NEO' : syncSource === 'outlook' ? 'Outlook' : 'beiden Quellen';
      const visibleCount = loadedOffers.filter(o => !importedIds.includes(o.id) && !rejectedIds.includes(o.id)).length;
      onWorkStatusChange?.('offers', {
        offers: loadedOffers,
        importedIds,
        rejectedIds,
        statusArchive,
        count: visibleCount,
        lastSyncAt: new Date().toISOString(),
        source: sourceLabel,
        status: `${visibleCount} Angebot(e) geladen`,
      });
      showToast(`${visibleCount} Angebot(e) aus ${sourceLabel} geladen.`);
    } catch (error) {
      setSyncError(error.message || 'ERP-Sync fehlgeschlagen.');
      onWorkStatusChange?.('offers', {
        offers: synced ? remoteOffers : [],
        count: synced ? remoteOffers.length : 0,
        statusArchive,
        lastSyncAt: new Date().toISOString(),
        status: error.message || 'ERP-Sync fehlgeschlagen',
      });
      showToast(error.message || 'ERP-Sync fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  }

  function handleUse(offer) {
    const nextImportedIds = [...new Set([...importedIds, offer.id])];
    const acceptedOffer = { ...offer, status: 'angenommen' };
    const nextOffers = [acceptedOffer, ...(synced ? remoteOffers : rawOffers).filter(o => o.id !== offer.id)];
    const nextStatusArchive = { ...statusArchive, [offer.id]: acceptedOffer };
    setRemoteOffers(nextOffers);
    setSynced(true);
    onWorkStatusChange?.('offers', {
      offers: nextOffers,
      importedIds: nextImportedIds,
      statusArchive: nextStatusArchive,
      count: Math.max(0, visibleOffersByStatus.alle.length - 1),
    });
    onUseInChecklist(offer);
    setTab('checklist');
    showToast(`Angebot ${offer.nr} — ${offer.items.length} Positionen in Checkliste geladen.`);
  }

  function handleReject(offer) {
    const nextRejectedIds = [...new Set([...rejectedIds, offer.id])];
    const rejectedOffer = { ...offer, status: 'abgelehnt' };
    const nextOffers = [rejectedOffer, ...(synced ? remoteOffers : rawOffers).filter(o => o.id !== offer.id)];
    const nextStatusArchive = { ...statusArchive, [offer.id]: rejectedOffer };
    setRemoteOffers(nextOffers);
    setSynced(true);
    onWorkStatusChange?.('offers', {
      offers: nextOffers,
      rejectedIds: nextRejectedIds,
      statusArchive: nextStatusArchive,
      count: Math.max(0, visibleOffersByStatus.alle.length - 1),
    });
    setSelected(null);
    showToast(`Angebot ${offer.nr} abgelehnt und aus dem Import ausgeblendet.`);
  }

  const sourceLabel = erpSource === 'selectline' ? 'SelectLine NEO API'
                    : erpSource === 'outlook' ? 'Outlook / Exchange'
                    : erpSource === 'both' ? 'SelectLine + Outlook'
                    : 'Keine Quelle konfiguriert';

  return (
    <div className="app-content">
      <div className="page-header">
        <div>
          <div className="page-title" style={{ marginBottom: 2 }}>Angebote<span className="dot">.</span></div>
          <div className="page-sub">
            Quelle: <strong>{sourceLabel}</strong>
            {currentUser.slnSachbearbeiter && erpSource !== 'outlook' && erpSource !== 'none' && (
              <> · SLN-Filter: <code style={{ background: 'var(--kw-bg)', padding: '1px 5px', borderRadius: 3, color: 'var(--kw-copper)' }}>{currentUser.slnSachbearbeiter}</code></>
            )}
            {currentUser.salesmanNumber && erpSource !== 'outlook' && erpSource !== 'none' && (
              <> · Vertreter: <code style={{ background: 'var(--kw-bg)', padding: '1px 5px', borderRadius: 3, color: 'var(--kw-copper)' }}>{currentUser.salesmanNumber}</code></>
            )}
          </div>
        </div>
        <div className="header-actions">
          {synced && <span className="sync-badge">✓ Synchronisiert</span>}
          {(erpSource === 'selectline' || erpSource === 'both') && (
            <button className="btn btn-olive" onClick={() => syncERP(erpSource === 'both' ? 'both' : 'selectline')} disabled={loading}>
              {loading ? '⟳ Lade...' : '↻ ERP-Sync'}
            </button>
          )}
          {(erpSource === 'outlook' || erpSource === 'both') && (
            <button className="btn btn-outline" onClick={() => syncERP('outlook')} disabled={loading}>
              {loading ? '⟳ Lade...' : 'Outlook-Sync starten'}
            </button>
          )}
        </div>
      </div>

      <div className="info-box">
        <div className="info-box-title">📦 ERP-Integration</div>
        <p>
          {erpSource === 'selectline' && <>Aktive Angebote werden direkt aus <strong>SelectLine NEO</strong> via REST-API geladen. Gefiltert auf Vertreter <strong>{currentUser.salesmanNumber || currentUser.slnSachbearbeiter || '—'}</strong> inklusive Belegpositionen.</>}
          {erpSource === 'outlook' && <>Angebote werden aus dem <strong>Outlook-Postfach</strong> per Microsoft Graph API gelesen. Zeitraum: <strong>{integrations.outlook?.lookbackMonths || 1} Monat(e) zurück</strong>. Betrefffilter: <strong>{!integrations.outlook?.subjectFilter || integrations.outlook?.subjectFilter === '*' ? 'kein Filter' : integrations.outlook.subjectFilter}</strong>. Es erscheinen nur Betreffe mit Angebotsnummer im Format <strong>JJ-NNNN</strong> oder <strong>JJ-INNN</strong>, z.B. <strong>26-1374</strong> oder <strong>26-I452</strong>. Bereits übergebene oder abgelehnte Mails werden ausgeblendet.</>}
          {erpSource === 'both' && <>Angebote werden parallel aus <strong>SelectLine NEO</strong> und <strong>Outlook</strong> geladen. Quelle pro Angebot sichtbar.</>}
          {erpSource === 'none' && <>Für diesen Benutzer ist keine ERP-Quelle konfiguriert. Admin kann das in der Benutzerverwaltung ändern.</>}
        </p>
      </div>

      {syncError && (
        <div className="info-box" style={{ background: 'var(--status-prob-bg)', borderColor: 'rgba(138,32,32,0.3)' }}>
          <div className="info-box-title">ERP-Sync fehlgeschlagen</div>
          <p>{syncError}</p>
        </div>
      )}

      {erpSource === 'both' && (
        <div className="filter-row">
          <button className={`filter-btn${sourceFilter === 'alle' ? ' active' : ''}`} onClick={() => setSourceFilter('alle')}>Beide Quellen</button>
          <button className={`filter-btn${sourceFilter === 'selectline' ? ' active' : ''}`} onClick={() => setSourceFilter('selectline')}>SelectLine</button>
          <button className={`filter-btn${sourceFilter === 'outlook' ? ' active' : ''}`} onClick={() => setSourceFilter('outlook')}>Outlook</button>
        </div>
      )}

      <div className="filter-row">
        {['alle', 'offen', 'angenommen', 'abgelehnt'].map(f => (
          <button key={f} className={`filter-btn${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== 'alle' && <span className="filter-count">{statusCounts[f] || 0}</span>}
          </button>
        ))}
      </div>

      {filter !== 'angenommen' && importedIds.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginBottom: 10 }}>
          {importedIds.length} Mail-Angebot(e) bereits in eine Checkliste uebergeben und ausgeblendet.
        </div>
      )}
      {filter !== 'abgelehnt' && rejectedIds.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginBottom: 10 }}>
          {rejectedIds.length} Mail-Angebot(e) abgelehnt und ausgeblendet.
        </div>
      )}

      {offers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <p>Keine Angebote gefunden</p>
          {synced && erpSource === 'outlook' && (
            <p style={{ maxWidth: 520, marginTop: 6, color: 'var(--kw-text-muted)' }}>
              Der Outlook-Sync war erfolgreich, hat aber keine Mail zum aktuellen Betrefffilter gefunden. In den Einstellungen kann der Outlook-Betrefffilter angepasst oder geleert werden.
            </p>
          )}
          {erpSource !== 'none' && (
            <button className="btn btn-olive" style={{ marginTop: 16 }} onClick={() => syncERP()}>ERP-Sync starten</button>
          )}
        </div>
      ) : offers.map(offer => (
        <div key={offer.id} className="offer-card" onClick={() => setSelected(offer)}>
          {/*
            PDF-Link oeffnet bewusst die serverseitig geladene Graph-Anlage.
            So laesst sich vergleichen, ob der Mailanhang wirklich dem lokal getesteten PDF entspricht.
          */}
          <div className="offer-card-top">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div className="offer-nr-badge">{offer.nr}</div>
                {offer.source && (
                  <span className={`source-badge source-${offer.source}`}>
                    {offer.source === 'selectline' ? 'SLN' : 'Outlook'}
                  </span>
                )}
              </div>
              <div className="offer-kunde">{offer.kunde}</div>
            </div>
            <div className="offer-card-right">
              <span className={`tag ${STATUS_CLASS[offer.status]}`}>{STATUS_LABEL[offer.status]}</span>
              <div className="offer-amount">{fmt(offer.total)}</div>
            </div>
          </div>
          <div className="offer-subject-line">{offer.subject}</div>
          {(offer.pdfName || offer.pdfError) && (
            <div style={{ fontSize: 11, color: offer.pdfError ? 'var(--status-prob)' : 'var(--kw-text-muted)', marginTop: -4, marginBottom: 8 }}>
              {offer.pdfName && <>PDF: {offer.pdfName}</>}
              {offer.pdfName && <> · <a href={`/api/erp/outlook?pdf_message_id=${encodeURIComponent(offer.id)}${offer.pdfAttachmentId ? `&pdf_attachment_id=${encodeURIComponent(offer.pdfAttachmentId)}` : ''}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>PDF öffnen</a></>}
              {offer.pdfError && <> · PDF-Fehler: {offer.pdfError}</>}
              {offer.pdfRawTextLength !== undefined && <> · Textzeichen: {offer.pdfRawTextLength || 0}</>}
              {offer.pdfExtractionMode && <> · Modus: {offer.pdfExtractionMode}</>}
            </div>
          )}
          <div className="offer-footer">
            <span>{fmtDate(offer.date)}</span>
            <span>{offer.items.length} Pos.</span>
            <button className="btn btn-sm btn-outline" style={{ marginLeft: 'auto' }}
              onClick={e => { e.stopPropagation(); handleReject(offer); }}>
              Ablehnen
            </button>
            <button className="btn btn-sm btn-olive"
              onClick={e => { e.stopPropagation(); handleUse(offer); }}>
              → Checkliste
            </button>
          </div>
        </div>
      ))}

      <OfferModal offer={selected} open={!!selected} onClose={() => setSelected(null)} onUseInChecklist={handleUse} onReject={handleReject} />
    </div>
  );
}

// ─── QUICK CALCULATION TAB ───────────────────────────────────────────────────

function CalculationTab({ currentUser, showToast, calculationDraft, calculationStatus, onWorkStatusChange, onUseInChecklist, integrations }) {
  const cachedCatalog = loadHomepageCatalog();
  const draft = calculationDraft || {};
  const storedCalculations = Array.isArray(calculationStatus?.calculations) ? calculationStatus.calculations : [];
  const [catalog, setCatalog] = useState(cachedCatalog?.products || STANDARD_CATALOG);
  const [catalogMeta, setCatalogMeta] = useState(cachedCatalog || { source: 'kaffeewerk.de', catalogVersion: STANDARD_CATALOG_VERSION, syncedAt: null });
  const [selectedProductId, setSelectedProductId] = useState(draft.selectedProductId || '');
  const [customItem, setCustomItem] = useState(draft.customItem || {
    name: '',
    brand: '',
    category: '',
    price: '',
  });
  const [customer, setCustomer] = useState(draft.customer || '');
  const [contact, setContact] = useState(draft.contact || '');
  const [project, setProject] = useState(draft.project || '');
  const [street, setStreet] = useState(draft.street || '');
  const [houseNumber, setHouseNumber] = useState(draft.houseNumber || '');
  const [zipCode, setZipCode] = useState(draft.zipCode || '');
  const [city, setCity] = useState(draft.city || '');
  const [phone, setPhone] = useState(draft.phone || '');
  const [email, setEmail] = useState(draft.email || '');
  const [notes, setNotes] = useState(draft.notes || '');
  const [requestOptions, setRequestOptions] = useState(draft.requestOptions || {
    dataSheets: false,
    flexRent: false,
    flexRentTerm: '9',
    leasing: false,
    leasingTerm: '36',
    roasterFinancing: false,
    roasterName: '',
    roasterContactEmail: '',
  });
  const [items, setItems] = useState(draft.items || []);
  const [brandDiscountsEnabled, setBrandDiscountsEnabled] = useState(!!draft.brandDiscountsEnabled);
  const [brandDiscounts, setBrandDiscounts] = useState(draft.brandDiscounts || {});
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sentCalculationOffer, setSentCalculationOffer] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [activeCalculationId, setActiveCalculationId] = useState(draft.id || `calc-draft-${Date.now()}`);
  const [calculationRecords, setCalculationRecords] = useState(storedCalculations);
  const [showArchivedCalculations, setShowArchivedCalculations] = useState(false);
  const priceListUrl = String(integrations?.catalog?.priceListUrl || '').trim();

  const groupedCatalog = catalog.reduce((groups, product) => {
    const group = product.group || `Produkte / ${product.brand || product.category || 'Allgemein'}`;
    groups[group] = groups[group] || [];
    groups[group].push(product);
    return groups;
  }, {});

  const subtotal = items.reduce((sum, item) => sum + item.qty * item.price, 0);
  const discountAmount = items.reduce((sum, item) => {
    const gross = item.qty * item.price;
    return sum + gross * ((Number(item.discount) || 0) / 100);
  }, 0);
  const netTotal = Math.max(0, subtotal - discountAmount);
  const selectedBrands = [...new Set(items.map(item => item.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'de'));
  const catalogBrands = [...new Set(catalog.map(item => item.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'de'));
  const hasCalculationContent = Boolean(customer || contact || project || street || houseNumber || zipCode || city || phone || email || notes || items.length);
  const visibleCalculationRecords = calculationRecords
    .filter(record => showArchivedCalculations ? record.archived : !record.archived)
    .sort((a, b) => timestampMs(b.updatedAt) - timestampMs(a.updatedAt));

  useEffect(() => {
    if (!storedCalculations.length) return;
    setCalculationRecords(prev => {
      const byId = new Map(prev.map(record => [record.id, record]));
      storedCalculations.forEach(record => {
        const existing = byId.get(record.id);
        if (!existing || timestampMs(record.updatedAt) > timestampMs(existing.updatedAt)) {
          byId.set(record.id, record);
        }
      });
      return Array.from(byId.values());
    });
  }, [calculationStatus?.calculations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const updatedAt = new Date().toISOString();
    const draftPayload = {
      id: activeCalculationId,
      selectedProductId,
      customItem,
      customer,
      contact,
      project,
      street,
      houseNumber,
      zipCode,
      city,
      phone,
      email,
      notes,
      requestOptions,
      items,
      brandDiscountsEnabled,
      brandDiscounts,
    };
    const currentRecord = hasCalculationContent ? {
      id: activeCalculationId,
      customer: customer || 'Kalkulation',
      contact,
      project,
      itemCount: items.length,
      netTotal,
      updatedAt,
      status: 'in_bearbeitung',
      archived: false,
      draft: draftPayload,
    } : null;
    const nextRecords = currentRecord
      ? [
          currentRecord,
          ...calculationRecords.filter(record => record.id !== activeCalculationId),
        ]
      : calculationRecords;
    if (currentRecord) setCalculationRecords(nextRecords);
    onWorkStatusChange?.('calculation', {
      draft: draftPayload,
      calculations: nextRecords,
      itemCount: items.length,
      customer,
      netTotal,
      updatedAt,
      status: items.length ? `${items.length} Position(en), ${fmt(netTotal)}` : 'Noch keine Positionen',
    });
  }, [
    activeCalculationId, selectedProductId, customItem, customer, contact, project, street, houseNumber, zipCode, city,
    phone, email, notes, requestOptions, items, brandDiscountsEnabled, brandDiscounts, netTotal,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  function updateCalculationRecords(nextRecords, patch = {}) {
    setCalculationRecords(nextRecords);
    onWorkStatusChange?.('calculation', {
      calculations: nextRecords,
      ...patch,
      updatedAt: new Date().toISOString(),
    });
  }

  function loadCalculation(record) {
    const loaded = record?.draft || {};
    setActiveCalculationId(record.id || loaded.id || `calc-draft-${Date.now()}`);
    setSelectedProductId(loaded.selectedProductId || '');
    setCustomItem(loaded.customItem || { name: '', brand: '', category: '', price: '' });
    setCustomer(loaded.customer || '');
    setContact(loaded.contact || '');
    setProject(loaded.project || '');
    setStreet(loaded.street || '');
    setHouseNumber(loaded.houseNumber || '');
    setZipCode(loaded.zipCode || '');
    setCity(loaded.city || '');
    setPhone(loaded.phone || '');
    setEmail(loaded.email || '');
    setNotes(loaded.notes || '');
    setRequestOptions(loaded.requestOptions || {
      dataSheets: false,
      flexRent: false,
      flexRentTerm: '9',
      leasing: false,
      leasingTerm: '36',
      roasterFinancing: false,
      roasterName: '',
      roasterContactEmail: '',
    });
    setItems(Array.isArray(loaded.items) ? loaded.items : []);
    setBrandDiscountsEnabled(!!loaded.brandDiscountsEnabled);
    setBrandDiscounts(loaded.brandDiscounts || {});
    showToast(`Kalkulation "${record.customer || 'Kalkulation'}" geladen.`);
  }

  function archiveCalculation(recordId) {
    const nextRecords = calculationRecords.map(record => record.id === recordId ? {
      ...record,
      archived: true,
      archivedAt: new Date().toISOString(),
    } : record);
    updateCalculationRecords(nextRecords);
    showToast('Kalkulation archiviert.');
  }

  function restoreCalculation(recordId) {
    const nextRecords = calculationRecords.map(record => record.id === recordId ? {
      ...record,
      archived: false,
      restoredAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } : record);
    updateCalculationRecords(nextRecords);
    showToast('Kalkulation wieder aktiviert.');
  }

  function deleteCalculation(recordId) {
    if (!window.confirm('Kalkulation wirklich loeschen?')) return;
    const nextRecords = calculationRecords.filter(record => record.id !== recordId);
    updateCalculationRecords(nextRecords);
    if (recordId === activeCalculationId) {
      setActiveCalculationId(`calc-draft-${Date.now()}`);
      setSelectedProductId('');
      setCustomer('');
      setContact('');
      setProject('');
      setStreet('');
      setHouseNumber('');
      setZipCode('');
      setCity('');
      setPhone('');
      setEmail('');
      setNotes('');
      setItems([]);
      setBrandDiscounts({});
      setBrandDiscountsEnabled(false);
      setSentCalculationOffer(null);
    }
    showToast('Kalkulation geloescht.');
  }

  function addProduct(product) {
    setItems((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        return prev.map((item) => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
      }
      return [...prev, { ...product, qty: 1, price: Number(product.price) || 0, discount: 0, netPriceWithoutDiscount: false, comment: '' }];
    });
  }

  function addSelectedProduct() {
    const product = catalog.find((entry) => entry.id === selectedProductId);
    if (!product) {
      setSendError('Bitte zuerst ein Produkt auswählen.');
      return;
    }
    setSendError('');
    addProduct(product);
  }

  function updateCustomItem(key, value) {
    setCustomItem((prev) => ({ ...prev, [key]: value }));
  }

  function addCustomItem() {
    const name = customItem.name.trim();
    if (!name) {
      setSendError('Bitte fuer den individuellen Artikel mindestens eine Bezeichnung eingeben.');
      return;
    }

    const product = {
      id: `custom-${Date.now()}`,
      articleNumber: 'Individuell',
      group: customItem.brand ? `Individuell / ${customItem.brand}` : 'Individuell',
      name,
      brand: customItem.brand.trim() || 'Individuell',
      category: customItem.category.trim() || 'Individueller Artikel',
      price: Math.max(0, Number(customItem.price) || 0),
      sourceUrl: '',
      isCustom: true,
    };

    setSendError('');
    setItems((prev) => [...prev, { ...product, qty: 1, discount: 0, netPriceWithoutDiscount: false, comment: '' }]);
    setCustomItem({ name: '', brand: '', category: '', price: '' });
  }

  async function refreshCatalog() {
    setCatalogLoading(true);
    setCatalogError('');

    try {
      const response = await fetch('/api/catalog/homepage', { credentials: 'same-origin' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false || !Array.isArray(data.products)) {
        throw new Error(data.error || `Katalog konnte nicht aktualisiert werden (${response.status}).`);
      }
      setCatalog(data.products);
      setCatalogMeta(data);
      saveHomepageCatalog(data.products, {
        source: data.source,
        syncedAt: data.syncedAt,
        catalogVersion: data.catalogVersion,
      });
      showToast(`${data.products.length} Homepage-Artikel aktualisiert.`);
    } catch (error) {
      setCatalogError(error.message || 'Homepage-Katalog konnte nicht aktualisiert werden.');
      showToast(error.message || 'Homepage-Katalog konnte nicht aktualisiert werden.');
    } finally {
      setCatalogLoading(false);
    }
  }

  function openProduct(product) {
    if (product.sourceUrl) {
      window.open(product.sourceUrl, '_blank', 'noopener,noreferrer');
    }
  }

  function updateItem(id, patch) {
    setItems((prev) => prev.map((item) => item.id === id ? { ...item, ...patch } : item));
  }

  function removeItem(id) {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }

  function updateBrandDiscount(brand, value) {
    const discount = Math.max(0, Math.min(100, Number(value) || 0));
    setBrandDiscounts((prev) => ({ ...prev, [brand]: discount }));
    setItems((prev) => prev.map((item) => (
      item.brand === brand
        ? { ...item, discount }
        : item
    )));
  }

  function toggleBrandDiscounts(enabled) {
    setBrandDiscountsEnabled(enabled);
    if (enabled) {
      setItems((prev) => prev.map((item) => {
        const discount = brandDiscounts[item.brand];
        return item.brand && discount !== undefined
          ? { ...item, discount }
          : item;
      }));
    }
  }

  function updateRequestOption(key, checked) {
    setRequestOptions((prev) => ({ ...prev, [key]: checked }));
  }

  function updateLeasingTerm(term) {
    setRequestOptions((prev) => ({ ...prev, leasingTerm: term }));
  }

  function updateFlexRentTerm(term) {
    setRequestOptions((prev) => ({ ...prev, flexRentTerm: term }));
  }

  function updateRequestOptionValue(key, value) {
    setRequestOptions((prev) => ({ ...prev, [key]: value }));
  }

  function buildCalculationOffer(payloadItems) {
    return {
      id: `calc-${Date.now()}`,
      nr: `KALK-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}`,
      kunde: customer || 'Kalkulation',
      date: new Date().toISOString().slice(0, 10),
      status: 'offen',
      source: 'calculation',
      subject: `Versendete Kalkulation ${customer || ''}`.trim(),
      total: netTotal,
      items: payloadItems.map((item, index) => ({
        pos: index + 1,
        art: item.articleNumber || item.id || 'Individuell',
        desc: item.comment ? `${item.name} - ${item.comment}` : item.name,
        qty: item.qty,
        unit: 'St.',
        price: item.price,
      })),
    };
  }

  async function sendOfferRequest() {
    if (!customer || items.length === 0 || sending) {
      setSendError('Bitte Kunde und mindestens eine Position erfassen.');
      return;
    }

    setSending(true);
    setSendError('');

    const payloadItems = items.map((item) => {
      const gross = item.qty * item.price;
      const discount = Number(item.discount) || 0;
      return {
        ...item,
        total: Math.max(0, gross - gross * (discount / 100)),
      };
    });
    const calculationOffer = buildCalculationOffer(payloadItems);

    try {
      const response = await fetch('/api/mail/offer-request', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer,
          contact,
          project,
          recipient: currentUser.insideSalesRecipient || currentUser.mailRecipient || currentUser.email || '',
          address: {
            street,
            houseNumber,
            zipCode,
            city,
            phone,
            email,
          },
          notes,
          requestOptions,
          salesperson: {
            name: currentUser.name,
            email: currentUser.email,
            mailRecipient: currentUser.mailRecipient || currentUser.email || '',
            insideSalesRecipient: currentUser.insideSalesRecipient || '',
            salesmanNumber: currentUser.salesmanNumber || currentUser.slnSachbearbeiter || '',
          },
          items: payloadItems,
          totals: { subtotal, discountAmount, netTotal },
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.ok === false) {
        throw new Error(data.error || `Versand fehlgeschlagen (${response.status}).`);
      }

      setSentCalculationOffer(calculationOffer);
      const sentRecord = {
        id: activeCalculationId,
        customer: customer || 'Kalkulation',
        contact,
        project,
        itemCount: items.length,
        netTotal,
        updatedAt: new Date().toISOString(),
        sentAt: new Date().toISOString(),
        status: 'versendet',
        archived: false,
        offer: calculationOffer,
        draft: {
          id: activeCalculationId,
          selectedProductId,
          customItem,
          customer,
          contact,
          project,
          street,
          houseNumber,
          zipCode,
          city,
          phone,
          email,
          notes,
          requestOptions,
          items,
          brandDiscountsEnabled,
          brandDiscounts,
        },
      };
      updateCalculationRecords([
        sentRecord,
        ...calculationRecords.filter(record => record.id !== activeCalculationId),
      ], {
        draft: sentRecord.draft,
        itemCount: items.length,
        customer,
        netTotal,
        status: `${items.length} Position(en), ${fmt(netTotal)} · versendet`,
      });
      showToast('Angebotsanforderung wurde an den Innendienst gesendet.');
    } catch (error) {
      setSendError(error.message || 'Angebotsanforderung konnte nicht versendet werden.');
      showToast(error.message || 'Angebotsanforderung konnte nicht versendet werden.');
    } finally {
      setSending(false);
    }
  }

  function exportCalculationPdf() {
    const rows = items.map((item, index) => {
      const gross = item.qty * item.price;
      const discount = Number(item.discount) || 0;
      const total = Math.max(0, gross - gross * (discount / 100));
      return `
        <tr>
          <td>${index + 1}</td>
          <td><strong>${escapeHtml(item.name)}</strong><br><span class="meta">${escapeHtml(item.group || item.brand || '')}</span>${item.comment ? `<br>${escapeHtml(item.comment)}` : ''}</td>
          <td>${escapeHtml(item.qty)}</td>
          <td>${fmt(item.price)}</td>
          <td>${discount} %${item.netPriceWithoutDiscount ? '<br><span class="meta">Netto-Preis ohne Rabattabzug ausweisen</span>' : ''}</td>
          <td>${fmt(total)}</td>
        </tr>`;
    }).join('');
    const options = [
      requestOptions.dataSheets ? 'Datenblaetter' : '',
      requestOptions.flexRent ? `Flexmiete ${requestOptions.flexRentTerm} Monate` : '',
      requestOptions.leasing ? `Leasing ${requestOptions.leasingTerm} Monate` : '',
      requestOptions.roasterFinancing ? `Roesterfinanzierung${requestOptions.roasterName ? `: ${requestOptions.roasterName}` : ''}` : '',
    ].filter(Boolean);

    const opened = openPrintDocument(`Kalkulation ${customer || ''}`.trim(), `
      <h1>Kalkulation</h1>
      <div class="meta">Erstellt am ${new Date().toLocaleString('de-DE')} · ${escapeHtml(currentUser.name || '')}</div>
      <div class="box">
        <strong>${escapeHtml(customer || 'Kunde noch offen')}</strong><br>
        ${escapeHtml(contact || '-')}<br>
        ${escapeHtml([street, houseNumber].filter(Boolean).join(' ') || '-')}<br>
        ${escapeHtml([zipCode, city].filter(Boolean).join(' ') || '-')}<br>
        ${escapeHtml(phone || '-')} · ${escapeHtml(email || '-')}
      </div>

      <h2>Positionen</h2>
      <table>
        <thead><tr><th>Pos.</th><th>Artikel</th><th>Menge</th><th>Einzelpreis</th><th>Rabatt</th><th>Summe</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="6">Keine Positionen erfasst</td></tr>'}</tbody>
      </table>
      <div class="total">Summe netto: ${fmt(netTotal)}</div>
      <h2>Hinweise</h2>
      <div class="box">${escapeHtml(notes || '-').replace(/\n/g, '<br>')}</div>
      <h2>Anforderungen</h2>
      <div class="box">${options.length ? options.map(escapeHtml).join('<br>') : '-'}</div>
    `);
    if (!opened) showToast('PDF-Fenster konnte nicht geoeffnet werden. Bitte Pop-up-Blocker pruefen.');
  }

  return (
    <div className="app-content">
      <div className="page-header">
        <div>
          <div className="page-title" style={{ marginBottom: 2 }}>Kalkulation<span className="dot">.</span></div>
          <div className="page-sub">Homepage-basierter Produktkatalog, Gesprächskalkulation und Angebotsanforderung</div>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={exportCalculationPdf}>PDF-Export</button>
          <button className="btn btn-olive" onClick={sendOfferRequest} disabled={sending}>
            {sending ? 'Sende...' : 'An Innendienst senden'}
          </button>
        </div>
      </div>

      <div className="section-label">Gespeicherte Kalkulationen</div>
      <div className="filter-row">
        <button className={`filter-btn${!showArchivedCalculations ? ' active' : ''}`} onClick={() => setShowArchivedCalculations(false)}>
          Aktiv <span className="filter-count">{calculationRecords.filter(record => !record.archived).length}</span>
        </button>
        <button className={`filter-btn${showArchivedCalculations ? ' active' : ''}`} onClick={() => setShowArchivedCalculations(true)}>
          Archiv <span className="filter-count">{calculationRecords.filter(record => record.archived).length}</span>
        </button>
      </div>
      {visibleCalculationRecords.length === 0 ? (
        <div className="check-item s-na">
          <div className="item-label">{showArchivedCalculations ? 'Keine archivierten Kalkulationen' : 'Noch keine gespeicherten Kalkulationen'}</div>
          <div className="item-hint">Sobald Kundendaten oder Positionen erfasst werden, erscheint die Kalkulation hier.</div>
        </div>
      ) : visibleCalculationRecords.map(record => (
        <div key={record.id} className={`offer-card${record.id === activeCalculationId ? ' active' : ''}`} onClick={() => loadCalculation(record)}>
          <div className="offer-card-top">
            <div>
              <div className="offer-kunde">{record.customer || 'Kalkulation'}</div>
              <div className="offer-subject-line" style={{ marginTop: 2 }}>
                {record.project || record.contact || 'Keine Projektangabe'}
              </div>
            </div>
            <div className="offer-card-right">
              <span className={`tag ${record.status === 'versendet' ? 'tag-ok' : record.archived ? 'tag-na' : 'tag-hint'}`}>
                {record.archived ? 'Archiv' : record.status === 'versendet' ? 'Versendet' : 'In Bearbeitung'}
              </span>
              <div className="offer-amount">{fmt(record.netTotal)}</div>
            </div>
          </div>
          <div className="offer-footer">
            <span>{record.itemCount || 0} Pos.</span>
            <span>{record.updatedAt ? new Date(record.updatedAt).toLocaleString('de-DE') : '-'}</span>
            <button className="btn btn-sm btn-outline" style={{ marginLeft: 'auto' }}
              onClick={e => { e.stopPropagation(); loadCalculation(record); }}>
              Laden
            </button>
            {record.archived ? (
              <button className="btn btn-sm btn-outline"
                onClick={e => { e.stopPropagation(); restoreCalculation(record.id); }}>
                Aktivieren
              </button>
            ) : (
              <button className="btn btn-sm btn-outline"
                onClick={e => { e.stopPropagation(); archiveCalculation(record.id); }}>
                Archivieren
              </button>
            )}
            <button className="btn btn-sm btn-danger" title="Kalkulation loeschen"
              onClick={e => { e.stopPropagation(); deleteCalculation(record.id); }}>
              ×
            </button>
          </div>
        </div>
      ))}

      <div className="section-label">Kundendaten</div>
      <div className="card">
        <div className="calc-form-grid">
          <div className="form-group"><label>Kunde</label>
            <input value={customer} onChange={e => setCustomer(e.target.value)} placeholder="z. B. Baeckerei Horsthemke" /></div>
          <div className="form-group"><label>Ansprechpartner</label>
            <input value={contact} onChange={e => setContact(e.target.value)} placeholder="Name" /></div>
          <div className="form-group"><label>Projekt / Standort</label>
            <input value={project} onChange={e => setProject(e.target.value)} placeholder="z. B. Neuer Standort, Theke, Hotelbar" /></div>
          <div className="form-group"><label>Straße</label>
            <input value={street} onChange={e => setStreet(e.target.value)} placeholder="z. B. Hauptstraße" /></div>
          <div className="form-group"><label>Hausnummer</label>
            <input value={houseNumber} onChange={e => setHouseNumber(e.target.value)} placeholder="z. B. 12a" /></div>
          <div className="form-group"><label>PLZ</label>
            <input value={zipCode} onChange={e => setZipCode(e.target.value)} placeholder="z. B. 50169" /></div>
          <div className="form-group"><label>Ort</label>
            <input value={city} onChange={e => setCity(e.target.value)} placeholder="z. B. Kerpen" /></div>
          <div className="form-group"><label>Telefonnummer</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="z. B. 02273 999930" /></div>
          <div className="form-group"><label>Mailadresse</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="kunde@example.de" /></div>
        </div>
      </div>

      <div className="section-label">Katalog</div>
      <div className="check-item">
        <div className="item-top">
          <div>
            <div className="item-label">Homepage-Produktauswahl</div>
            <div className="item-hint">Produkte und Tools aus der Kaffeewerk-Webseitenstruktur auswählen</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="btn btn-outline btn-sm" type="button"
              onClick={() => priceListUrl && window.open(priceListUrl, '_blank', 'noopener,noreferrer')}
              disabled={!priceListUrl}
              title={priceListUrl ? 'Preisliste öffnen' : 'Preislisten-Link unter Einstellungen konfigurieren'}>
              € Preislisten
            </button>
            <button className="btn btn-outline btn-sm" onClick={refreshCatalog} disabled={catalogLoading}>
              {catalogLoading ? 'Aktualisiere...' : 'Aktualisieren'}
            </button>
          </div>
        </div>

        <div className="calc-select-row">
          <select value={selectedProductId} onChange={e => setSelectedProductId(e.target.value)}>
            <option value="">Produkt aus Homepage-Menü wählen...</option>
            {Object.entries(groupedCatalog).map(([group, products]) => (
              <optgroup key={group} label={group}>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.group || group} / {product.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <button className="btn btn-olive btn-sm" onClick={addSelectedProduct}>Hinzufuegen</button>
        </div>
        <div className="catalog-sync-row">
          <span>
            Quelle: {catalogMeta.source || 'kaffeewerk.de'}
            {catalogMeta.syncedAt ? ` · aktualisiert ${new Date(catalogMeta.syncedAt).toLocaleString('de-DE')}` : ' · Startkatalog'}
          </span>
          <span>{catalog.length} Artikel</span>
        </div>
        {catalogError && <div className="form-error">{catalogError}</div>}
      </div>

      <div className="section-label">Individueller Artikel</div>
      <div className="check-item">
        <div className="item-top">
          <div>
            <div className="item-label">Freie Position erfassen</div>
            <div className="item-hint">Für Artikel, Dienstleistungen oder Sonderpositionen außerhalb des Homepage-Katalogs</div>
          </div>
          <button className="btn btn-olive btn-sm" onClick={addCustomItem}>Hinzufuegen</button>
        </div>
        <div className="calc-custom-item-grid">
          <div className="form-group"><label>Artikel / Leistung</label>
            <input value={customItem.name} onChange={e => updateCustomItem('name', e.target.value)}
              placeholder="z. B. Sonderzubehoer, Servicepauschale" /></div>
          <div className="form-group"><label>Hersteller</label>
            <select value={customItem.brand} onChange={e => updateCustomItem('brand', e.target.value)}>
              <option value="">Hersteller wählen...</option>
              {catalogBrands.map(brand => <option key={brand} value={brand}>{brand}</option>)}
              <option value="Individuell">Individuell</option>
            </select></div>
          <div className="form-group"><label>Kategorie</label>
            <input value={customItem.category} onChange={e => updateCustomItem('category', e.target.value)}
              placeholder="z. B. Tool, Service, Zubehoer" /></div>
          <div className="form-group"><label>Preis netto</label>
            <input type="number" min="0" step="0.01" value={customItem.price}
              onChange={e => updateCustomItem('price', e.target.value)} placeholder="0,00" /></div>
        </div>
      </div>

      <div className="section-label">Hersteller-Rabatte</div>
      <div className="check-item">
        <label className="calc-check-row" style={{ marginTop: 0 }}>
          <input type="checkbox" checked={brandDiscountsEnabled}
            onChange={e => toggleBrandDiscounts(e.target.checked)} />
          <span>Einheitliche Rabatte je Hersteller verwenden</span>
        </label>
        <div className="item-hint">
          Die Liste richtet sich nach den gewaehlten Produkten. Der Netto-Preis-Hinweis aendert nur die Darstellung, Rabatte werden weiterhin berechnet.
        </div>
        {brandDiscountsEnabled && (
          selectedBrands.length === 0 ? (
            <div className="form-error">Bitte zuerst Produkte auswaehlen.</div>
          ) : (
            <div className="calc-brand-discount-grid">
              {selectedBrands.map(brand => (
                <div key={brand} className="form-group">
                  <label>{brand}</label>
                  <input type="number" min="0" max="100" value={brandDiscounts[brand] ?? ''}
                    placeholder="0"
                    onChange={e => updateBrandDiscount(brand, e.target.value)} />
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <div className="section-label">Kalkulationspositionen</div>
      {items.length === 0 ? (
        <div className="check-item s-na">
          <div className="item-label">Noch keine Positionen erfasst</div>
          <div className="item-hint">Produkte aus dem Homepage-Katalog hinzufuegen.</div>
        </div>
      ) : items.map(item => {
        const gross = item.qty * item.price;
        const total = Math.max(0, gross - gross * ((Number(item.discount) || 0) / 100));
        return (
          <div key={item.id} className="check-item s-hint">
            <div className="item-top">
              <div>
                <div className="item-label">{item.name}</div>
                <div className="item-hint">{item.group || item.brand} · {item.category}</div>
              </div>
              <div className="catalog-actions">
                {item.sourceUrl && <button className="btn btn-outline btn-sm" onClick={() => openProduct(item)}>Homepage</button>}
                <button className="btn-icon" onClick={() => removeItem(item.id)}>×</button>
              </div>
            </div>
            <div className="calc-position-grid">
              <div className="form-group"><label>Menge</label>
                <input type="number" min="1" value={item.qty}
                  onChange={e => updateItem(item.id, { qty: Math.max(1, Number(e.target.value) || 1) })} /></div>
              <div className="form-group"><label>Preis netto</label>
                <input type="number" min="0" step="0.01" value={item.price}
                  onChange={e => updateItem(item.id, { price: Math.max(0, Number(e.target.value) || 0) })} /></div>
              <div className="form-group"><label>Rabatt %</label>
                <input type="number" min="0" max="100" value={item.discount}
                  disabled={brandDiscountsEnabled && !!item.brand}
                  onChange={e => updateItem(item.id, { discount: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} /></div>
              <div className="form-group"><label>Summe netto</label>
                <div className="calc-readonly-value">{fmt(total)}</div>
              </div>
            </div>
            <label className="calc-check-row">
              <input type="checkbox" checked={!!item.netPriceWithoutDiscount}
                onChange={e => updateItem(item.id, { netPriceWithoutDiscount: e.target.checked })} />
              <span>Preis als Netto-Preis ausweisen, ohne Rabattabzug</span>
            </label>
            {item.netPriceWithoutDiscount && (
              <div className="item-hint">Hinweis: Rabatt wird kalkuliert, der Artikel soll aber als Netto-Preis ohne Rabattabzug ausgewiesen werden.</div>
            )}
            <div className="form-group calc-comment-field"><label>Kommentar zur Position</label>
              <textarea className="note-area" value={item.comment || ''}
                onChange={e => updateItem(item.id, { comment: e.target.value })}
                placeholder="Hinweis, Ausstattung, Variante, Farbe, Alternative ..." rows={2} />
            </div>
          </div>
        );
      })}

      <div className="section-label">Zusammenfassung</div>
      <div className="check-item s-ok">
        <div className="item-top">
          <div>
            <div className="item-label">Kalkulationssumme</div>
            <div className="item-hint">{items.length} Position{items.length !== 1 ? 'en' : ''} fuer die Angebotsanforderung</div>
          </div>
          <strong className="calc-grand-value">{fmt(netTotal)}</strong>
        </div>
        <div className="calc-summary-grid">
          <div><span>Zwischensumme</span><strong>{fmt(subtotal)}</strong></div>
          <div><span>Rabatt gesamt</span><strong>{fmt(discountAmount)}</strong></div>
          <div><span>Summe netto</span><strong>{fmt(netTotal)}</strong></div>
        </div>
      </div>

      <div className="section-label">Anforderung</div>
      <div className="check-item">
        <div className="item-label">Hinweise fuer den Innendienst</div>
        <textarea className="note-area" value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Budget, Liefertermin, Besonderheiten, Alternativen, Kundenerwartung..." rows={5} />
        <div className="calc-request-options">
          <label className="calc-check-row">
            <input type="checkbox" checked={requestOptions.dataSheets}
              onChange={e => updateRequestOption('dataSheets', e.target.checked)} />
            <span>Datenblaetter beilegen</span>
          </label>
          <label className="calc-check-row">
            <input type="checkbox" checked={requestOptions.flexRent}
              onChange={e => updateRequestOption('flexRent', e.target.checked)} />
            <span>Flexmiete anbieten</span>
          </label>
          {requestOptions.flexRent && (
            <div className="form-group calc-inline-select">
              <label>Flexmiete Laufzeit</label>
              <select value={requestOptions.flexRentTerm} onChange={e => updateFlexRentTerm(e.target.value)}>
                <option value="9">9 Monate</option>
                <option value="24">24 Monate</option>
              </select>
            </div>
          )}
          <label className="calc-check-row">
            <input type="checkbox" checked={requestOptions.leasing}
              onChange={e => updateRequestOption('leasing', e.target.checked)} />
            <span>Leasing anbieten</span>
          </label>
          {requestOptions.leasing && (
            <div className="form-group calc-inline-select">
              <label>Leasing Laufzeit</label>
              <select value={requestOptions.leasingTerm} onChange={e => updateLeasingTerm(e.target.value)}>
                <option value="24">24 Monate</option>
                <option value="36">36 Monate</option>
                <option value="48">48 Monate</option>
                <option value="60">60 Monate</option>
              </select>
            </div>
          )}
          <label className="calc-check-row">
            <input type="checkbox" checked={requestOptions.roasterFinancing}
              onChange={e => updateRequestOption('roasterFinancing', e.target.checked)} />
            <span>Roesterfinanzierung anfragen</span>
          </label>
          {requestOptions.roasterFinancing && (
            <div className="calc-roaster-grid">
              <div className="form-group">
                <label>Roester Name</label>
                <input value={requestOptions.roasterName}
                  onChange={e => updateRequestOptionValue('roasterName', e.target.value)}
                  placeholder="z. B. Kaffeeroesterei Muster" />
              </div>
              <div className="form-group">
                <label>Ansprechpartner Mailadresse</label>
                <input type="email" value={requestOptions.roasterContactEmail}
                  onChange={e => updateRequestOptionValue('roasterContactEmail', e.target.value)}
                  placeholder="ansprechpartner@roesterei.de" />
              </div>
            </div>
          )}
        </div>
        {sendError && <div className="form-error">{sendError}</div>}
        <div className="action-row">
          <button className="btn btn-outline" onClick={() => setItems([])} disabled={!items.length}>Leeren</button>
          {sentCalculationOffer && (
            <button className="btn btn-outline" onClick={() => onUseInChecklist?.(sentCalculationOffer)}>
              Checkliste aus Kalkulation erstellen
            </button>
          )}
          <button className="btn btn-olive" onClick={sendOfferRequest} disabled={sending}>
            {sending ? 'Sende...' : 'Angebotsanforderung senden'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── OFFER PANEL (in checklist) ───────────────────────────────────────────────

function OfferPanel({ offer, onClear }) {
  if (!offer) return null;
  return (
    <div className="offer-panel">
      <div className="offer-panel-header">
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--kw-brown)' }}>
            📋 Angebot {offer.nr} — {offer.kunde}
          </div>
          <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 2 }}>
            {offer.items.length} Positionen aus ERP-PDF · {fmtDate(offer.date)}
          </div>
        </div>
        <button className="btn btn-sm btn-outline" onClick={onClear}>✕</button>
      </div>
      <div className="offer-panel-items">
        {offer.items.map((item, index) => (
          <div key={`${item.pos}-${index}`} className="offer-panel-item">
            <code className="art-code" style={{ fontSize: 11 }}>{item.art}</code>
            <span className="offer-panel-desc">{item.desc}</span>
            <span className="offer-panel-qty">{item.qty} {item.unit}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CHECK ITEM ───────────────────────────────────────────────────────────────

// ─── PHOTO PICKER (reusable inline component) ──────────────────────────────────

function PhotoPicker({ photoData, onPhoto, label = 'Foto' }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  return (
    <>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment"
        style={{ display: 'none' }} onChange={e => handlePhotoSelected(e, onPhoto)} />
      <input ref={galleryRef} type="file" accept="image/*"
        style={{ display: 'none' }} onChange={e => handlePhotoSelected(e, onPhoto)} />

      {!photoData ? (
        <div className="photo-actions">
          <button className="photo-btn" onClick={() => cameraRef.current?.click()}>
            <span style={{ fontSize: 20 }}>📷</span>
            <span>{label} — Kamera</span>
          </button>
          <button className="photo-btn" onClick={() => galleryRef.current?.click()}>
            <span style={{ fontSize: 20 }}>🖼</span>
            <span>{label} — Bibliothek</span>
          </button>
        </div>
      ) : (
        <div className="photo-preview-row">
          {photoData.dataUrl && (
            <img src={photoData.dataUrl} alt={photoData.name}
              onClick={() => setPreviewOpen(true)} className="photo-thumb" />
          )}
          <div className="photo-meta">
            <div className="photo-meta-name">✓ {photoData.name || 'Foto vorhanden'}</div>
            {photoData.size && <div className="photo-meta-size">{formatBytes(photoData.size)}</div>}
            <div className="photo-meta-actions">
              <button className="btn btn-sm btn-outline" onClick={() => galleryRef.current?.click()}>Ersetzen</button>
              <button className="btn btn-sm btn-danger" onClick={() => onPhoto(null)}>Entfernen</button>
            </div>
          </div>
        </div>
      )}

      {previewOpen && photoData?.dataUrl && (
        <div className="photo-fullscreen" onClick={() => setPreviewOpen(false)}>
          <img src={photoData.dataUrl} alt={photoData.name} />
          <button className="photo-fullscreen-close" onClick={() => setPreviewOpen(false)}>✕</button>
        </div>
      )}
    </>
  );
}

// ─── CHECK ITEM (Router) ───────────────────────────────────────────────────────
//
// Datenmodell pro Checkpunkt:
//   data = {
//     done: boolean,             // erledigt?
//     blocked: boolean,          // verhindert Installation
//     blockReason: string,       // Begründung wenn blocked
//     note: string,              // Notiz
//     photo: {name, dataUrl, ...},  // Foto
//     extra: { ... }             // typ-spezifische Felder (Standort, Maße, Anschlüsse, ...)
//   }

function CheckItem({ item, data = {}, onChange, machineValues, linkedOffer, trainingTopics, anschlussRules }) {
  const { done = false, blocked = false, blockReason = '', note = '', photo = null, extra = {} } = data;
  const [showGuideline, setShowGuideline] = useState(false);

  function update(patch) { onChange({ ...data, ...patch }); }
  function updateExtra(patch) { update({ extra: { ...extra, ...patch } }); }

  // Visual state
  const visualClass = blocked ? 's-problem' : done ? 's-ok' : '';
  const guidelineText = GUIDELINE_TEXTS[item.label];
  const guidelineKeys = GUIDELINE_HINTS[item.label] || [];
  const guidelineValues = machineValues ? guidelineKeys.map(k => machineValues[k]).filter(Boolean) : [];
  const hasGuideline = guidelineText || guidelineValues.length > 0;

  return (
    <div className={`check-item ${visualClass}`}>
      <div className="item-top">
        <div>
          <div className="item-label">
            {done && !blocked && <span style={{ color: 'var(--status-ok)', marginRight: 5 }}>✓</span>}
            {blocked && <span style={{ color: 'var(--status-prob)', marginRight: 5 }}>⚠</span>}
            {item.label}
          </div>
          {item.hint && <div className="item-hint">{item.hint}</div>}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {hasGuideline && (
            <button className={`sbtn btn-sm guideline-toggle${showGuideline ? ' active' : ''}`}
              onClick={() => setShowGuideline(v => !v)}>📖 Leitlinie</button>
          )}
        </div>
      </div>

      {showGuideline && hasGuideline && (
        <div className="guideline-box">
          {guidelineText && <div className="guideline-text">{guidelineText}</div>}
          {guidelineValues.length > 0 && (
            <div className="guideline-values">
              {guidelineValues.map((v, i) => (
                <div key={i} className="guideline-value">
                  <span className="guideline-value-label">{v.label}</span>
                  <span className="guideline-value-num">{v.value}{v.unit ? ` ${v.unit}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Typ-spezifischer Inhalt */}
      {item.type === 'standort'     && <StandortField extra={extra} updateExtra={updateExtra} photo={photo} onPhoto={p => update({ photo: p })} onDone={() => update({ done: true })} done={done} />}
      {item.type === 'stellflaeche' && <StellflaecheField extra={extra} updateExtra={updateExtra} onDone={() => update({ done: true })} done={done} />}
      {item.type === 'anschluesse'  && <AnschluesseField extra={extra} updateExtra={updateExtra} photo={photo} onPhoto={p => update({ photo: p })} onDone={() => update({ done: true })} done={done} linkedOffer={linkedOffer} anschlussRules={anschlussRules} />}
      {item.type === 'dosierung'    && <DosierungField extra={extra} updateExtra={updateExtra} machineValues={machineValues} onDone={() => update({ done: true })} done={done} />}
      {item.type === 'schulung'     && <SchulungField extra={extra} updateExtra={updateExtra} trainingTopics={trainingTopics} onDone={() => update({ done: true })} done={done} />}
      {item.type === 'notiz_foto'   && <NotizFotoField note={note} onNote={n => update({ note: n })} photo={photo} onPhoto={p => update({ photo: p })} onDone={() => update({ done: true })} done={done} />}
      {item.type === 'text'         && (
        <textarea className="note-area" style={{ marginTop: 8 }} placeholder="Notiz..." value={note} onChange={e => update({ note: e.target.value, done: e.target.value.length > 0 })} />
      )}
      {item.type === 'photo'        && (
        <PhotoPicker photoData={photo} onPhoto={p => update({ photo: p, done: !!p })} />
      )}

      {/* Blockierungs-Sektion: kann immer aktiviert werden */}
      <div className="block-toggle-row">
        <label className="block-toggle">
          <input type="checkbox" checked={blocked}
            onChange={e => update({ blocked: e.target.checked, done: e.target.checked ? false : done })} />
          <span>⚠ Verhindert Installation</span>
        </label>
        {done && !blocked && <span className="done-badge">✓ erledigt</span>}
      </div>

      {blocked && (
        <div className="block-reason-box">
          <label>Begründung (Pflicht)</label>
          <textarea className="note-area" placeholder="Warum kann die Installation hier nicht erfolgen?"
            value={blockReason} onChange={e => update({ blockReason: e.target.value })} />
        </div>
      )}
    </div>
  );
}

// ─── STANDORT FIELD ──────────────────────────────────────────────────────────
// Klick auf Position → Modal: zusätzlicher Text + Foto → erledigt markieren

function StandortField({ extra, updateExtra, photo, onPhoto, onDone, done }) {
  const [askDetailsFor, setAskDetailsFor] = useState(null);
  const position = extra.position || '';
  const additionalText = extra.additionalText || '';

  function pickPosition(pos) {
    if (done && position === pos) return; // schon gewählt + erledigt
    updateExtra({ position: pos });
    setAskDetailsFor(pos);
  }
  function confirmDetails() {
    setAskDetailsFor(null);
    onDone();
  }

  return (
    <>
      <div className="loc-btns" style={{ marginTop: 8 }}>
        {['Fronttheke', 'Rückbuffet'].map(opt => (
          <button key={opt}
            className={`loc-btn${position === opt ? ' active' : ''}`}
            onClick={() => pickPosition(opt)}>
            {opt}
          </button>
        ))}
        <button className={`loc-btn${position && position !== 'Fronttheke' && position !== 'Rückbuffet' ? ' active' : ''}`}
          onClick={() => pickPosition('Freitext')}>
          Freitext
        </button>
      </div>

      {position && position !== 'Fronttheke' && position !== 'Rückbuffet' && (
        <input className="loc-input" placeholder="Standort genau bezeichnen..."
          value={position === 'Freitext' ? '' : position}
          onChange={e => updateExtra({ position: e.target.value })} />
      )}

      {position && (
        <>
          <div className="field-section-label">Foto des Standorts</div>
          <PhotoPicker photoData={photo} onPhoto={onPhoto} label="Standort" />
        </>
      )}

      <Modal open={!!askDetailsFor} onClose={() => setAskDetailsFor(null)}
        title={`Position: ${askDetailsFor || ''}`} width={460}>
        <div className="form-group">
          <label>Zusätzliche Beschreibung (optional)</label>
          <textarea className="note-area" placeholder="z.B. links neben Kassentisch, vor Spüle ..."
            value={additionalText} onChange={e => updateExtra({ additionalText: e.target.value })}
            autoFocus />
        </div>
        <div className="modal-actions">
          <span style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>
            Foto kann anschließend hinzugefügt werden
          </span>
          <button className="btn btn-outline btn-sm" onClick={() => setAskDetailsFor(null)}>Abbrechen</button>
          <button className="btn btn-olive btn-sm" onClick={confirmDetails}>✓ Bestätigen</button>
        </div>
      </Modal>

      {additionalText && (
        <div style={{ marginTop: 8, padding: '6px 10px', background: 'var(--kw-bg)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--kw-text-muted)' }}>
          📝 {additionalText}
        </div>
      )}
    </>
  );
}

// ─── STELLFLAECHE FIELD ───────────────────────────────────────────────────────

function StellflaecheField({ extra, updateExtra, onDone, done }) {
  const breite       = extra.breite       || '';
  const tiefe        = extra.tiefe        || '';
  const distWasserZu = extra.distWasserZu || '';
  const distWasserAb = extra.distWasserAb || '';
  const distStrom    = extra.distStrom    || '';

  const filled = breite && tiefe && distWasserZu && distWasserAb && distStrom;

  return (
    <>
      <div className="field-section-label">Arbeitsplatten-Maße</div>
      <div className="dim-grid">
        <div className="form-group"><label>Breite</label>
          <div className="input-with-unit">
            <input type="number" value={breite} onChange={e => updateExtra({ breite: e.target.value })} />
            <span>cm</span>
          </div>
        </div>
        <div className="form-group"><label>Tiefe</label>
          <div className="input-with-unit">
            <input type="number" value={tiefe} onChange={e => updateExtra({ tiefe: e.target.value })} />
            <span>cm</span>
          </div>
        </div>
      </div>

      <div className="field-section-label">Anschluss-Entfernungen</div>
      <div className="dim-grid">
        <div className="form-group"><label>Wasserzulauf</label>
          <div className="input-with-unit">
            <input type="number" value={distWasserZu} onChange={e => updateExtra({ distWasserZu: e.target.value })} />
            <span>cm</span>
          </div>
        </div>
        <div className="form-group"><label>Wasserablauf</label>
          <div className="input-with-unit">
            <input type="number" value={distWasserAb} onChange={e => updateExtra({ distWasserAb: e.target.value })} />
            <span>cm</span>
          </div>
        </div>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Stromanschluss</label>
          <div className="input-with-unit">
            <input type="number" value={distStrom} onChange={e => updateExtra({ distStrom: e.target.value })} />
            <span>cm</span>
          </div>
        </div>
      </div>

      <button className="btn btn-olive btn-sm" style={{ marginTop: 10 }} onClick={onDone} disabled={!filled || done}>
        {done ? '✓ Erledigt' : 'Als erledigt markieren'}
      </button>
    </>
  );
}

// ─── ANSCHLUESSE FIELD ────────────────────────────────────────────────────────

function AnschluesseField({ extra, updateExtra, photo, onPhoto, onDone, done, linkedOffer, anschlussRules }) {
  // Berechne Bedarf aus Angebot
  const calculatedNeed = { schuko: 0, cee16: 0, eckventil: 0, siphon: 0 };
  const matchedItems = [];

  if (linkedOffer?.items) {
    linkedOffer.items.forEach(it => {
      const rule = (anschlussRules || []).find(r => it.art?.toUpperCase().includes(r.matchArt.toUpperCase()));
      if (rule) {
        const qty = it.qty || 1;
        const socketCount = (rule.steckdose || 0) * qty;
        matchedItems.push({ art: it.art, name: rule.name, qty, rule });
        if (rule.is400v) calculatedNeed.cee16 += socketCount;
        else calculatedNeed.schuko += socketCount;
        calculatedNeed.eckventil += (rule.eckventil || 0) * qty;
        calculatedNeed.siphon    += (rule.siphon    || 0) * qty;
      }
    });
  }

  const schukoOk    = !!extra.schukoOk;
  const cee16Ok     = !!extra.cee16Ok;
  const eckventilOk = !!extra.eckventilOk;
  const siphonOk    = !!extra.siphonOk;
  const additionalText = extra.additionalText || '';

  const allOk = (calculatedNeed.schuko === 0 || schukoOk)
             && (calculatedNeed.cee16 === 0 || cee16Ok)
             && (calculatedNeed.eckventil === 0 || eckventilOk)
             && (calculatedNeed.siphon    === 0 || siphonOk);

  return (
    <>
      {!linkedOffer ? (
        <div className="info-box" style={{ marginTop: 8 }}>
          <p>⚠ Kein Angebot verknüpft. Bedarf kann nicht automatisch berechnet werden — bitte ein Angebot in der Checkliste laden.</p>
        </div>
      ) : matchedItems.length === 0 ? (
        <div className="info-box" style={{ marginTop: 8 }}>
          <p>Keine Artikel aus dem Angebot konnten den Anschluss-Regeln zugeordnet werden.</p>
        </div>
      ) : (
        <>
          <div className="field-section-label">Bedarf laut Angebot {linkedOffer.nr}</div>
          <div className="anschluss-grid">
            {calculatedNeed.schuko > 0 && (
              <label className="anschluss-row">
                <input type="checkbox" checked={schukoOk} onChange={e => updateExtra({ schukoOk: e.target.checked })} />
                <div>
                  <div className="anschluss-label">{calculatedNeed.schuko}× Schuko-Steckdose bauseits</div>
                  <div className="anschluss-source">benötigt durch: {matchedItems.filter(m => m.rule.steckdose && !m.rule.is400v).map(m => m.name).join(', ')}</div>
                </div>
              </label>
            )}
            {calculatedNeed.cee16 > 0 && (
              <label className="anschluss-row">
                <input type="checkbox" checked={cee16Ok} onChange={e => updateExtra({ cee16Ok: e.target.checked })} />
                <div>
                  <div className="anschluss-label">{calculatedNeed.cee16}× 16A-CEE-Steckdose bauseits (400V)</div>
                  <div className="anschluss-source">benötigt durch: {matchedItems.filter(m => m.rule.steckdose && m.rule.is400v).map(m => m.name).join(', ')}</div>
                </div>
              </label>
            )}
            {calculatedNeed.eckventil > 0 && (
              <label className="anschluss-row">
                <input type="checkbox" checked={eckventilOk} onChange={e => updateExtra({ eckventilOk: e.target.checked })} />
                <div>
                  <div className="anschluss-label">{calculatedNeed.eckventil}× Eckventil</div>
                  <div className="anschluss-source">benötigt durch: {matchedItems.filter(m => m.rule.eckventil).map(m => m.name).join(', ')}</div>
                </div>
              </label>
            )}
            {calculatedNeed.siphon > 0 && (
              <label className="anschluss-row">
                <input type="checkbox" checked={siphonOk} onChange={e => updateExtra({ siphonOk: e.target.checked })} />
                <div>
                  <div className="anschluss-label">{calculatedNeed.siphon}× Abwasser-Siphon</div>
                  <div className="anschluss-source">benötigt durch: {matchedItems.filter(m => m.rule.siphon).map(m => m.name).join(', ')}</div>
                </div>
              </label>
            )}
          </div>
        </>
      )}

      <div className="field-section-label">Zusätzliche Bemerkungen</div>
      <textarea className="note-area" placeholder="z.B. Eckventil oberhalb der Arbeitsplatte, alte Steckdose nicht zugänglich ..."
        value={additionalText} onChange={e => updateExtra({ additionalText: e.target.value })} />

      <div className="field-section-label">Foto der Anschlusssituation</div>
      <PhotoPicker photoData={photo} onPhoto={onPhoto} label="Anschlüsse" />

      <button className="btn btn-olive btn-sm" style={{ marginTop: 10 }} onClick={onDone}
        disabled={done || (matchedItems.length > 0 && !allOk)}>
        {done ? '✓ Erledigt' : 'Als erledigt markieren'}
      </button>
    </>
  );
}

// ─── DOSIERUNG FIELD ──────────────────────────────────────────────────────────

function DosierungField({ extra, updateExtra, machineValues, onDone, done }) {
  const mode = extra.mode || 'standards'; // 'standards' | 'individual'
  const individual = extra.individual || {};

  // Default-Werte aus Technik-Standards
  const defaults = {
    grammatur_doppelt:  machineValues?.grammatur_doppelt?.value  ?? 17,
    grammatur_einzeln:  machineValues?.grammatur_einzeln?.value  ?? 9,
    zeit_espresso:      machineValues?.zeit_espresso?.value      ?? 25,
    zeit_kaffee:        machineValues?.zeit_kaffee?.value        ?? 35,
  };

  function setMode(newMode) {
    if (newMode === 'individual' && !extra.individual) {
      // Erste Aktivierung: Vorlage aus Standards übernehmen
      updateExtra({ mode: newMode, individual: { ...defaults } });
    } else {
      updateExtra({ mode: newMode });
    }
  }

  function updateInd(key, val) {
    updateExtra({ individual: { ...individual, [key]: val } });
  }

  return (
    <>
      <div className="field-section-label">Vorgehen</div>
      <div className="loc-btns" style={{ marginTop: 4 }}>
        <button className={`loc-btn${mode === 'standards' ? ' active' : ''}`} onClick={() => setMode('standards')}>
          Nach Technik-Standards
        </button>
        <button className={`loc-btn${mode === 'individual' ? ' active' : ''}`} onClick={() => setMode('individual')}>
          Individuelle Vorgaben
        </button>
      </div>

      {mode === 'standards' ? (
        <div className="machine-banner-values" style={{ marginTop: 10 }}>
          <div className="machine-banner-value">
            <span className="machine-banner-value-label">Doppelbezug</span>
            <span className="machine-banner-value-num">{defaults.grammatur_doppelt} g</span>
          </div>
          <div className="machine-banner-value">
            <span className="machine-banner-value-label">Einzelbezug</span>
            <span className="machine-banner-value-num">{defaults.grammatur_einzeln} g</span>
          </div>
          <div className="machine-banner-value">
            <span className="machine-banner-value-label">Espresso-Zeit</span>
            <span className="machine-banner-value-num">{defaults.zeit_espresso} Sek.</span>
          </div>
          <div className="machine-banner-value">
            <span className="machine-banner-value-label">Kaffee-Zeit</span>
            <span className="machine-banner-value-num">{defaults.zeit_kaffee} Sek.</span>
          </div>
        </div>
      ) : (
        <div className="dim-grid" style={{ marginTop: 10 }}>
          <div className="form-group"><label>Doppelbezug (g)</label>
            <div className="input-with-unit">
              <input type="number" step="0.5"
                value={individual.grammatur_doppelt ?? defaults.grammatur_doppelt}
                onChange={e => updateInd('grammatur_doppelt', Number(e.target.value))} />
              <span>g</span>
            </div>
          </div>
          <div className="form-group"><label>Einzelbezug (g)</label>
            <div className="input-with-unit">
              <input type="number" step="0.5"
                value={individual.grammatur_einzeln ?? defaults.grammatur_einzeln}
                onChange={e => updateInd('grammatur_einzeln', Number(e.target.value))} />
              <span>g</span>
            </div>
          </div>
          <div className="form-group"><label>Espresso-Zeit</label>
            <div className="input-with-unit">
              <input type="number"
                value={individual.zeit_espresso ?? defaults.zeit_espresso}
                onChange={e => updateInd('zeit_espresso', Number(e.target.value))} />
              <span>Sek.</span>
            </div>
          </div>
          <div className="form-group"><label>Kaffee-Zeit</label>
            <div className="input-with-unit">
              <input type="number"
                value={individual.zeit_kaffee ?? defaults.zeit_kaffee}
                onChange={e => updateInd('zeit_kaffee', Number(e.target.value))} />
              <span>Sek.</span>
            </div>
          </div>
        </div>
      )}

      <button className="btn btn-olive btn-sm" style={{ marginTop: 10 }} onClick={onDone} disabled={done}>
        {done ? '✓ Erledigt' : 'Als erledigt markieren'}
      </button>
    </>
  );
}

// ─── SCHULUNG FIELD ───────────────────────────────────────────────────────────

function SchulungField({ extra, updateExtra, trainingTopics, onDone, done }) {
  const checks = extra.topicChecks || {};
  const note = extra.note || '';

  function toggle(id) {
    updateExtra({ topicChecks: { ...checks, [id]: !checks[id] } });
  }

  const activeTopics = (trainingTopics || []).filter(t => t.active);

  return (
    <>
      <div className="field-section-label">Schulungsthemen</div>
      <div className="training-list">
        {activeTopics.map(topic => (
          <label key={topic.id} className="training-row">
            <input type="checkbox" checked={!!checks[topic.id]} onChange={() => toggle(topic.id)} />
            <span>{topic.label}</span>
          </label>
        ))}
      </div>

      <div className="field-section-label">Zusätzliche Notiz</div>
      <textarea className="note-area" placeholder="Weitere Schulungspunkte, Auffälligkeiten ..."
        value={note} onChange={e => updateExtra({ note: e.target.value })} />

      <button className="btn btn-olive btn-sm" style={{ marginTop: 10 }} onClick={onDone} disabled={done}>
        {done ? '✓ Erledigt' : 'Als erledigt markieren'}
      </button>
    </>
  );
}

// ─── NOTIZ + FOTO FIELD ───────────────────────────────────────────────────────

function NotizFotoField({ note, onNote, photo, onPhoto, onDone, done }) {
  return (
    <>
      <textarea className="note-area" style={{ marginTop: 8, minHeight: 80 }}
        placeholder="Besonderheiten, Auffälligkeiten, Sonderwünsche ..."
        value={note} onChange={e => onNote(e.target.value)} />

      <div className="field-section-label">Optionales Foto</div>
      <PhotoPicker photoData={photo} onPhoto={onPhoto} label="Foto" />

      <button className="btn btn-olive btn-sm" style={{ marginTop: 10 }} onClick={onDone} disabled={done}>
        {done ? '✓ Erledigt' : 'Als erledigt markieren'}
      </button>
    </>
  );
}



// ─── MAIL SEND MODAL ──────────────────────────────────────────────────────────

function MailSendModal({ open, onClose, template, users, currentUser, smtp, context, checklist, showToast }) {
  const [recipients, setRecipients] = useState([]);
  const [extraEmail, setExtraEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  // Apply template + context on open
  useEffect(() => {
    if (open && template) {
      let s = template.subject || '';
      let b = template.body || '';
      if (context?.angebot_nr) {
        s = s
          .replace(/\s*\(\{datum\}\)/g, '')
          .replace(/\{datum\}/g, '')
          .replace(/\s{2,}/g, ' ')
          .trim();
        if (!s.includes('{angebot_nr}')) s = `${s} · Angebot {angebot_nr}`.trim();
      }
      Object.entries(context || {}).forEach(([key, val]) => {
        const re = new RegExp(`\\{${key}\\}`, 'g');
        s = s.replace(re, val ?? '—');
        b = b.replace(re, val ?? '—');
      });
      setSubject(s);
      setBody(b);
      const insideSalesRecipient = currentUser.insideSalesRecipient || currentUser.mailRecipient || currentUser.email;
      setRecipients([...new Set([insideSalesRecipient].filter(Boolean))]);
    }
  }, [open, template, currentUser]);

  function toggleRecipient(email) {
    setRecipients(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  }
  function addExtraEmail() {
    if (extraEmail && extraEmail.includes('@') && !recipients.includes(extraEmail)) {
      setRecipients(prev => [...prev, extraEmail]);
      setExtraEmail('');
    }
  }

  async function send() {
    if (!recipients.length) {
      showToast('Bitte mindestens einen Empfaenger auswaehlen.');
      return;
    }
    setSending(true);
    try {
      const response = await fetch('/api/mail/offer-request', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mailType: 'checklist',
          customer: context?.kunde || checklist?.kunde || 'Checkliste',
          recipients,
          recipient: recipients[0],
          subject,
          body,
          checklist,
          salesperson: {
            name: currentUser.name,
            email: currentUser.email,
            mailRecipient: currentUser.mailRecipient || currentUser.email || '',
            insideSalesRecipient: currentUser.insideSalesRecipient || '',
            salesmanNumber: currentUser.salesmanNumber || currentUser.slnSachbearbeiter || '',
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error(data.error || `Versand fehlgeschlagen (${response.status}).`);
      }
      setSending(false);
      onClose();
      showToast(`Mail an ${recipients.length} Empfaenger versendet.`);
    } catch (error) {
      setSending(false);
      showToast(error.message || 'Checkliste konnte nicht versendet werden.');
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Checkliste per Mail versenden" width={620}>
      {!smtp?.passwordSet && (
        <div className="info-box" style={{ background: 'var(--status-hint-bg)', borderColor: 'rgba(138,106,0,0.3)' }}>
          <div className="info-box-title">Mailversand über Microsoft Graph oder SMTP</div>
          <p>Der Versand nutzt die gleiche serverseitige Route wie die Kalkulation. Wenn Microsoft Graph konfiguriert ist, wird Outlook/Azure verwendet; sonst SMTP als Fallback.</p>
        </div>
      )}

      <div className="section-label" style={{ marginTop: 0 }}>Empfänger</div>
      <div style={{ background: 'var(--kw-bg)', border: '1px solid var(--kw-border)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1, marginBottom: 6 }}>
          STANDARD-EMPFÄNGER AUS BENUTZERVERWALTUNG
        </div>
        {(() => {
          const recipient = currentUser.insideSalesRecipient || currentUser.mailRecipient || currentUser.email;
          const checked = recipients.includes(recipient);
          return recipient ? (
            <label className="notify-row" style={{ padding: '4px 0', marginBottom: 0, border: 'none' }}>
              <input type="checkbox" checked={checked} onChange={() => toggleRecipient(recipient)} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <div className="user-avatar" style={{ width: 22, height: 22, fontSize: 10 }}>
                  {currentUser.initials || currentUser.username?.slice(0, 2).toUpperCase() || 'KW'}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>Innendienst / hinterlegter Empfänger</div>
                  <div style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>{recipient}</div>
                </div>
                <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--kw-olive)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1 }}>STANDARD</span>
              </div>
            </label>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--kw-text-muted)' }}>
              Kein Empfänger im Benutzer hinterlegt. Bitte Zusatzadresse eintragen oder Benutzerverwaltung prüfen.
            </div>
          );
        })()}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <input type="email" value={extraEmail} onChange={e => setExtraEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addExtraEmail()}
          placeholder="Weitere E-Mail hinzufuegen..."
          style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--kw-border)', borderRadius: 'var(--radius)', fontSize: 13, fontFamily: 'Barlow', background: '#FAFAF8' }} />
        <button className="btn btn-outline btn-sm" onClick={addExtraEmail}>+ Hinzufügen</button>
      </div>

      {recipients.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginBottom: 12 }}>
          {recipients.length} Empfänger: {recipients.join(', ')}
        </div>
      )}

      <div className="section-label">Mail-Inhalt</div>
      <div className="form-group" style={{ marginBottom: 8 }}>
        <label>Betreff</label>
        <input type="text" value={subject} onChange={e => setSubject(e.target.value)} />
      </div>
      <div className="form-group">
        <label>Text</label>
        <textarea className="note-area" style={{ minHeight: 140 }} value={body} onChange={e => setBody(e.target.value)} />
      </div>

      <div className="modal-actions">
        <span style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>
          Absender: {currentUser.email || smtp?.fromEmail}
        </span>
        <button className="btn btn-outline btn-sm" onClick={onClose}>Abbrechen</button>
        <button className="btn btn-olive btn-sm" onClick={send} disabled={sending || !recipients.length}>
          {sending ? '⟳ Versende...' : '✉ Senden'}
        </button>
      </div>
    </Modal>
  );
}

// ─── CHECKLIST TAB ────────────────────────────────────────────────────────────

function ChecklistTab({ items, checklists, setChecklists, currentUser, showToast, linkedOffer, onClearOffer, machineValues, users, integrations, mailTemplates, trainingTopics, anschlussRules, editingId, setEditingId, checklistDraft, onWorkStatusChange }) {
  const [itemData, setItemData] = useState(checklistDraft?.itemData || {});  // {[itemId]: {done, blocked, blockReason, note, photo, extra}}
  const [kunde, setKunde]       = useState(checklistDraft?.kunde || '');
  const [datum, setDatum]       = useState(checklistDraft?.datum || new Date().toISOString().slice(0, 10));
  const [person, setPerson]     = useState(checklistDraft?.person || currentUser.name);
  const [angebot, setAngebot]   = useState(checklistDraft?.angebot || '');
  const [mailModalOpen, setMailModalOpen] = useState(false);
  const [showVersionsOf, setShowVersionsOf] = useState(null);
  const [showVersionDetail, setShowVersionDetail] = useState(null);

  // Beim Laden: wenn editingId gesetzt -> bestehende Checkliste in Formular laden
  useEffect(() => {
    if (editingId) {
      const cl = checklists.find(c => c.id === editingId);
      if (cl) {
        setKunde(cl.kunde);
        setDatum(cl.datum);
        setPerson(cl.person);
        setAngebot(cl.angebot || '');
        // Rekonstruiere itemData aus den gespeicherten Items
        const rebuilt = {};
        (cl.items || []).forEach(it => {
          rebuilt[it.id] = {
            done: it.done || false,
            blocked: it.blocked || false,
            blockReason: it.blockReason || '',
            note: it.note || '',
            photo: it.photo || null,
            extra: it.extra || {},
          };
        });
        // Globale Felder
        rebuilt.__global = {
          aborted: cl.installationAborted || false,
          abortReason: cl.abortReason || '',
        };
        setItemData(rebuilt);
      }
    }
  }, [editingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wenn ein Angebot frisch verlinkt wird (nicht bei Edit) -> Kunde+Nr setzen
  useEffect(() => {
    if (linkedOffer && !editingId) {
      setItemData({});
      setKunde(linkedOffer.kunde && linkedOffer.kunde !== '-' ? cleanCustomerName(linkedOffer.kunde) : '');
      setAngebot(linkedOffer.nr);
      setDatum(new Date().toISOString().slice(0, 10));
      setPerson(currentUser.name);
    }
  }, [linkedOffer, editingId, currentUser.name]);

  const active = items.filter(i => i.active);
  const done = active.filter(i => itemData[i.id]?.done && !itemData[i.id]?.blocked).length;
  const blocked = active.filter(i => itemData[i.id]?.blocked);
  const pct = active.length > 0 ? Math.round((done / active.length) * 100) : 0;
  const isEditing = !!editingId;
  const installationBlocked = blocked.length > 0;
  const installationAborted = !!itemData.__global?.aborted;
  const abortReason = itemData.__global?.abortReason || '';
  const draftStatus = installationAborted ? 'Abgebrochen'
                    : installationBlocked ? 'Blockiert'
                    : pct === 100 ? 'Fertig'
                    : pct > 0 ? 'Laufend' : 'Offen';
  const persistedStatus = installationAborted ? 'abgebrochen'
                        : installationBlocked ? 'blockiert'
                        : pct === 100 ? 'ok'
                        : pct > 0 ? 'laufend' : 'offen';

  useEffect(() => {
    onWorkStatusChange?.('checklist', {
      draft: { kunde: cleanCustomerName(kunde), datum, person, angebot, itemData, editingId },
      pct,
      status: draftStatus,
      customer: cleanCustomerName(kunde),
      updatedAt: new Date().toISOString(),
    });
  }, [kunde, datum, person, angebot, itemData, editingId, pct, draftStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!editingId) return;
    const now = new Date().toISOString();
    const cleanKunde = cleanCustomerName(kunde);
    const latestItems = active.map(it => ({
      id: it.id,
      label: it.label,
      type: it.type,
      done: !!itemData[it.id]?.done,
      blocked: !!itemData[it.id]?.blocked,
      blockReason: itemData[it.id]?.blockReason || '',
      note: itemData[it.id]?.note || '',
      extra: itemData[it.id]?.extra || {},
      photo: itemData[it.id]?.photo || null,
    }));
    setChecklists(prev => prev.map(c => c.id === editingId ? {
      ...c,
      kunde: cleanKunde,
      datum,
      person,
      angebot,
      pct,
      status: persistedStatus,
      installationBlocked,
      installationAborted,
      abortReason: installationAborted ? abortReason : '',
      blockedCount: blocked.length,
      items: latestItems,
      updatedAt: now,
    } : c));
  }, [editingId, kunde, datum, person, angebot, itemData, pct, persistedStatus, installationBlocked, installationAborted, abortReason, blocked.length]); // eslint-disable-line react-hooks/exhaustive-deps

  function resetForm() {
    setItemData({});
    setKunde(''); setAngebot(''); setPerson(currentUser.name);
    setDatum(new Date().toISOString().slice(0, 10));
    setEditingId(null);
    onClearOffer();
  }

  function updateItem(itemId, newData) {
    setItemData(prev => ({ ...prev, [itemId]: newData }));
  }

  function save() {
    if (!kunde.trim()) { showToast('Bitte Kundennamen eingeben.'); return; }
    if (installationBlocked && blocked.some(b => !itemData[b.id]?.blockReason?.trim())) {
      showToast('Bei blockierten Punkten ist eine Begründung Pflicht.'); return;
    }
    if (installationAborted && !abortReason.trim()) {
      showToast('Bei "Installation abbrechen" ist eine Gesamtbegründung Pflicht.'); return;
    }

    // Status-Hierarchie: abgebrochen > blockiert > fertig > laufend > offen
    const status = installationAborted ? 'abgebrochen'
                 : installationBlocked  ? 'blockiert'
                 : pct === 100          ? 'ok'
                 : pct > 0              ? 'laufend' : 'offen';

    // Neue Version (Snapshot mit allen Daten)
    const newVersion = {
      versionNr: 1,                                          // wird unten überschrieben
      savedAt: new Date().toISOString(),
      savedBy: currentUser.username,
      savedByName: currentUser.name,
      kunde: kunde.trim(), datum, person, angebot,
      pct, status,
      linkedOffer: linkedOffer ? { id: linkedOffer.id, nr: linkedOffer.nr, items: linkedOffer.items, kunde: linkedOffer.kunde, source: linkedOffer.source, date: linkedOffer.date, subject: linkedOffer.subject } : null,
      installationBlocked,
      installationAborted,
      abortReason: installationAborted ? abortReason : '',
      blockedCount: blocked.length,
      items: active.map(it => ({
        id: it.id, label: it.label, type: it.type,
        ...itemData[it.id],
      })),
    };

    if (isEditing) {
      // Neue Version an bestehende Checkliste anhängen
      setChecklists(prev => prev.map(c => {
        if (c.id !== editingId) return c;
        const versions = [...(c.versions || []), { ...newVersion, versionNr: (c.versions?.length || 0) + 1 }];
        return {
          ...c,
          versions,
          // Active = letzte Version, deshalb aktualisieren wir die Wurzel-Daten
          kunde: newVersion.kunde, datum: newVersion.datum, person: newVersion.person,
          angebot: newVersion.angebot, pct, status,
          linkedOffer: newVersion.linkedOffer,
          installationBlocked, installationAborted,
          blockedCount: blocked.length,
          items: newVersion.items,
          activeVersionNr: versions.length,
          updatedAt: newVersion.savedAt,
        };
      }));
      showToast(`Version ${(checklists.find(c => c.id === editingId)?.versions?.length || 0) + 1} gespeichert.`);
    } else {
      // Neue Checkliste mit erster Version
      const firstVersion = { ...newVersion, versionNr: 1 };
      const snap = {
        id: Date.now().toString(),
        owner: currentUser.username,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...newVersion,
        versions: [firstVersion],
        activeVersionNr: 1,
      };
      setChecklists(prev => [snap, ...prev]);
      setEditingId(snap.id);
      showToast('Checkliste angelegt (Version 1).');
    }
  }

  function setAborted(val) {
    setItemData(prev => ({
      ...prev,
      __global: { ...(prev.__global || {}), aborted: val }
    }));
  }
  function setAbortReason(val) {
    setItemData(prev => ({
      ...prev,
      __global: { ...(prev.__global || {}), abortReason: val }
    }));
  }

  function openExisting(id) {
    setEditingId(id);
    onClearOffer();
    window.scrollTo(0, 0);
  }

  function deleteChecklist(id) {
    if (!window.confirm('Checkliste wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    setChecklists(prev => prev.filter(c => c.id !== id));
    if (editingId === id) resetForm();
    showToast('Checkliste gelöscht.');
  }

  const mineChecklists = checklists.filter(c => c.owner === currentUser.username || currentUser.role === 'admin');
  const showVersionsOfChecklist = checklists.find(c => c.id === showVersionsOf);
  const currentChecklistSnapshot = {
    kunde: cleanCustomerName(kunde) || 'Checkliste',
    datum,
    person,
    angebot,
    pct,
    status: draftStatus,
    installationBlocked,
    installationAborted,
    abortReason,
    blockedCount: blocked.length,
    trainingTopics,
    machineValues,
    items: active.map(it => ({
      id: it.id,
      label: it.label,
      type: it.type,
      done: !!itemData[it.id]?.done,
      blocked: !!itemData[it.id]?.blocked,
      blockReason: itemData[it.id]?.blockReason || '',
      note: itemData[it.id]?.note || '',
      extra: itemData[it.id]?.extra || {},
      photo: itemData[it.id]?.photo || null,
    })),
  };

  function exportChecklistPdf() {
    const rows = currentChecklistSnapshot.items.map((item, index) => `
      <tr>
        <td>${index + 1}</td>
        <td><strong>${escapeHtml(item.label)}</strong>${item.note ? `<br>${escapeHtml(item.note)}` : ''}${item.blockReason ? `<br><span class="meta">${escapeHtml(item.blockReason)}</span>` : ''}</td>
        <td>${item.blocked ? 'Blockiert' : item.done ? 'OK' : 'Offen'}</td>
      </tr>
    `).join('');
    const opened = openPrintDocument(`Checkliste ${kunde || ''}`.trim(), `
      <h1>Checkliste Neuinstallation</h1>
      <div class="meta">Exportiert am ${new Date().toLocaleString('de-DE')} · ${escapeHtml(currentUser.name || '')}</div>
      <div class="box">
        <strong>${escapeHtml(currentChecklistSnapshot.kunde)}</strong><br>
        Datum: ${escapeHtml(fmtDate(datum))}<br>
        Sachbearbeiter: ${escapeHtml(person || '-')}<br>
        Angebot: ${escapeHtml(angebot || '-')}<br>
        Fortschritt: ${pct} %
      </div>
      ${installationAborted ? `<div class="box"><strong>Installation abgebrochen:</strong><br>${escapeHtml(abortReason || '-')}</div>` : ''}
      <h2>Checkpunkte</h2>
      <table>
        <thead><tr><th>Pos.</th><th>Punkt</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    `);
    if (!opened) showToast('PDF-Fenster konnte nicht geoeffnet werden. Bitte Pop-up-Blocker pruefen.');
  }

  return (
    <div className="app-content">
      <div className="page-header">
        <div>
          <div className="page-title" style={{ marginBottom: 2 }}>
            Neuinstallation<span className="dot">.</span>
          </div>
          {isEditing && (
            <div style={{ fontSize: 12, color: 'var(--kw-copper)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1 }}>
              ✎ BEARBEITE BESTEHENDE CHECKLISTE
            </div>
          )}
        </div>
        <div className="header-actions">
          <button className="btn btn-olive" onClick={resetForm}>+ Neu</button>
          <button className="btn btn-outline" onClick={save}>{isEditing ? '+ Neue Version' : 'Speichern'}</button>
        </div>
      </div>

      <OfferPanel offer={linkedOffer} onClear={onClearOffer} />

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="project-grid">
          <div className="form-group"><label>Kunde</label>
            <input type="text" placeholder="z.B. Bäckerei Horsthemke" value={kunde} onChange={e => setKunde(e.target.value)} /></div>
          <div className="form-group"><label>Datum</label>
            <input type="date" value={datum} onChange={e => setDatum(e.target.value)} /></div>
          <div className="form-group"><label>Sachbearbeiter</label>
            <input type="text" value={person} onChange={e => setPerson(e.target.value)} /></div>
          <div className="form-group"><label>Angebot-Nr.</label>
            <input type="text" placeholder="z.B. 26-0750" value={angebot} onChange={e => setAngebot(e.target.value)} /></div>
        </div>
      </div>

      <div className="progress-wrap">
        <div className="progress-top">
          <span>{done} von {active.length} Punkten erledigt{blocked.length > 0 ? ` · ${blocked.length} blockiert` : ''}</span>
          <span>{pct} %</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: pct + '%', background: installationBlocked ? 'var(--status-prob)' : 'var(--kw-olive)' }} />
        </div>
      </div>

      {installationAborted && (
        <div className="info-box" style={{ background: 'var(--status-prob-bg)', borderColor: 'rgba(138,32,32,0.4)', borderLeft: '4px solid var(--status-prob)', marginTop: 4, marginBottom: 10 }}>
          <div className="info-box-title" style={{ color: 'var(--status-prob)' }}>⛔ Installation abgebrochen</div>
          {abortReason && <p style={{ marginTop: 4 }}>{abortReason}</p>}
        </div>
      )}

      {installationBlocked && !installationAborted && (
        <div className="info-box" style={{ background: 'var(--status-prob-bg)', borderColor: 'rgba(138,32,32,0.3)', marginTop: 4, marginBottom: 14 }}>
          <div className="info-box-title" style={{ color: 'var(--status-prob)' }}>⚠ Einzelne Punkte verhindern die Installation</div>
          <p>
            <strong>{blocked.length} Punkt{blocked.length !== 1 ? 'e' : ''} blockier{blocked.length !== 1 ? 'en' : 't'} die Installation:</strong>{' '}
            {blocked.map(b => b.label).join(', ')}
          </p>
        </div>
      )}

      {active.map(item => (
        <CheckItem key={item.id} item={item}
          data={itemData[item.id]}
          onChange={d => updateItem(item.id, d)}
          machineValues={machineValues}
          linkedOffer={linkedOffer}
          trainingTopics={trainingTopics}
          anschlussRules={anschlussRules}
        />
      ))}

      {/* Globaler Abbruch-Block */}
      <div className={`abort-section${installationAborted ? ' active' : ''}`}>
        <label className="abort-toggle">
          <input type="checkbox" checked={installationAborted}
            onChange={e => setAborted(e.target.checked)} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: installationAborted ? 'var(--status-prob)' : 'var(--kw-brown)' }}>
              ⛔ Installation komplett abbrechen
            </div>
            <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 2 }}>
              Wenn die gesamte Installation nicht durchgeführt werden kann — z.B. fehlende Bauseits-Voraussetzungen, Kundenrückzug, Terminverschiebung.
            </div>
          </div>
        </label>
        {installationAborted && (
          <div style={{ marginTop: 10 }}>
            <label style={{ display: 'block', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--status-prob)', fontWeight: 600, marginBottom: 4 }}>
              Gesamt-Begründung (Pflicht)
            </label>
            <textarea className="note-area" placeholder="Warum wird die Installation insgesamt nicht durchgeführt?"
              value={abortReason} onChange={e => setAbortReason(e.target.value)} />
          </div>
        )}
      </div>

      <div className="action-row">
        <button className="btn btn-olive" onClick={save}>
          {isEditing ? '+ Neue Version speichern' : 'Speichern'}
        </button>
        <button className="btn btn-outline" onClick={() => setMailModalOpen(true)}>Per Mail versenden</button>
        <button className="btn btn-outline" onClick={exportChecklistPdf}>PDF-Export</button>
      </div>

      {mineChecklists.length > 0 && (
        <>
          <div className="section-label" style={{ marginTop: 24 }}>Gespeicherte Checklisten</div>
          <div className="recent-list">
            {mineChecklists.map(c => {
              const versionCount = c.versions?.length || 1;
              return (
                <div key={c.id} className={`recent-row${editingId === c.id ? ' editing' : ''}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, cursor: 'pointer' }} onClick={() => openExisting(c.id)}>
                    <div className={`recent-dot dot-${
                      c.status === 'abgebrochen' || c.status === 'blockiert' ? 'problem' :
                      c.status === 'ok' ? 'ok' :
                      c.status === 'laufend' ? 'laufend' : 'offen'
                    }`} />
                    <div>
                      <div className="recent-name">
                        {c.kunde}
                        {editingId === c.id && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--kw-copper)' }}>✎ in Bearbeitung</span>}
                        <span className="version-pill">v{versionCount}</span>
                      </div>
                      <div className="recent-meta">
                        {fmtDate(c.datum)} · {c.person}
                        {c.angebot ? ` · Ang. ${c.angebot}` : ''}
                        {c.linkedOffer ? ` · 📋 ${c.linkedOffer.nr}` : ''}
                        {c.updatedAt && c.updatedAt !== c.createdAt && (
                          <> · v{versionCount} am {new Date(c.updatedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`tag tag-sm ${
                      c.status === 'ok' ? 'tag-ok' :
                      c.status === 'abgebrochen' || c.status === 'blockiert' ? 'tag-problem' :
                      c.status === 'laufend' ? 'tag-hint' : 'tag-na'}`}>
                      {c.status === 'ok' ? 'Fertig' :
                       c.status === 'abgebrochen' ? 'Abgebrochen' :
                       c.status === 'blockiert' ? 'Blockiert' :
                       c.status === 'laufend' ? 'Laufend' : 'Offen'}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--kw-text-muted)', minWidth: 32, textAlign: 'right' }}>{c.pct} %</span>
                    {versionCount > 1 && (
                      <button className="btn btn-sm btn-outline" onClick={() => setShowVersionsOf(c.id)} title="Versionen anzeigen">
                        📜 {versionCount}
                      </button>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={() => deleteChecklist(c.id)}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Versionen-Modal */}
      <Modal open={!!showVersionsOf}
        onClose={() => setShowVersionsOf(null)}
        title={`Versionsverlauf — ${showVersionsOfChecklist?.kunde || ''}`}
        width={640}>
        {showVersionsOfChecklist && (
          <div className="version-history">
            {[...(showVersionsOfChecklist.versions || [])].reverse().map((v, idx, arr) => {
              const versionNr = v.versionNr;
              const isLatest = idx === 0;
              return (
                <div key={versionNr} className={`version-card${isLatest ? ' latest' : ''}`}>
                  <div className="version-card-header">
                    <div>
                      <span className="version-card-nr">Version {versionNr}</span>
                      {isLatest && <span className="tag tag-ok" style={{ fontSize: 10, marginLeft: 8 }}>Aktuell</span>}
                    </div>
                    <span className="version-card-time">{new Date(v.savedAt).toLocaleString('de-DE')}</span>
                  </div>
                  <div className="version-card-meta">
                    {v.savedByName || v.savedBy} · {v.pct} % · {
                      v.status === 'ok' ? 'Fertig' :
                      v.status === 'abgebrochen' ? 'Abgebrochen' :
                      v.status === 'blockiert' ? 'Blockiert' :
                      v.status === 'laufend' ? 'Laufend' : 'Offen'
                    }
                    {v.installationAborted && <> · ⛔ abgebrochen</>}
                    {v.installationBlocked && !v.installationAborted && <> · ⚠ {v.blockedCount} blockiert</>}
                  </div>
                  {v.abortReason && (
                    <div style={{ marginTop: 6, padding: '6px 10px', background: 'var(--status-prob-bg)', borderRadius: 'var(--radius)', fontSize: 11, color: 'var(--status-prob)' }}>
                      ⛔ {v.abortReason}
                    </div>
                  )}
                  <button className="btn btn-sm btn-outline" style={{ marginTop: 8 }}
                    onClick={() => { setShowVersionDetail(v); }}>
                    Details ansehen
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* Version-Detail-Modal */}
      <Modal open={!!showVersionDetail}
        onClose={() => setShowVersionDetail(null)}
        title={showVersionDetail ? `Version ${showVersionDetail.versionNr} — ${showVersionDetail.kunde}` : ''}
        width={640}>
        {showVersionDetail && (
          <>
            <div className="offer-detail-grid" style={{ marginBottom: 12 }}>
              <div><label>Kunde</label><strong>{showVersionDetail.kunde}</strong></div>
              <div><label>Datum</label>{fmtDate(showVersionDetail.datum)}</div>
              <div><label>Sachbearbeiter</label>{showVersionDetail.person}</div>
              <div><label>Fortschritt</label><strong style={{ color: 'var(--kw-olive)' }}>{showVersionDetail.pct} %</strong></div>
            </div>
            <div className="section-label">Checkpunkte zum Zeitpunkt der Version</div>
            {showVersionDetail.items.map(item => (
              <div key={item.id} className={`check-item-mini ${item.blocked ? 's-problem' : item.done ? 's-ok' : ''}`}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{item.label}</div>
                  {item.blocked && item.blockReason && (
                    <div style={{ fontSize: 11, color: 'var(--status-prob)', marginTop: 2 }}>⚠ {item.blockReason}</div>
                  )}
                  {item.note && <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 2 }}>📝 {item.note}</div>}
                  {item.extra?.position && <div style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>📍 {item.extra.position}{item.extra.additionalText ? ` — ${item.extra.additionalText}` : ''}</div>}
                  {item.photo?.dataUrl && (
                    <img src={item.photo.dataUrl} alt={item.photo.name}
                      style={{ marginTop: 4, maxWidth: 80, maxHeight: 80, borderRadius: 4, border: '1px solid var(--kw-border)' }} />
                  )}
                </div>
                <span className={`tag tag-sm ${item.blocked ? 'tag-problem' : item.done ? 'tag-ok' : 'tag-na'}`}>
                  {item.blocked ? 'Blockiert' : item.done ? 'OK' : 'Offen'}
                </span>
              </div>
            ))}
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--kw-text-muted)' }}>
              Gespeichert von {showVersionDetail.savedByName || showVersionDetail.savedBy} am {new Date(showVersionDetail.savedAt).toLocaleString('de-DE')}
            </div>
          </>
        )}
      </Modal>

      <MailSendModal open={mailModalOpen} onClose={() => setMailModalOpen(false)}
        template={mailTemplates?.checklist_done}
        users={users || {}}
        currentUser={currentUser}
        smtp={integrations?.smtp}
        checklist={currentChecklistSnapshot}
        context={{
          kunde: cleanCustomerName(kunde), datum, sachbearbeiter: person, angebot_nr: angebot || '—',
          fortschritt: `${done} / ${active.length}`, absender: currentUser.name,
        }}
        showToast={showToast}
      />
    </div>
  );
}

// ─── OVERVIEW TAB ─────────────────────────────────────────────────────────────

function OverviewTab({ checklists, setTab, currentUser, workStatus }) {
  const mine = checklists.filter(c => c.owner === currentUser.username || currentUser.role === 'admin');
  const calcStatus = workStatus?.calculation || DEFAULT_WORK_STATUS.calculation;
  const activeCalculations = Array.isArray(calcStatus.calculations)
    ? calcStatus.calculations.filter(record => !record.archived)
    : [];
  const offersStatus = workStatus?.offers || DEFAULT_WORK_STATUS.offers;
  const hasStoredOfferDocs = Array.isArray(offersStatus.offers);
  const importedOfferIds = offersStatus.importedIds || [];
  const rejectedOfferIds = offersStatus.rejectedIds || [];
  const offerStatusArchive = offersStatus.statusArchive && typeof offersStatus.statusArchive === 'object'
    ? offersStatus.statusArchive
    : {};
  const hiddenOfferIds = new Set([...importedOfferIds, ...rejectedOfferIds]);
  const storedOffers = hasStoredOfferDocs ? offersStatus.offers : [];
  const archivedOffers = Object.values(offerStatusArchive).filter(offer => offer && typeof offer === 'object');
  const sourceOfferMap = new Map();
  archivedOffers.forEach(offer => sourceOfferMap.set(offer.id, offer));
  storedOffers.forEach(offer => sourceOfferMap.set(offer.id, offer));
  importedOfferIds.forEach(id => {
    if (sourceOfferMap.has(id)) return;
    sourceOfferMap.set(id, {
      id,
      nr: '-',
      kunde: 'Bereits in Checkliste uebernommen',
      status: 'angenommen',
      source: 'outlook',
      subject: 'Dieses Angebot ist im letzten Sync nicht mehr enthalten.',
      total: 0,
      items: [],
    });
  });
  rejectedOfferIds.forEach(id => {
    if (sourceOfferMap.has(id)) return;
    sourceOfferMap.set(id, {
      id,
      nr: '-',
      kunde: 'Abgelehntes Angebot',
      status: 'abgelehnt',
      source: 'outlook',
      subject: 'Dieses Angebot ist im letzten Sync nicht mehr enthalten.',
      total: 0,
      items: [],
    });
  });
  const sourceOffers = Array.from(sourceOfferMap.values()).map(offer => ({
    ...offer,
    status: rejectedOfferIds.includes(offer.id) ? 'abgelehnt'
      : importedOfferIds.includes(offer.id) ? 'angenommen'
      : offer.status,
  }));
  const myOffers = sourceOffers.filter(offer => !hiddenOfferIds.has(offer.id));
  const offersDisplayStatus = hasStoredOfferDocs
    ? (myOffers.length ? `${myOffers.length} Angebot(e) geladen` : 'Keine synchronisierten Angebote')
    : 'Nicht synchronisiert';
  const sortedChecklists = [...mine].sort((a, b) => {
    const aTime = new Date(a.updatedAt || a.datum || a.createdAt || 0).getTime();
    const bTime = new Date(b.updatedAt || b.datum || b.createdAt || 0).getTime();
    return bTime - aTime;
  });
  const latestChecklist = sortedChecklists[0] || null;
  const offersOpen = sourceOffers.filter(o => o.status === 'offen' && !hiddenOfferIds.has(o.id)).length;
  const offersAcc  = sourceOffers.filter(o => o.status === 'angenommen' || importedOfferIds.includes(o.id)).length;
  const offersDec  = sourceOffers.filter(o => o.status === 'abgelehnt' || rejectedOfferIds.includes(o.id)).length;
  const checklistDone = mine.filter(c => c.status === 'ok').length;
  const checklistOpen = mine.filter(c => c.status !== 'ok').length;

  return (
    <div className="app-content">
      <div className="page-title">Übersicht<span className="dot">.</span></div>

      <div className="section-label" style={{ marginTop: 0 }}>Angebote</div>
      <div className="status-card" onClick={() => setTab('offers')}>
        <div>
          <div className="status-title">Angebote</div>
          <div className="status-meta">
            {offersDisplayStatus}
            {hasStoredOfferDocs && offersStatus.lastSyncAt ? ` · ${new Date(offersStatus.lastSyncAt).toLocaleString('de-DE')}` : ''}
          </div>
        </div>
        <button className="btn btn-outline btn-sm">Fortfahren</button>
      </div>
      <div className="metrics-grid">
        <div className="metric-card" style={{ cursor: 'pointer' }} onClick={() => setTab('offers')}>
          <div className="metric-num">{myOffers.length}</div>
          <div className="metric-label">Gesamt</div>
        </div>
        <div className="metric-card" style={{ borderTopColor: 'var(--status-hint)', cursor: 'pointer' }} onClick={() => setTab('offers')}>
          <div className="metric-num" style={{ color: 'var(--status-hint)' }}>{offersOpen}</div>
          <div className="metric-label">Offen</div>
        </div>
        <div className="metric-card" style={{ borderTopColor: 'var(--status-ok)', cursor: 'pointer' }} onClick={() => setTab('offers')}>
          <div className="metric-num" style={{ color: 'var(--status-ok)' }}>{offersAcc}</div>
          <div className="metric-label">Angenommen</div>
        </div>
        <div className="metric-card" style={{ borderTopColor: 'var(--status-prob)', cursor: 'pointer' }} onClick={() => setTab('offers')}>
          <div className="metric-num" style={{ color: 'var(--status-prob)' }}>{offersDec}</div>
          <div className="metric-label">Abgelehnt</div>
        </div>
      </div>

      <div className="section-label">Kalkulation</div>
      <div className="status-card" onClick={() => setTab('calculation')}>
        <div>
          <div className="status-title">{calcStatus.customer || 'Aktuelle Kalkulation'}</div>
          <div className="status-meta">
            {calcStatus.status || 'Noch keine Positionen'}
            {activeCalculations.length ? ` · ${activeCalculations.length} gespeichert` : ''}
            {calcStatus.updatedAt ? ` · ${new Date(calcStatus.updatedAt).toLocaleString('de-DE')}` : ''}
          </div>
        </div>
        <button className="btn btn-outline btn-sm">Fortfahren</button>
      </div>
      {activeCalculations.slice(0, 3).map(record => (
        <div key={record.id} className="recent-row" onClick={() => setTab('calculation')} style={{ cursor: 'pointer', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className={`recent-dot ${record.status === 'versendet' ? 'dot-ok' : 'dot-laufend'}`} />
            <div>
              <div className="recent-name">{record.customer || 'Kalkulation'}</div>
              <div className="recent-meta">{record.itemCount || 0} Pos. · {fmt(record.netTotal || 0)}{record.updatedAt ? ` · ${new Date(record.updatedAt).toLocaleString('de-DE')}` : ''}</div>
            </div>
          </div>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, color: 'var(--kw-text-muted)' }}>
            {record.status === 'versendet' ? 'Versendet' : 'Offen'}
          </div>
        </div>
      ))}

      <div className="section-label">Checklisten</div>
      <div className="status-card" onClick={() => setTab('checklist')}>
        <div>
          <div className="status-title">{latestChecklist?.kunde || 'Keine gespeicherte Checkliste'}</div>
          <div className="status-meta">
            {latestChecklist
              ? `${latestChecklist.status === 'ok' ? 'Abgeschlossen' : 'In Bearbeitung'} · ${latestChecklist.pct || 0} %${latestChecklist.updatedAt ? ` · ${new Date(latestChecklist.updatedAt).toLocaleString('de-DE')}` : ''}`
              : 'Noch kein Dokument vorhanden'}
          </div>
        </div>
        <button className="btn btn-outline btn-sm">Fortfahren</button>
      </div>
      <div className="metrics-grid">
        <div className="metric-card" style={{ cursor: 'pointer' }} onClick={() => setTab('checklist')}>
          <div className="metric-num">{mine.length}</div>
          <div className="metric-label">Gesamt</div>
        </div>
        <div className="metric-card" style={{ borderTopColor: 'var(--status-ok)', cursor: 'pointer' }} onClick={() => setTab('checklist')}>
          <div className="metric-num" style={{ color: 'var(--status-ok)' }}>{checklistDone}</div>
          <div className="metric-label">Abgeschlossen</div>
        </div>
        <div className="metric-card" style={{ borderTopColor: 'var(--status-hint)', cursor: 'pointer' }} onClick={() => setTab('checklist')}>
          <div className="metric-num" style={{ color: 'var(--status-hint)' }}>{checklistOpen}</div>
          <div className="metric-label">In Bearbeitung</div>
        </div>
      </div>

      <div className="section-label">Zuletzt bearbeitet</div>
      <div className="recent-list">
        {mine.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📋</div><p>Noch keine Checklisten angelegt</p></div>
        ) : sortedChecklists.slice(0, 8).map(c => (
          <div key={c.id} className="recent-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className={`recent-dot dot-${c.status}`} />
              <div>
                <div className="recent-name">{c.kunde}</div>
                <div className="recent-meta">{fmtDate(c.datum)} · {c.person}{c.angebot ? ` · Ang. ${c.angebot}` : ''}{c.linkedOffer ? ` · 📋 ${c.linkedOffer.nr}` : ''}</div>
              </div>
            </div>
            <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, color: 'var(--kw-text-muted)' }}>{c.pct} %</div>
          </div>
        ))}
      </div>
      <div className="action-row">
        <button className="btn btn-olive" onClick={() => setTab('checklist')}>+ Neue Checkliste</button>
        <button className="btn btn-outline" onClick={() => setTab('offers')}>Angebote</button>
      </div>
    </div>
  );
}

// ─── CONFIG TAB ───────────────────────────────────────────────────────────────

// ─── SETTINGS TAB (konsolidiert: Checkpunkte + Technik + Integration + Regeln) ─

function SettingsTab({ items, setItems, machineValues, setMachineValues,
                       integrations, setIntegrations, mailTemplates, setMailTemplates,
                       trainingTopics, setTrainingTopics, anschlussRules, setAnschlussRules,
                       isAdmin, showToast, resetAppData, checklists, currentUser }) {
  const [section, setSection] = useState('checkpunkte');

  if (!isAdmin) return (
    <div className="app-content"><div className="empty-state"><div className="empty-icon">🔒</div><p>Nur für Administratoren</p></div></div>
  );

  const sections = [
    { id: 'checkpunkte', label: 'Checkpunkte',        icon: '☑' },
    { id: 'technik',     label: 'Technik-Standards',  icon: '⚙' },
    { id: 'schulung',    label: 'Schulungs-Themen',   icon: '🎓' },
    { id: 'anschluesse', label: 'Anschluss-Regeln',   icon: '🔌' },
    { id: 'integration', label: 'Integrationen',      icon: '🔗' },
    { id: 'versionierung', label: 'Versionierung',    icon: 'v' },
  ];

  return (
    <div className="app-content">
      <div className="page-title">Einstellungen<span className="dot">.</span></div>

      <div className="settings-nav">
        {sections.map(s => (
          <button key={s.id} className={`settings-nav-item${section === s.id ? ' active' : ''}`}
            onClick={() => setSection(s.id)}>
            <span style={{ marginRight: 6 }}>{s.icon}</span>{s.label}
          </button>
        ))}
      </div>

      {section === 'checkpunkte' && (
        <CheckpointSettings items={items} setItems={setItems} showToast={showToast} />
      )}
      {section === 'technik' && (
        <TechStandardsTab machineValues={machineValues} setMachineValues={setMachineValues}
          isAdmin={isAdmin} showToast={showToast} embedded />
      )}
      {section === 'schulung' && (
        <TrainingTopicsSettings topics={trainingTopics} setTopics={setTrainingTopics} showToast={showToast} />
      )}
      {section === 'anschluesse' && (
        <AnschlussRulesSettings rules={anschlussRules} setRules={setAnschlussRules} showToast={showToast} />
      )}
      {section === 'integration' && (
        <IntegrationsTab integrations={integrations} setIntegrations={setIntegrations}
          mailTemplates={mailTemplates} setMailTemplates={setMailTemplates}
          isAdmin={isAdmin} showToast={showToast} currentUser={currentUser} embedded />
      )}
      {section === 'versionierung' && (
        <VersioningSettings checklists={checklists} resetAppData={resetAppData} />
      )}
    </div>
  );
}

function VersioningSettings({ checklists, resetAppData }) {
  const versionCount = checklists.reduce((sum, c) => sum + (c.versions?.length || 1), 0);
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--kw-text-muted)', marginBottom: 14 }}>
        Die App speichert lokale Daten versioniert im Browser. Beim Speichern einer bestehenden Checkliste entsteht
        automatisch eine neue fachliche Checklisten-Version.
      </p>
      <div className="card">
        <div className="offer-detail-grid">
          <div><label>App-Version</label><strong>v{APP_VERSION}</strong></div>
          <div><label>Release</label>{fmtDate(APP_RELEASE_DATE)}</div>
          <div><label>Speicher-Schema</label>v{STORAGE_SCHEMA_VERSION}</div>
          <div><label>Lokaler Speicher</label><code>{STORAGE_KEY}</code></div>
          <div><label>Checklisten</label>{checklists.length}</div>
          <div><label>Gespeicherte Versionen</label>{versionCount}</div>
        </div>
      </div>
      <div className="action-row">
        <button className="btn btn-danger" onClick={resetAppData}>Lokale App-Daten zuruecksetzen</button>
      </div>
    </>
  );
}

// Checkpunkte konfigurieren
function CheckpointSettings({ items, setItems, showToast }) {
  const update = (idx, key, val) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--kw-text-muted)', marginBottom: 14 }}>
        Checklisten-Punkte aktivieren, umbenennen oder ergänzen. Der Typ steuert,
        welche Eingabe-Maske im Checkpunkt erscheint.
      </p>
      {items.map((item, idx) => (
        <div key={item.id} className="config-row">
          <span className="drag-handle">⠿</span>
          <input type="text" value={item.label} onChange={e => update(idx, 'label', e.target.value)} />
          <select value={item.type} onChange={e => update(idx, 'type', e.target.value)} title="Eingabe-Typ">
            <option value="standort">Standort + Foto</option>
            <option value="stellflaeche">Stellfläche (Maße)</option>
            <option value="anschluesse">Anschlüsse (aus Angebot)</option>
            <option value="dosierung">Dosierung</option>
            <option value="schulung">Schulungs-Checkliste</option>
            <option value="notiz_foto">Notiz + Foto</option>
            <option value="text">Nur Text</option>
            <option value="photo">Nur Foto</option>
          </select>
          <label className="toggle">
            <input type="checkbox" checked={item.active} onChange={e => update(idx, 'active', e.target.checked)} />
            <span className="toggle-track" />
          </label>
          <button className="btn btn-sm btn-danger" onClick={() => setItems(p => p.filter((_, i) => i !== idx))}>✕</button>
        </div>
      ))}
      <div className="action-row">
        <button className="btn btn-olive"
          onClick={() => setItems(p => [...p, { id: Date.now(), label: 'Neuer Punkt', type: 'notiz_foto', active: true, hint: '' }])}>
          + Punkt hinzufügen
        </button>
        <button className="btn btn-outline" onClick={() => showToast('Checkpunkte gespeichert.')}>Speichern</button>
      </div>
    </>
  );
}

// Schulungs-Themen
function TrainingTopicsSettings({ topics, setTopics, showToast }) {
  const update = (idx, key, val) => setTopics(prev => prev.map((t, i) => i === idx ? { ...t, [key]: val } : t));
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--kw-text-muted)', marginBottom: 14 }}>
        Themen die im Schulungs-Checkpunkt zur Auswahl stehen. Inaktive Themen werden ausgeblendet.
      </p>
      {topics.map((t, idx) => (
        <div key={t.id} className="config-row">
          <span className="drag-handle">⠿</span>
          <input type="text" value={t.label} onChange={e => update(idx, 'label', e.target.value)} />
          <label className="toggle">
            <input type="checkbox" checked={t.active} onChange={e => update(idx, 'active', e.target.checked)} />
            <span className="toggle-track" />
          </label>
          <button className="btn btn-sm btn-danger" onClick={() => setTopics(p => p.filter((_, i) => i !== idx))}>✕</button>
        </div>
      ))}
      <div className="action-row">
        <button className="btn btn-olive"
          onClick={() => setTopics(p => [...p, { id: 't' + Date.now(), label: 'Neues Thema', active: true }])}>
          + Schulungsthema hinzufügen
        </button>
        <button className="btn btn-outline" onClick={() => showToast('Schulungs-Themen gespeichert.')}>Speichern</button>
      </div>
    </>
  );
}

// Anschluss-Regeln pro Artikelschlüssel
function AnschlussRulesSettings({ rules, setRules, showToast }) {
  const update = (idx, key, val) => setRules(prev => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--kw-text-muted)', marginBottom: 14 }}>
        Regeln zur automatischen Bedarfsberechnung. Wenn im Angebot ein Artikel mit passender
        Art.-Nr. (Teil-Match) auftaucht, werden Schuko/16A-CEE, Eckventile und Siphons aufsummiert.
      </p>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="kw-table">
          <thead><tr>
            <th>Art.-Nr. enthält</th><th>Name</th>
            <th style={{ textAlign: 'right' }}>Steckdose</th>
            <th style={{ textAlign: 'center' }}>400V</th>
            <th style={{ textAlign: 'right' }}>Eckventil</th>
            <th style={{ textAlign: 'right' }}>Siphon</th>
            <th></th>
          </tr></thead>
          <tbody>
            {rules.map((r, idx) => (
              <tr key={idx}>
                <td><input type="text" value={r.matchArt} style={{ width: 90, fontFamily: 'monospace' }}
                  onChange={e => update(idx, 'matchArt', e.target.value.toUpperCase())} /></td>
                <td><input type="text" value={r.name}
                  onChange={e => update(idx, 'name', e.target.value)} /></td>
                <td><input type="number" min={0} value={r.steckdose} style={{ width: 50, textAlign: 'right' }}
                  onChange={e => update(idx, 'steckdose', Number(e.target.value))} /></td>
                <td style={{ textAlign: 'center' }}>
                  <input type="checkbox" checked={!!r.is400v} onChange={e => update(idx, 'is400v', e.target.checked)} />
                </td>
                <td><input type="number" min={0} value={r.eckventil} style={{ width: 50, textAlign: 'right' }}
                  onChange={e => update(idx, 'eckventil', Number(e.target.value))} /></td>
                <td><input type="number" min={0} value={r.siphon} style={{ width: 50, textAlign: 'right' }}
                  onChange={e => update(idx, 'siphon', Number(e.target.value))} /></td>
                <td><button className="btn btn-sm btn-danger"
                  onClick={() => setRules(p => p.filter((_, i) => i !== idx))}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="action-row">
        <button className="btn btn-olive"
          onClick={() => setRules(p => [...p, { matchArt: 'NEW', name: 'Neuer Artikel', steckdose: 0, is400v: false, eckventil: 0, siphon: 0 }])}>
          + Regel hinzufügen
        </button>
        <button className="btn btn-outline" onClick={() => showToast('Regeln gespeichert.')}>Speichern</button>
      </div>
    </>
  );
}

// ─── ADMIN TAB ────────────────────────────────────────────────────────────────

function AdminTab({ users, setUsers, showToast, initialEditUsername, initialEditRequest }) {
  const [editUser, setEditUser] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  function startEdit(username) {
    setEditUser({ username, ...users[username] });
  }

  useEffect(() => {
    if (!initialEditUsername || !users[initialEditUsername]) return;
    setShowAdd(false);
    startEdit(initialEditUsername);
  }, [initialEditUsername, initialEditRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  function saveEdit() {
    const u = editUser.username;
    const email = (editUser.email || u || '').toLowerCase();
    const isForcedAdmin = FORCED_ADMIN_EMAILS.includes(email) || FORCED_ADMIN_EMAILS.includes(String(u).toLowerCase());
    setUsers(prev => ({
      ...prev,
      [u]: {
        pass: editUser.pass || prev[u].pass,
        role: isForcedAdmin ? 'admin' : editUser.role,
        name: editUser.name,
        email: editUser.email,
        mailRecipient: editUser.mailRecipient || editUser.email || '',
        insideSalesRecipient: editUser.insideSalesRecipient || 'buero@kaffeewerk.de',
        initials: editUser.initials || u.slice(0, 2).toUpperCase(),
        slnSachbearbeiter: editUser.slnSachbearbeiter || '',
        salesmanNumber: editUser.salesmanNumber || '',
        erpSource: editUser.erpSource || 'selectline',
        notify: editUser.notify || { checklistDone: true, newOffer: false, weeklyReport: false },
      },
    }));
    setEditUser(null);
    showToast(`Benutzer "${u}" gespeichert.`);
  }
  function startAdd() {
    setEditUser({
      username: '', pass: '', role: 'user', name: '', email: '', mailRecipient: '', insideSalesRecipient: 'buero@kaffeewerk.de', initials: '',
      slnSachbearbeiter: '', salesmanNumber: '', erpSource: 'selectline',
      notify: { checklistDone: true, newOffer: false, weeklyReport: false },
      _isNew: true,
    });
    setShowAdd(true);
  }
  function saveNew() {
    const u = editUser.username || editUser.email;
    if (!u) { showToast('Microsoft-E-Mail oder Benutzerkennung ist Pflicht.'); return; }
    if (users[u]) { showToast('Benutzer existiert bereits.'); return; }
    const email = (editUser.email || u || '').toLowerCase();
    const isForcedAdmin = FORCED_ADMIN_EMAILS.includes(email) || FORCED_ADMIN_EMAILS.includes(String(u).toLowerCase());
    setUsers(prev => ({
      ...prev,
      [u]: {
        pass: '', role: isForcedAdmin ? 'admin' : editUser.role,
        name: editUser.name || u, email: editUser.email || `${u}@kaffeewerk.de`,
        mailRecipient: editUser.mailRecipient || editUser.email || `${u}@kaffeewerk.de`,
        insideSalesRecipient: editUser.insideSalesRecipient || 'buero@kaffeewerk.de',
        initials: editUser.initials || u.slice(0, 2).toUpperCase(),
        slnSachbearbeiter: editUser.slnSachbearbeiter || u.slice(0, 2).toUpperCase(),
        salesmanNumber: editUser.salesmanNumber || editUser.slnSachbearbeiter || u.slice(0, 2).toUpperCase(),
        erpSource: editUser.erpSource,
        notify: editUser.notify,
      },
    }));
    setEditUser(null); setShowAdd(false);
    showToast(`Benutzer "${u}" angelegt.`);
  }
  function deleteUser(u) {
    if (u === 'admin') { showToast('Admin-Benutzer kann nicht gelöscht werden.'); return; }
    if (FORCED_ADMIN_EMAILS.includes(String(u).toLowerCase()) || FORCED_ADMIN_EMAILS.includes(users[u]?.email?.toLowerCase())) {
      showToast('Fester Admin-Benutzer kann nicht gelöscht werden.');
      return;
    }
    setUsers(prev => { const next = { ...prev }; delete next[u]; return next; });
    showToast('Benutzer entfernt.');
  }
  function updateField(key, val) { setEditUser(p => ({ ...p, [key]: val })); }
  function updateNotify(key, val) { setEditUser(p => ({ ...p, notify: { ...p.notify, [key]: val } })); }

  const inModal = editUser && (showAdd || !editUser._isNew);

  return (
    <div className="app-content">
      <div className="page-title">Benutzerverwaltung<span className="dot">.</span></div>
      <div style={{ fontSize: 12, color: 'var(--kw-text-muted)', marginBottom: 14 }}>
        Pro Microsoft-Benutzer konfigurierbar: Rolle, ERP-Quelle, SelectLine-Sachbearbeiter-Kuerzel, Checklisten-Empfaenger und Innendienst-Empfaenger.
      </div>

      <div className="card admin-table-card">
        <table className="kw-table admin-users-table">
          <thead>
            <tr>
              <th>Benutzer</th><th>Name</th><th>E-Mail</th><th>Checklisten-Empfaenger</th><th>Innendienst</th><th>SLN</th><th>Vertreter</th><th>ERP-Quelle</th><th>Rolle</th><th>Mails</th><th></th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(users).map(([u, d]) => {
              const notifyCount = d.notify ? Object.values(d.notify).filter(Boolean).length : 0;
              return (
                <tr key={u}>
                  <td style={{ fontWeight: 600, color: 'var(--kw-brown)' }}>{u}</td>
                  <td>{d.name}</td>
                  <td style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>{d.email || '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>{d.mailRecipient || d.email || '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>{d.insideSalesRecipient || 'buero@kaffeewerk.de'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--kw-copper)' }}>{d.slnSachbearbeiter || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--kw-copper)' }}>{d.salesmanNumber || '—'}</td>
                  <td><span className="tag tag-na" style={{ fontSize: 10 }}>
                    {d.erpSource === 'selectline' ? 'SelectLine' : d.erpSource === 'outlook' ? 'Outlook' : '—'}
                  </span></td>
                  <td><span className={`role-tag role-${d.role}`}>{d.role === 'admin' ? 'Admin' : 'Mitarbeiter'}</span></td>
                  <td><span style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>{notifyCount} aktiv</span></td>
                  <td style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => startEdit(u)}>✎</button>
                    {u !== 'admin' && (
                      <button className="btn btn-sm btn-danger" onClick={() => deleteUser(u)}>✕</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="action-row">
        <button className="btn btn-olive" onClick={startAdd}>+ Benutzer hinzufügen</button>
      </div>

      {/* User edit modal */}
      <Modal open={!!editUser} onClose={() => { setEditUser(null); setShowAdd(false); }}
        title={showAdd ? 'Neuer Benutzer' : `Benutzer bearbeiten: ${editUser?.username}`} width={780}>
        {editUser && (
          <>
            <div className="section-label" style={{ marginTop: 0 }}>Stammdaten</div>
            <div className="project-grid">
              {showAdd && (
                <div className="form-group"><label>Microsoft-Benutzer / E-Mail</label>
                  <input type="text" value={editUser.username} onChange={e => updateField('username', e.target.value)} placeholder="name@kaffeewerk.de" /></div>
              )}
              <div className="form-group"><label>Vollständiger Name</label>
                <input type="text" value={editUser.name} onChange={e => updateField('name', e.target.value)} placeholder="Vor- und Nachname" /></div>
              <div className="form-group"><label>E-Mail-Adresse</label>
                <input type="email" value={editUser.email} onChange={e => updateField('email', e.target.value)} placeholder="name@kaffeewerk.de" /></div>
              <div className="form-group"><label>Checklisten-Empfaenger</label>
                <input type="email" value={editUser.mailRecipient || ''} onChange={e => updateField('mailRecipient', e.target.value)}
                  placeholder={editUser.email || 'name@kaffeewerk.de'} /></div>
              <div className="form-group"><label>Innendienst-Empfaenger fuer Angebotsanforderungen</label>
                <input type="email" value={editUser.insideSalesRecipient || ''} onChange={e => updateField('insideSalesRecipient', e.target.value)}
                  placeholder="buero@kaffeewerk.de" /></div>
              <div className="form-group"><label>Initialen (Avatar)</label>
                <input type="text" maxLength={3} value={editUser.initials} onChange={e => updateField('initials', e.target.value.toUpperCase())} placeholder="z.B. DW" /></div>
              <div className="form-group"><label>Rolle</label>
                <select value={editUser.role} onChange={e => updateField('role', e.target.value)}>
                  <option value="user">Mitarbeiter</option>
                  <option value="admin">Admin</option>
                </select></div>
            </div>

            <div className="section-label">ERP-Anbindung</div>
            <div className="project-grid">
              <div className="form-group"><label>SelectLine-Sachbearbeiter-Kuerzel</label>
                <input type="text" maxLength={5} value={editUser.slnSachbearbeiter || ''}
                  onChange={e => updateField('slnSachbearbeiter', e.target.value.toUpperCase())}
                  placeholder="z.B. DW" /></div>
              <div className="form-group"><label>Vertreternummer</label>
                <input type="text" value={editUser.salesmanNumber || ''}
                  onChange={e => updateField('salesmanNumber', e.target.value)}
                  placeholder="z.B. 1001 oder DW" /></div>
              <div className="form-group"><label>Angebots-Quelle</label>
                <select value={editUser.erpSource || 'selectline'} onChange={e => updateField('erpSource', e.target.value)}>
                  <option value="selectline">SelectLine NEO API (direkt)</option>
                  <option value="outlook">Microsoft Outlook (Mail-Import)</option>
                  <option value="both">Beide (kombinierte Anzeige)</option>
                  <option value="none">Keine</option>
                </select></div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 6 }}>
              Die Vertreternummer wird als <code>SalesmanNumber</code> an SelectLine gesendet und filtert aktive Angebote auf den Vertreter. Das Sachbearbeiter-Kuerzel bleibt als Fallback erhalten.
            </div>

            <div className="section-label">E-Mail-Benachrichtigungen</div>
            <div className="notify-grid">
              <label className="notify-row">
                <input type="checkbox" checked={editUser.notify?.checklistDone || false}
                  onChange={e => updateNotify('checklistDone', e.target.checked)} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Checkliste abgeschlossen</div>
                  <div style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>Benachrichtigung wenn eine Inbetriebnahme-Checkliste fertiggestellt wird</div>
                </div>
              </label>
              <label className="notify-row">
                <input type="checkbox" checked={editUser.notify?.newOffer || false}
                  onChange={e => updateNotify('newOffer', e.target.checked)} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Neues Angebot importiert</div>
                  <div style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>Wenn ein neues Angebot aus dem ERP geladen wird</div>
                </div>
              </label>
              <label className="notify-row">
                <input type="checkbox" checked={editUser.notify?.weeklyReport || false}
                  onChange={e => updateNotify('weeklyReport', e.target.checked)} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Wochenbericht (montags)</div>
                  <div style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>Übersicht aller Aktivitäten der vergangenen Woche</div>
                </div>
              </label>
            </div>

            <div className="modal-actions">
              <span style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>
                {showAdd ? 'Neuen Benutzer anlegen' : 'Änderungen speichern'}
              </span>
              <button className="btn btn-outline btn-sm" onClick={() => { setEditUser(null); setShowAdd(false); }}>Abbrechen</button>
              <button className="btn btn-olive btn-sm" onClick={showAdd ? saveNew : saveEdit}>
                {showAdd ? 'Anlegen' : 'Speichern'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ─── TECH STANDARDS TAB ───────────────────────────────────────────────────────

function TechStandardsTab({ machineValues, setMachineValues, isAdmin, showToast, embedded = false }) {
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);

  function startEdit() {
    setDraft({ ...machineValues });
    setEditMode(true);
  }
  function saveEdit() {
    setMachineValues(draft);
    setEditMode(false);
    setDraft(null);
    showToast('Vorgabewerte gespeichert.');
  }
  function cancelEdit() {
    setDraft(null);
    setEditMode(false);
  }
  function resetDefaults() {
    setMachineValues(DEFAULT_MACHINE_VALUES);
    setEditMode(false);
    setDraft(null);
    showToast('Vorgabewerte auf Standard zurückgesetzt.');
  }
  function updateValue(key, val) {
    setDraft(prev => ({ ...prev, [key]: { ...prev[key], value: val } }));
  }

  const values = editMode ? draft : machineValues;

  const categories = [
    { key: 'allgemein', label: 'Allgemein',         data: TECH_GUIDELINES.allgemein, icon: '⚙' },
    { key: 'kaffee',    label: 'Kaffee / Espresso', data: TECH_GUIDELINES.kaffee,    icon: '☕' },
    { key: 'milch',     label: 'Milch',             data: TECH_GUIDELINES.milch,     icon: '🥛' },
  ];

  // Group machine values by category
  const valuesByGroup = Object.entries(values).reduce((acc, [key, v]) => {
    if (!acc[v.group]) acc[v.group] = [];
    acc[v.group].push({ key, ...v });
    return acc;
  }, {});

  const Wrapper = embedded ? 'div' : 'div';
  const wrapperClass = embedded ? '' : 'app-content';

  return (
    <Wrapper className={wrapperClass}>
      {!embedded && (
        <div className="page-header">
          <div>
            <div className="page-title" style={{ marginBottom: 2 }}>Technische Leitlinien<span className="dot">.</span></div>
            <div className="page-sub">Empfehlungen zur Inbetriebnahme &amp; änderbare Vorgabewerte</div>
          </div>
        </div>
      )}

      <div className="info-box">
        <div className="info-box-title">📖 Leitlinien zur Inbetriebnahme</div>
        <p>
          Diese Empfehlungen sind das Kaffeewerk-Standardvorgehen für jede Neuinstallation. Die abgeleiteten
          Vorgabewerte werden bei jeder Checkliste als Startwert übernommen und können dort individuell
          angepasst werden — die Leitlinien selbst bleiben unverändert als Referenz erhalten.
        </p>
      </div>

      {/* Leitlinien — kategorisiert */}
      {categories.map(cat => (
        <div key={cat.key} style={{ marginBottom: 22 }}>
          <div className="tech-cat-header">
            <span className="tech-cat-icon">{cat.icon}</span>
            <span className="tech-cat-label">{cat.label}</span>
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="kw-table tech-table">
              <thead>
                <tr>
                  <th style={{ width: '32%' }}>Thema</th>
                  <th style={{ width: '28%' }}>Einstellung / Empfehlung</th>
                  <th>Kaffeewerk-Hinweis</th>
                </tr>
              </thead>
              <tbody>
                {cat.data.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: 'var(--kw-brown)' }}>{row.topic}</td>
                    <td style={{ color: 'var(--kw-olive)', fontWeight: 600 }}>{row.value}</td>
                    <td style={{ fontSize: 12, color: 'var(--kw-text-muted)', lineHeight: 1.5 }}>{row.hint}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Änderbare Vorgabewerte */}
      <div className="section-label" style={{ marginTop: 28, fontSize: 12 }}>
        Änderbare Vorgabewerte für die Maschinen
      </div>
      <div style={{ fontSize: 12, color: 'var(--kw-text-muted)', marginBottom: 14 }}>
        Aus den Leitlinien abgeleitete Startwerte. Diese werden in jeder neuen Checkliste vorausgewählt
        und können dort pro Kunde überschrieben werden.
        {!isAdmin && <span style={{ display: 'block', marginTop: 4, fontStyle: 'italic' }}>🔒 Bearbeitung nur durch Administratoren.</span>}
      </div>

      <div className="machine-values-actions">
        {!editMode && isAdmin && (
          <>
            <button className="btn btn-olive" onClick={startEdit}>✎ Werte bearbeiten</button>
            <button className="btn btn-outline" onClick={resetDefaults}>↻ Auf Standard zurücksetzen</button>
          </>
        )}
        {editMode && (
          <>
            <button className="btn btn-olive" onClick={saveEdit}>✓ Speichern</button>
            <button className="btn btn-outline" onClick={cancelEdit}>Abbrechen</button>
          </>
        )}
      </div>

      {Object.entries(valuesByGroup).map(([group, list]) => (
        <div key={group} style={{ marginBottom: 14 }}>
          <div className="machine-group-label">{group}</div>
          <div className="machine-values-grid">
            {list.map(v => (
              <div key={v.key} className={`machine-value-card${editMode ? ' editable' : ''}`}>
                <div className="machine-value-label">{v.label}</div>
                {editMode ? (
                  v.options ? (
                    <select className="machine-value-input"
                      value={v.value}
                      onChange={e => updateValue(v.key, e.target.value)}>
                      {v.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <div className="machine-value-edit">
                      <input type="number"
                        className="machine-value-input"
                        value={v.value}
                        min={v.min} max={v.max}
                        onChange={e => updateValue(v.key, Number(e.target.value))} />
                      <span className="machine-value-unit">{v.unit}</span>
                    </div>
                  )
                ) : (
                  <div className="machine-value-display">
                    <span className="machine-value-num">{v.value}</span>
                    {v.unit && <span className="machine-value-unit">{v.unit}</span>}
                  </div>
                )}
                {v.min !== undefined && (
                  <div className="machine-value-range">Bereich: {v.min} – {v.max} {v.unit}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </Wrapper>
  );
}

// ─── INTEGRATIONS TAB ─────────────────────────────────────────────────────────

function IntegrationsTab({ integrations, setIntegrations, mailTemplates, setMailTemplates, isAdmin, showToast, currentUser, embedded = false }) {
  const [section, setSection] = useState('selectline');
  const [editTemplate, setEditTemplate] = useState(null);
  const [erpLoginUser, setErpLoginUser] = useState('');
  const [erpLoginPass, setErpLoginPass] = useState('');
  const [erpLoginLoading, setErpLoginLoading] = useState(false);

  if (!isAdmin) {
    return (
      <div className="app-content">
        <div className="empty-state"><div className="empty-icon">🔒</div><p>Nur für Administratoren</p></div>
      </div>
    );
  }

  function updateSL(key, val) {
    setIntegrations(prev => ({ ...prev, selectline: { ...prev.selectline, [key]: val } }));
  }
  function updateOL(key, val) {
    setIntegrations(prev => ({ ...prev, outlook: { ...prev.outlook, [key]: val } }));
  }
  function updateSMTP(key, val) {
    setIntegrations(prev => ({ ...prev, smtp: { ...prev.smtp, [key]: val } }));
  }
  function updateCatalog(key, val) {
    setIntegrations(prev => ({ ...prev, catalog: { ...prev.catalog, [key]: val } }));
  }
  async function testSelectLine() {
    showToast('Verbindungstest gestartet...');
    try {
      const response = await fetch('/api/erp/selectline?Items=1', { credentials: 'same-origin' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) throw new Error(data.error || 'Verbindungstest fehlgeschlagen.');
      updateSL('status', 'verbunden');
      updateSL('lastSync', new Date().toISOString());
      showToast('Verbindung zu SelectLine NEO erfolgreich.');
    } catch (error) {
      updateSL('status', 'fehler');
      showToast(error.message || 'Verbindung zu SelectLine NEO fehlgeschlagen.');
    }
  }
  async function loginSelectLineERP() {
    if (!erpLoginUser || !erpLoginPass || erpLoginLoading) {
      showToast('Bitte ERP-Benutzer und Passwort eingeben.');
      return;
    }
    setErpLoginLoading(true);
    showToast('ERP-Anmeldung wird geprueft...');
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ username: erpLoginUser, password: erpLoginPass }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) throw new Error(data.error || 'ERP-Anmeldung fehlgeschlagen.');

      updateSL('status', 'verbunden');
      updateSL('lastSync', new Date().toISOString());
      updateSL('apiTokenSet', true);
      setErpLoginPass('');
      showToast('ERP-Sync ist angemeldet.');
    } catch (error) {
      updateSL('status', 'fehler');
      showToast(error.message || 'ERP-Anmeldung fehlgeschlagen.');
    } finally {
      setErpLoginLoading(false);
    }
  }
  async function testOutlook() {
    showToast('Microsoft Graph-Verbindung wird geprüft...');
    try {
      const params = new URLSearchParams();
      const normalizedFilter = String(ol.subjectFilter || '').trim();
      if (normalizedFilter && normalizedFilter !== '*') params.set('subject_filter', normalizedFilter);
      params.set('lookback_months', String(ol.lookbackMonths || 1));
      const response = await fetch(`/api/erp/outlook?${params.toString()}`, { credentials: 'same-origin' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error(data.error || 'Outlook-Verbindung fehlgeschlagen.');
      }
      updateOL('status', 'verbunden');
      updateOL('lastSync', new Date().toISOString());
      showToast(`Outlook OK: ${data.count || 0} Mail(s) gefunden, ${data.scannedCount || 0} gelesen.`);
    } catch (error) {
      updateOL('status', 'fehler');
      showToast(error.message || 'Outlook-Verbindung fehlgeschlagen.');
    }
  }
  function testSMTP() {
    showToast('SMTP-Testmail wird versendet...');
    setTimeout(() => {
      updateSMTP('testStatus', { ok: true, time: new Date().toISOString() });
      showToast('✓ Test-E-Mail an Admin gesendet.');
    }, 1500);
  }

  const sl = integrations.selectline;
  const ol = integrations.outlook;
  const sm = integrations.smtp;
  const catalogSettings = integrations.catalog || DEFAULT_INTEGRATIONS.catalog;

  const Wrapper = embedded ? 'div' : 'div';
  const wrapperClass = embedded ? '' : 'app-content';

  return (
    <Wrapper className={wrapperClass}>
      {!embedded && <div className="page-title">Integrationen<span className="dot">.</span></div>}
      <div style={{ fontSize: 12, color: 'var(--kw-text-muted)', marginBottom: 16 }}>
        Verbindungen zu SelectLine NEO ERP, Microsoft 365 Outlook und SMTP-Versand. Anmeldedaten werden als
        Vercel-Umgebungsvariablen sicher gespeichert — niemals im Browser.
      </div>

      {/* Section selector */}
      <div className="filter-row">
        <button className={`filter-btn${section === 'selectline' ? ' active' : ''}`} onClick={() => setSection('selectline')}>
          SelectLine NEO API
          <span className={`int-status int-${sl.status}`}>●</span>
        </button>
        <button className={`filter-btn${section === 'outlook' ? ' active' : ''}`} onClick={() => setSection('outlook')}>
          Microsoft Outlook
          <span className={`int-status int-${ol.status}`}>●</span>
        </button>
        <button className={`filter-btn${section === 'smtp' ? ' active' : ''}`} onClick={() => setSection('smtp')}>
          SMTP-Versand
          {sm.testStatus?.ok && <span className="int-status int-verbunden">●</span>}
        </button>
        <button className={`filter-btn${section === 'catalog' ? ' active' : ''}`} onClick={() => setSection('catalog')}>
          Katalog
        </button>
        <button className={`filter-btn${section === 'templates' ? ' active' : ''}`} onClick={() => setSection('templates')}>
          E-Mail-Vorlagen
        </button>
      </div>

      {/* SELECTLINE */}
      {section === 'selectline' && (
        <>
          <div className="info-box">
            <div className="info-box-title">📦 SelectLine NEO REST-API</div>
            <p>
              Holt Angebote direkt aus eurer Warenwirtschaft. Die App-Anmeldung erfolgt per Microsoft Login; SelectLine-Zugangsdaten werden nur serverseitig fuer ERP-API-Zugriffe verwendet.
            </p>
          </div>
          <div className="card">
            <div className="project-grid">
              <div className="form-group"><label>API-Endpunkt</label>
                <input type="text" value={sl.apiUrl} onChange={e => updateSL('apiUrl', e.target.value)}
                  placeholder="https://demo.slmobile.de/demoApi" /></div>
              <div className="form-group"><label>Mandantennummer</label>
                <input type="text" value={sl.mandantenNr} onChange={e => updateSL('mandantenNr', e.target.value)} /></div>
              <div className="form-group"><label>Belegart</label>
                <select value={sl.belegArt} onChange={e => updateSL('belegArt', e.target.value)}>
                  <option value="F">F - Angebot/Beleg laut SelectLine</option>
                  <option value="A">A - Auftrag/abweichende Installation</option>
                  <option value="L">L - Lieferschein</option>
                  <option value="R">R - Rechnung</option>
                </select></div>
              <div className="form-group"><label>Abrufintervall (Sek.)</label>
                <input type="number" value={sl.pollInterval} onChange={e => updateSL('pollInterval', Number(e.target.value))}
                  min={60} max={3600} /></div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>AppKey</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="password" placeholder={sl.appKeySet ? '••••••• (in Vercel hinterlegt)' : 'AppKey eingeben...'}
                    onChange={e => updateSL('appKeySet', e.target.value.length > 0)} />
                  {sl.appKeySet && <span className="tag tag-ok">Gespeichert</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 4 }}>
                  Wird als Vercel-Env <code>SELECTLINE_APP_KEY</code> gesetzt. Die App-Anmeldung erfolgt ausschliesslich per Microsoft Login.
                </div>
              </div>
              <div className="form-group"><label>ERP-Benutzer</label>
                <input type="text" value={erpLoginUser} onChange={e => setErpLoginUser(e.target.value)}
                  placeholder="SelectLine / SL.mobile Benutzer" autoComplete="username" /></div>
              <div className="form-group"><label>ERP-Passwort</label>
                <input type="password" value={erpLoginPass} onChange={e => setErpLoginPass(e.target.value)}
                  placeholder="Nur fuer diese Anmeldung" autoComplete="current-password"
                  onKeyDown={e => e.key === 'Enter' && loginSelectLineERP()} /></div>
            </div>
            <div style={{ marginTop: 12, padding: 12, background: 'rgba(139, 90, 43, 0.06)', border: '1px solid rgba(139, 90, 43, 0.18)', borderRadius: 'var(--radius)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--kw-brown)', fontSize: 13 }}>ERP-Sync anmelden</div>
                  <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 2 }}>
                    Erzeugt serverseitig ein SelectLine-Token als HttpOnly-Cookie. Das Passwort wird nicht im Frontend gespeichert.
                  </div>
                </div>
                <button className="btn btn-olive btn-sm" onClick={loginSelectLineERP} disabled={erpLoginLoading}>
                  {erpLoginLoading ? 'Pruefe...' : 'ERP anmelden'}
                </button>
              </div>
            </div>
            <div style={{ marginTop: 14, padding: 12, background: 'var(--kw-bg)', borderRadius: 'var(--radius)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1 }}>STATUS</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                  <span className={`int-status-dot int-${sl.status}`}>●</span>
                  <strong style={{ fontSize: 13 }}>
                    {sl.status === 'verbunden' ? 'Verbunden' : sl.status === 'fehler' ? 'Verbindungsfehler' : 'Nicht verbunden'}
                  </strong>
                  {sl.lastSync && <span style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>
                    · Letzter Sync: {new Date(sl.lastSync).toLocaleString('de-DE')}
                  </span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-outline btn-sm" onClick={testSelectLine}>↻ Verbindung testen</button>
                <button className="btn btn-olive btn-sm" onClick={() => showToast('Konfiguration gespeichert.')}>Speichern</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* OUTLOOK */}
      {section === 'outlook' && (
        <>
          <div className="info-box">
            <div className="info-box-title">📨 Microsoft 365 Outlook / Exchange Online</div>
            <p>
              Liest Mails direkt aus dem angemeldeten O365-Benutzerpostfach <strong>{currentUser?.email || 'aktueller Benutzer'}</strong>.
              Azure-Zugangsdaten werden ausschliesslich in Vercel gepflegt und hier nicht mehr gespeichert.
            </p>
          </div>
          <div className="card">
            <div className="project-grid">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Betreff-Filter (enthält)</label>
                <input type="text" value={ol.subjectFilter} onChange={e => updateOL('subjectFilter', e.target.value)}
                  placeholder="z.B. Angebot oder leer/* fuer alle Mails" />
                <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 4 }}>
                  Dieser Parameter wird automatisch lokal und im geräteuebergreifenden Status gespeichert.
                </div>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Importzeitraum</label>
                <select value={ol.lookbackMonths || 1} onChange={e => updateOL('lookbackMonths', Number(e.target.value))}>
                  <option value={1}>1 Monat zurück</option>
                  <option value={2}>2 Monate zurück</option>
                  <option value={6}>6 Monate zurück</option>
                </select>
                <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 4 }}>
                  Ausgehend vom aktuellen Datum werden nur neuere Mails gelesen. Bereits in Checklisten uebergebene Mails werden ausgeblendet.
                </div>
              </div>
            </div>
            <div style={{ marginTop: 14, padding: 12, background: 'var(--kw-bg)', borderRadius: 'var(--radius)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1 }}>STATUS</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                  <span className={`int-status-dot int-${ol.status}`}>●</span>
                  <strong style={{ fontSize: 13 }}>
                    {ol.status === 'verbunden' ? 'Verbunden' : ol.status === 'fehler' ? 'Verbindungsfehler' : 'Nicht verbunden'}
                  </strong>
                  {ol.lastSync && <span style={{ fontSize: 11, color: 'var(--kw-text-muted)' }}>
                    · Letzter Sync: {new Date(ol.lastSync).toLocaleString('de-DE')}
                  </span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-outline btn-sm" onClick={testOutlook}>↻ Verbindung testen</button>
                <button className="btn btn-olive btn-sm" onClick={() => showToast('Konfiguration gespeichert.')}>Speichern</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* SMTP */}
      {section === 'smtp' && (
        <>
          <div className="info-box">
            <div className="info-box-title">📤 SMTP-Versand via Microsoft 365</div>
            <p>
              Globaler Versand-Account für Checklisten, Berichte und Benachrichtigungen. Empfehlung für M365:
              <code> smtp.office365.com</code> auf Port 587 mit STARTTLS. Pro Benutzer regelbar in der Benutzerverwaltung,
              welche Mails er erhalten möchte.
            </p>
          </div>
          <div className="card">
            <div className="project-grid">
              <div className="form-group"><label>SMTP-Host</label>
                <input type="text" value={sm.host} onChange={e => updateSMTP('host', e.target.value)} /></div>
              <div className="form-group"><label>Port</label>
                <input type="number" value={sm.port} onChange={e => updateSMTP('port', Number(e.target.value))} /></div>
              <div className="form-group"><label>Verschlüsselung</label>
                <select value={sm.security} onChange={e => updateSMTP('security', e.target.value)}>
                  <option value="starttls">STARTTLS (empfohlen)</option>
                  <option value="ssl">SSL/TLS</option>
                  <option value="none">Keine</option>
                </select></div>
              <div className="form-group"><label>Auth-Benutzer</label>
                <input type="text" value={sm.user} onChange={e => updateSMTP('user', e.target.value)} /></div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>App-Passwort (M365)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="password" placeholder={sm.passwordSet ? '••••••• (in Vercel hinterlegt)' : 'App-Passwort aus M365 eingeben...'}
                    onChange={e => updateSMTP('passwordSet', e.target.value.length > 0)} />
                  {sm.passwordSet && <span className="tag tag-ok">Gespeichert</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 4 }}>
                  M365 erfordert ein eigenes App-Passwort, kein normales Benutzerpasswort. Erzeugen über
                  <a href="https://account.microsoft.com/security" target="_blank" rel="noreferrer" style={{ color: 'var(--kw-olive)' }}> account.microsoft.com/security</a>.
                </div>
              </div>
              <div className="form-group"><label>Absender-Name</label>
                <input type="text" value={sm.fromName} onChange={e => updateSMTP('fromName', e.target.value)} /></div>
              <div className="form-group"><label>Absender-E-Mail</label>
                <input type="email" value={sm.fromEmail} onChange={e => updateSMTP('fromEmail', e.target.value)} /></div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Antwort-an (optional)</label>
                <input type="email" value={sm.replyTo} onChange={e => updateSMTP('replyTo', e.target.value)}
                  placeholder="Wenn leer: gleiche wie Absender" /></div>
            </div>
            <div style={{ marginTop: 14, padding: 12, background: 'var(--kw-bg)', borderRadius: 'var(--radius)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1 }}>LETZTER TEST</div>
                <div style={{ marginTop: 2, fontSize: 13 }}>
                  {sm.testStatus?.ok ? (
                    <><span style={{ color: 'var(--status-ok)' }}>✓ Erfolgreich</span>
                    <span style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginLeft: 8 }}>
                      {new Date(sm.testStatus.time).toLocaleString('de-DE')}
                    </span></>
                  ) : <span style={{ color: 'var(--kw-text-muted)' }}>Noch nicht getestet</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-outline btn-sm" onClick={testSMTP}>↻ Testmail senden</button>
                <button className="btn btn-olive btn-sm" onClick={() => showToast('Konfiguration gespeichert.')}>Speichern</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* CATALOG */}
      {section === 'catalog' && (
        <>
          <div className="info-box">
            <div className="info-box-title">€ Katalog und Preislisten</div>
            <p>
              Der Homepage-Katalog wird weiterhin automatisch geladen. Hier wird nur der externe Link gepflegt,
              der in der Kalkulation als Preislisten-Button angezeigt wird.
            </p>
          </div>
          <div className="card">
            <div className="project-grid">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Preislisten-Link</label>
                <input type="url" value={catalogSettings.priceListUrl || ''}
                  onChange={e => updateCatalog('priceListUrl', e.target.value)}
                  placeholder="https://..." />
                <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', marginTop: 4 }}>
                  Wird im Bereich Kalkulation / Katalog als Button <strong>€ Preislisten</strong> geöffnet.
                </div>
              </div>
            </div>
            <div className="action-row" style={{ marginTop: 14 }}>
              <button className="btn btn-outline btn-sm" type="button"
                onClick={() => catalogSettings.priceListUrl && window.open(catalogSettings.priceListUrl, '_blank', 'noopener,noreferrer')}
                disabled={!catalogSettings.priceListUrl}>
                € Testen
              </button>
              <button className="btn btn-olive btn-sm" onClick={() => showToast('Katalog-Einstellungen gespeichert.')}>Speichern</button>
            </div>
          </div>
        </>
      )}

      {/* TEMPLATES */}
      {section === 'templates' && (
        <>
          <div className="info-box">
            <div className="info-box-title">✉ E-Mail-Vorlagen</div>
            <p>
              Vorlagen für automatische und manuelle Versandfunktionen. Platzhalter wie
              <code> {'{kunde}'}</code>, <code>{'{datum}'}</code>, <code>{'{angebot_nr}'}</code> werden beim
              Versand automatisch ersetzt.
            </p>
          </div>
          {Object.entries(mailTemplates).map(([key, tpl]) => (
            <div key={key} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--kw-brown)' }}>{tpl.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--kw-text-muted)', fontFamily: 'monospace' }}>{key}</div>
                </div>
                <button className="btn btn-outline btn-sm" onClick={() => setEditTemplate(editTemplate === key ? null : key)}>
                  {editTemplate === key ? 'Schließen' : '✎ Bearbeiten'}
                </button>
              </div>
              {editTemplate === key ? (
                <div className="project-grid">
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Betreff</label>
                    <input type="text" value={tpl.subject}
                      onChange={e => setMailTemplates(p => ({ ...p, [key]: { ...p[key], subject: e.target.value } }))} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Textkörper</label>
                    <textarea className="note-area" style={{ minHeight: 150 }} value={tpl.body}
                      onChange={e => setMailTemplates(p => ({ ...p, [key]: { ...p[key], body: e.target.value } }))} />
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--kw-text-muted)' }}>
                  <div><strong>Betreff:</strong> {tpl.subject}</div>
                  <div style={{ marginTop: 6, whiteSpace: 'pre-line', borderLeft: '2px solid var(--kw-border)', paddingLeft: 10 }}>{tpl.body}</div>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </Wrapper>
  );
}



export default function App() {
  const storedStateRef = useRef(null);
  if (storedStateRef.current === null) storedStateRef.current = loadVersionedState();
  const stored = storedStateRef.current;
  const cloudHydratedUserRef = useRef(null);
  const cloudApplyingRef = useRef(false);
  const cloudSaveTimerRef = useRef(null);
  const cloudLastPayloadRef = useRef('');

  const [users, setUsers]                 = useState(normalizeUsersForApp(stored.users || {}));
  const [currentUser, setCurrentUser]     = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [tab, setTab]                     = useState(stored.activeTab || 'overview');
  const [items, setItems]                 = useState(stored.items || DEFAULT_ITEMS);
  const [checklists, setChecklists]       = useState(stored.checklists || []);
  const [toast, setToast]                 = useState('');
  const [versionOpen, setVersionOpen]     = useState(false);
  const [linkedOffer, setLinkedOffer]     = useState(null);
  const [machineValues, setMachineValues] = useState(stored.machineValues || DEFAULT_MACHINE_VALUES);
  const [integrations, setIntegrations]   = useState(normalizeIntegrationsForApp(stored.integrations || {}));
  const [mailTemplates, setMailTemplates] = useState(stored.mailTemplates || DEFAULT_MAIL_TEMPLATES);
  const [trainingTopics, setTrainingTopics] = useState(stored.trainingTopics || DEFAULT_TRAINING_TOPICS);
  const [anschlussRules, setAnschlussRules] = useState(normalizeAnschlussRules(stored.anschlussRules || DEFAULT_ANSCHLUSS_RULES));
  const [workStatus, setWorkStatus] = useState(normalizeWorkStatusForApp(stored.workStatus || {}));
  const [editingChecklistId, setEditingChecklistId] = useState(null);
  const [userSettingsRequest, setUserSettingsRequest] = useState(0);
  const [stateSync, setStateSync] = useState({
    configured: null,
    loading: false,
    lastLoadedAt: null,
    lastSavedAt: null,
    error: null,
  });

  const showToast = useCallback((msg) => {
    setToast(msg); setTimeout(() => setToast(''), 2800);
  }, []);

  const updateWorkStatus = useCallback((area, patch) => {
    setWorkStatus(prev => ({
      ...prev,
      [area]: {
        ...(prev[area] || {}),
        ...patch,
      },
    }));
  }, []);

  const buildPersistedPayload = useCallback(() => ({
    activeTab: tab,
    users,
    items,
    checklists,
    machineValues,
    integrations,
    mailTemplates,
    trainingTopics,
    anschlussRules,
    workStatus,
  }), [tab, users, items, checklists, machineValues, integrations, mailTemplates, trainingTopics, anschlussRules, workStatus]);

  const getCurrentUserSyncKey = useCallback(() => {
    if (!currentUser) return '';
    return String(currentUser.email || currentUser.username || '').trim().toLowerCase();
  }, [currentUser]);

  function buildAppUserFromMicrosoft(msUser) {
    const email = (msUser.email || '').toLowerCase();
    const isForcedAdmin = FORCED_ADMIN_EMAILS.includes(email);
    const matchedEntry = Object.entries(users).find(([username, user]) =>
      username.toLowerCase() === email
      || username.toLowerCase() === email.split('@')[0]
      || user.email?.toLowerCase() === email
    );

    if (isForcedAdmin) {
      const matchedUser = matchedEntry?.[1] || {};
      return {
        ...DW_ADMIN_USER,
        ...matchedUser,
        username: email,
        name: matchedUser.name || msUser.name || DW_ADMIN_USER.name,
        email,
        mailRecipient: matchedUser.mailRecipient || matchedUser.email || email,
        insideSalesRecipient: matchedUser.insideSalesRecipient || matchedUser.offerRecipient || 'buero@kaffeewerk.de',
        role: 'admin',
        pass: matchedUser.pass || '',
        notify: matchedUser.notify || DW_ADMIN_USER.notify,
        microsoft: msUser,
      };
    }

    if (matchedEntry) {
      const [username, user] = matchedEntry;
      return { username, ...user, microsoft: msUser };
    }

    const fallbackName = msUser.name || email || 'Microsoft Benutzer';
    return {
      username: email || msUser.id || 'microsoft',
      role: 'user',
      name: fallbackName,
      email,
      mailRecipient: email,
      insideSalesRecipient: 'buero@kaffeewerk.de',
      initials: fallbackName.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase() || 'MS',
      slnSachbearbeiter: email.split('@')[0] || '',
      salesmanNumber: '',
      erpSource: 'outlook',
      notify: { checklistDone: true, newOffer: false, weeklyReport: false },
      microsoft: msUser,
    };
  }

  const applyLoadedCloudPayload = useCallback((payload, sharedConfigRecord = null) => {
    const userPayload = payload && typeof payload === 'object' ? payload : {};
    const sharedPayload = sharedConfigRecord?.payload || sharedConfigRecord || null;
    if ((!payload || typeof payload !== 'object') && (!sharedPayload || typeof sharedPayload !== 'object')) return;
    if (userPayload.checklists) setChecklists(userPayload.checklists);
    if (userPayload.workStatus) setWorkStatus(prev => mergeWorkStatus(prev, userPayload.workStatus));
    if (userPayload.activeTab) setTab(userPayload.activeTab);
    if (sharedPayload && typeof sharedPayload === 'object') {
      if (sharedPayload.users) setUsers(normalizeUsersForApp(sharedPayload.users));
      if (sharedPayload.items) setItems(sharedPayload.items);
      if (sharedPayload.machineValues) setMachineValues(sharedPayload.machineValues);
      if (sharedPayload.integrations) setIntegrations(normalizeIntegrationsForApp(sharedPayload.integrations));
      if (sharedPayload.mailTemplates) setMailTemplates(sharedPayload.mailTemplates);
      if (sharedPayload.trainingTopics) setTrainingTopics(sharedPayload.trainingTopics);
      if (sharedPayload.anschlussRules) setAnschlussRules(normalizeAnschlussRules(sharedPayload.anschlussRules));
    } else {
      // Migration fallback for older per-user states that still contain configuration.
      if (userPayload.users) setUsers(normalizeUsersForApp(userPayload.users));
      if (userPayload.items) setItems(userPayload.items);
      if (userPayload.machineValues) setMachineValues(userPayload.machineValues);
      if (userPayload.integrations) setIntegrations(normalizeIntegrationsForApp(userPayload.integrations));
      if (userPayload.mailTemplates) setMailTemplates(userPayload.mailTemplates);
      if (userPayload.trainingTopics) setTrainingTopics(userPayload.trainingTopics);
      if (userPayload.anschlussRules) setAnschlussRules(normalizeAnschlussRules(userPayload.anschlussRules));
    }
    cloudLastPayloadRef.current = JSON.stringify({
      state: userPayload,
      sharedConfig: sharedPayload && typeof sharedPayload === 'object' ? sharedPayload : null,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function restoreMicrosoftSession() {
      try {
        const response = await fetch('/api/auth/me', { credentials: 'same-origin' });
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled && data.ok && data.user) {
          setCurrentUser(buildAppUserFromMicrosoft(data.user));
        }
      } catch {
        // No active Microsoft session; stay on login screen.
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    }
    restoreMicrosoftSession();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    saveVersionedState(buildPersistedPayload());
  }, [buildPersistedPayload]);

  useEffect(() => {
    if (!currentUser) {
      cloudHydratedUserRef.current = null;
      return;
    }

    const userKey = getCurrentUserSyncKey();
    if (!userKey || cloudHydratedUserRef.current === userKey) return;

    let cancelled = false;
    cloudApplyingRef.current = true;
    setStateSync(prev => ({ ...prev, loading: true, error: null }));

    async function loadCloudState() {
      try {
        const response = await fetch('/api/user-state', { credentials: 'same-origin' });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.ok) {
          throw new Error(data.error || 'Arbeitsstatus konnte nicht geladen werden.');
        }
        if (cancelled) return;

        if (data.configured === false) {
          cloudHydratedUserRef.current = userKey;
          setStateSync({
            configured: false,
            loading: false,
            lastLoadedAt: null,
            lastSavedAt: null,
            error: null,
          });
          return;
        }

        const payload = data.state?.payload || data.state || null;
        applyLoadedCloudPayload(payload, data.sharedConfig);

        cloudHydratedUserRef.current = userKey;
        setStateSync({
          configured: true,
          loading: false,
          lastLoadedAt: data.state?.savedAt || new Date().toISOString(),
          lastSavedAt: data.state?.savedAt || null,
          error: null,
        });
      } catch (error) {
        if (cancelled) return;
        cloudHydratedUserRef.current = userKey;
        setStateSync(prev => ({
          ...prev,
          configured: true,
          loading: false,
          error: error.message || 'Arbeitsstatus konnte nicht geladen werden.',
        }));
      } finally {
        window.setTimeout(() => {
          if (!cancelled) cloudApplyingRef.current = false;
        }, 0);
      }
    }

    loadCloudState();
    return () => {
      cancelled = true;
    };
  }, [currentUser, getCurrentUserSyncKey, applyLoadedCloudPayload]);

  useEffect(() => {
    if (!currentUser) return;
    async function refreshCloudState() {
      const userKey = getCurrentUserSyncKey();
      if (!userKey || cloudApplyingRef.current) return;
      try {
        const response = await fetch('/api/user-state', { credentials: 'same-origin' });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.ok || data.configured === false) return;
        const remoteSavedAt = data.state?.savedAt || null;
        const localSavedAt = stateSync.lastSavedAt || stateSync.lastLoadedAt || null;
        if (remoteSavedAt && localSavedAt && new Date(remoteSavedAt) <= new Date(localSavedAt)) {
          if (data.sharedConfig) {
            cloudApplyingRef.current = true;
            applyLoadedCloudPayload({}, data.sharedConfig);
          }
          return;
        }
        cloudApplyingRef.current = true;
        applyLoadedCloudPayload(data.state?.payload || data.state || null, data.sharedConfig);
        setStateSync(prev => ({
          ...prev,
          configured: true,
          lastLoadedAt: remoteSavedAt || new Date().toISOString(),
          error: null,
        }));
      } catch {
        // Optionaler Fokus-Refresh; Fehler bleiben im normalen Sync-Pfad sichtbar.
      } finally {
        window.setTimeout(() => { cloudApplyingRef.current = false; }, 0);
      }
    }
    function handleVisibility() {
      if (document.visibilityState === 'visible') refreshCloudState();
    }
    window.addEventListener('focus', refreshCloudState);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', refreshCloudState);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [currentUser, getCurrentUserSyncKey, applyLoadedCloudPayload, stateSync.lastLoadedAt, stateSync.lastSavedAt]);

  useEffect(() => {
    const userKey = getCurrentUserSyncKey();
    if (!currentUser || !userKey) return;
    if (cloudHydratedUserRef.current !== userKey) return;
    if (cloudApplyingRef.current) return;

    const persistedPayload = buildPersistedPayload();
    const payload = buildCloudStatePayload(persistedPayload);
    const sharedConfigPayload = currentUser.role === 'admin' ? buildSharedConfigPayload(persistedPayload) : null;
    const serialized = JSON.stringify({
      state: payload,
      sharedConfig: sharedConfigPayload,
    });
    if (serialized === cloudLastPayloadRef.current) return;

    if (cloudSaveTimerRef.current) window.clearTimeout(cloudSaveTimerRef.current);
    cloudSaveTimerRef.current = window.setTimeout(async () => {
      try {
        const response = await fetch('/api/user-state', {
          method: 'PUT',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            state: payload,
            sharedConfig: sharedConfigPayload || undefined,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.ok) {
          throw new Error(data.error || 'Arbeitsstatus konnte nicht gespeichert werden.');
        }
        cloudLastPayloadRef.current = serialized;
        setStateSync(prev => ({
          ...prev,
          configured: data.configured !== false,
          lastSavedAt: data.savedAt || new Date().toISOString(),
          lastLoadedAt: data.savedAt || prev.lastLoadedAt,
          error: null,
        }));
      } catch (error) {
        setStateSync(prev => ({
          ...prev,
          error: error.message || 'Arbeitsstatus konnte nicht gespeichert werden.',
        }));
      }
    }, 250);

    return () => {
      if (cloudSaveTimerRef.current) window.clearTimeout(cloudSaveTimerRef.current);
    };
  }, [currentUser, getCurrentUserSyncKey, buildPersistedPayload]);

  useEffect(() => {
    if (!currentUser) return;
    const email = (currentUser.email || '').toLowerCase();
    const matchedEntry = Object.entries(users).find(([username, user]) =>
      username.toLowerCase() === String(currentUser.username || '').toLowerCase()
      || username.toLowerCase() === email
      || user.email?.toLowerCase() === email
    );

    if (!matchedEntry) return;

    const [username, user] = matchedEntry;
    const isForcedAdmin = FORCED_ADMIN_EMAILS.includes(email) || FORCED_ADMIN_EMAILS.includes(username.toLowerCase());
    setCurrentUser(prev => ({
      ...prev,
      ...user,
      username: prev.username || username,
      role: isForcedAdmin ? 'admin' : user.role,
      microsoft: prev.microsoft,
    }));
  }, [users]); // eslint-disable-line react-hooks/exhaustive-deps

  function resetAppData() {
    if (!window.confirm('Lokale App-Daten wirklich zuruecksetzen? Checklisten, Einstellungen und lokale Benutzer werden entfernt.')) return;
    if (currentUser) {
      fetch('/api/user-state', { method: 'DELETE', credentials: 'same-origin' }).catch(() => {});
    }
    clearVersionedState();
    cloudHydratedUserRef.current = getCurrentUserSyncKey() || null;
    cloudLastPayloadRef.current = '';
    setUsers(normalizeUsersForApp());
    setItems(DEFAULT_ITEMS);
    setChecklists([]);
    setMachineValues(DEFAULT_MACHINE_VALUES);
    setWorkStatus(normalizeWorkStatusForApp());
    setIntegrations(normalizeIntegrationsForApp());
    setMailTemplates(DEFAULT_MAIL_TEMPLATES);
    setTrainingTopics(DEFAULT_TRAINING_TOPICS);
    setAnschlussRules(normalizeAnschlussRules(DEFAULT_ANSCHLUSS_RULES));
    setEditingChecklistId(null);
    setLinkedOffer(null);
    setTab('overview');
    showToast('Lokale App-Daten zurueckgesetzt.');
  }

  useEffect(() => {
    if (currentUser && users[currentUser.username]) {
      const fresh = users[currentUser.username];
      if (fresh !== currentUser) {
        setCurrentUser(c => c ? { ...fresh, username: c.username } : null);
      }
    }
  }, [users]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleLogout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch {
      // Logout in der UI trotzdem durchfuehren; der Server loescht Cookie beim naechsten erfolgreichen Call.
    }
    setCurrentUser(null);
    setLinkedOffer(null);
    setEditingChecklistId(null);
  }

  if (!sessionChecked) return (
    <>
      <div className="login-screen">
        <div className="login-hero">
          <KWLogo height={44} />
          <div className="login-headline">Anmeldung wird geprüft</div>
        </div>
      </div>
      <Toast msg={toast} />
    </>
  );

  if (!currentUser) return (
    <>
      <LoginScreen />
      <Toast msg={toast} />
    </>
  );

  const isAdmin = currentUser.role === 'admin';

  function openOwnUserSettings() {
    if (!isAdmin) return;
    setTab('admin');
    setUserSettingsRequest(value => value + 1);
  }

  function createChecklistFromOffer(offer, toastMessage) {
    if (!offer) return;
    const existing = checklists.find((checklist) =>
      checklist.owner === currentUser.username
      && checklist.angebot
      && checklist.angebot === offer.nr
      && checklist.linkedOffer?.source === offer.source
    );

    setLinkedOffer(offer);

    if (existing) {
      setEditingChecklistId(existing.id);
      setTab('checklist');
      showToast(`Checkliste zu Angebot ${offer.nr} geoeffnet.`);
      return;
    }

    const now = new Date().toISOString();
    const checklistItems = items.filter(item => item.active).map(item => ({
      id: item.id,
      label: item.label,
      type: item.type,
      done: false,
      blocked: false,
      blockReason: '',
      note: '',
      photo: null,
      extra: {},
    }));
    const snapshot = {
      id: `checklist-${offer.id || Date.now()}`,
      owner: currentUser.username,
      createdAt: now,
      updatedAt: now,
      versionNr: 1,
      savedAt: now,
      savedBy: currentUser.username,
      savedByName: currentUser.name,
      kunde: offer.kunde && offer.kunde !== '-' ? cleanCustomerName(offer.kunde) : '',
      datum: new Date().toISOString().slice(0, 10),
      person: currentUser.name,
      angebot: offer.nr || '',
      pct: 0,
      status: 'offen',
      linkedOffer: { id: offer.id, nr: offer.nr, items: offer.items || [], kunde: cleanCustomerName(offer.kunde), source: offer.source, date: offer.date, subject: offer.subject },
      installationBlocked: false,
      installationAborted: false,
      abortReason: '',
      blockedCount: 0,
      items: checklistItems,
    };
    const firstVersion = { ...snapshot, versionNr: 1 };
    const checklist = {
      ...snapshot,
      versions: [firstVersion],
      activeVersionNr: 1,
    };

    setChecklists(prev => [checklist, ...prev]);
    setEditingChecklistId(checklist.id);
    setTab('checklist');
    updateWorkStatus('checklist', {
      draft: {
        kunde: checklist.kunde,
        datum: checklist.datum,
        person: checklist.person,
        angebot: checklist.angebot,
        itemData: {},
        editingId: checklist.id,
      },
      pct: 0,
      status: 'Offen',
      customer: checklist.kunde,
      updatedAt: now,
    });
    showToast(toastMessage || `Neue Checkliste zu Angebot ${offer.nr} angelegt.`);
  }

  return (
    <>
      <Header user={currentUser} onLogout={handleLogout}
        setTab={setTab} isAdmin={isAdmin} onUserSettings={openOwnUserSettings} />
      <Nav tab={tab} setTab={setTab} isAdmin={isAdmin} />

      {tab === 'overview'  && <OverviewTab checklists={checklists} setTab={setTab} currentUser={currentUser} workStatus={workStatus} />}
      {tab === 'offers'    && <OffersTab currentUser={currentUser} setTab={setTab} showToast={showToast}
          integrations={integrations}
          workStatus={workStatus}
          onWorkStatusChange={updateWorkStatus}
          onUseInChecklist={offer => createChecklistFromOffer(offer, `Neue Checkliste zu Angebot ${offer.nr} angelegt.`)} />}
      {tab === 'calculation' && <CalculationTab currentUser={currentUser} showToast={showToast}
          calculationDraft={workStatus.calculation?.draft}
          calculationStatus={workStatus.calculation}
          onWorkStatusChange={updateWorkStatus}
          integrations={integrations}
          onUseInChecklist={offer => createChecklistFromOffer(offer, 'Kalkulation als neue Checkliste angelegt.')} />}
      {tab === 'checklist' && <ChecklistTab items={items} checklists={checklists} setChecklists={setChecklists}
          currentUser={currentUser} showToast={showToast}
          linkedOffer={linkedOffer} onClearOffer={() => setLinkedOffer(null)}
          machineValues={machineValues} users={users}
          integrations={integrations} mailTemplates={mailTemplates}
          trainingTopics={trainingTopics} anschlussRules={anschlussRules}
          editingId={editingChecklistId} setEditingId={setEditingChecklistId}
          checklistDraft={workStatus.checklist?.draft}
          onWorkStatusChange={updateWorkStatus} />}
      {tab === 'settings'  && isAdmin && (
        <SettingsTab
          items={items} setItems={setItems}
          machineValues={machineValues} setMachineValues={setMachineValues}
          integrations={integrations} setIntegrations={setIntegrations}
          mailTemplates={mailTemplates} setMailTemplates={setMailTemplates}
          trainingTopics={trainingTopics} setTrainingTopics={setTrainingTopics}
          anschlussRules={anschlussRules} setAnschlussRules={setAnschlussRules}
          isAdmin={isAdmin} showToast={showToast}
          resetAppData={resetAppData} checklists={checklists} currentUser={currentUser}
        />
      )}
      {tab === 'admin'     && isAdmin && <AdminTab users={users} setUsers={setUsers} showToast={showToast}
          initialEditUsername={currentUser.username} initialEditRequest={userSettingsRequest} />}

      <AppFooter onVersionClick={() => setVersionOpen(true)} stateSync={stateSync} />
      <VersionModal open={versionOpen} onClose={() => setVersionOpen(false)} />
      <Toast msg={toast} />
    </>
  );
}












